import { StudyWorkspace } from "./components/StudyWorkspace";
import type { ResearchRun, AnswerLength } from "./shared/research";
import "./research.css";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Bell,
  BookOpenText,
  Books,
  BookmarkSimple,
  Bug,
  CaretDown,
  CaretRight,
  ChalkboardTeacher,
  ChatCircleDots,
  Check,
  CheckCircle,
  Church,
  CreditCard,
  DownloadSimple,
  GearSix,
  GoogleLogo,
  Heart,
  List,
  Lightbulb,
  LockKey,
  MagnifyingGlass,
  Microphone,
  Monitor,
  Moon,
  NotePencil,
  Pause,
  Play,
  Plus,
  Scales,
  ShieldCheck,
  ShieldStar,
  SignIn,
  SignOut,
  Sparkle,
  SpeakerHigh,
  Sun,
  Trash,
  UploadSimple,
  UserCircle,
  UsersThree,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import type { IconProps } from "@phosphor-icons/react";
import { Capacitor } from "@capacitor/core";
import {
  type ComponentType,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import {
  BibleAloneSymbol,
  BrandLockup,
  BrandSurfaceProvider,
  SolaLabel,
  SolaMark,
} from "./components/Brand";
import { MarkdownContent } from "./components/MarkdownContent";
import { ScriptureReader } from "./components/ScriptureReader";
import {
  NewSermon,
  SermonBuilder,
  SermonDashboard,
  SermonPreach,
} from "./components/Sermons";
import { brand } from "./config/brand";
import {
  doctrineProfileLabel,
  doctrineProfiles,
} from "./config/doctrines";
import { api, streamChat, resumeResearch } from "./lib/api";
import { authClient } from "./lib/auth-client";
import {
  bibleBooks,
  type BibleData,
  loadKjv,
  parseReference,
  referencePath,
} from "./lib/bible";
import {
  bibleVersion,
  scriptureRangeLabel,
  type ScriptureSelection,
} from "./shared/bible";
import {
  scriptureBookmarkPath,
  type ScriptureBookmark,
} from "./shared/bookmarks";
import type {
  DeepStudyArtifact as DeepStudyDetail,
  DeepStudySource,
  NoteAttachment,
  StudyNote,
} from "./shared/study";

type Icon = ComponentType<IconProps>;
type ThemePreference = "system" | "light" | "dark";
type ResolvedTheme = "light" | "dark";

const THEME_STORAGE_KEY = "bible-alone-theme";
const CONVERSATIONS_CHANGED_EVENT = "bible-alone:conversations-changed";
const NEW_CONVERSATION_EVENT = "bible-alone:new-conversation";

function notifyConversationHistoryChanged() {
  window.dispatchEvent(new Event(CONVERSATIONS_CHANGED_EVENT));
}

function readThemePreference(): ThemePreference {
  if (typeof window === "undefined") return "system";
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
}

interface Profile {
  username: string;
  display_name: string;
  doctrine_profile: string;
  doctrine_document_id?: string | null;
  doctrine_document_title?: string | null;
  bible_translation: string;
  response_depth: string;
  scripture_mode: number;
  show_multiple_interpretations: number;
  onboarding_completed: number;
  interests_json?: string;
}

interface MeResponse {
  user: {
    id: string;
    name: string;
    email: string;
    image?: string | null;
    role: string;
  };
  profile: Profile | null;
  billing: BillingState;
}

type PlanKey = "free" | "core" | "pro" | "max" | "church";

interface BillingPrice {
  id: string;
  planKey: Exclude<PlanKey, "free">;
  interval: "month" | "year";
  currency: string;
  unitAmount: number;
  productName: string;
  monthlyCredits: number;
}

interface BillingState {
  configured: boolean;
  admin: boolean;
  trialAvailable: boolean;
  planKey: PlanKey;
  plan: {
    name: string;
    monthlyCredits: number;
    deepStudy: boolean;
    organization: boolean;
  };
  prices: BillingPrice[];
  topUps: Array<{
    key: "credits_100" | "credits_500" | "credits_2000";
    name: string;
    credits: number;
    unitAmount: number;
    configured: boolean;
  }>;
  organizations: Array<{
    organizationId: string;
    name: string;
    role: string;
    status: string | null;
    seatCount: number | null;
    available: number;
    reserved: number;
    refreshAt: number | null;
  }>;
  credits: {
    allowance: number;
    included: number;
    topUp: number;
    reserved: number;
    available: number;
    percentRemaining: number;
    refreshAt: number | null;
    unlimited: boolean;
  };
  subscription: {
    id: string;
    priceId: string | null;
    status: string;
    entitled: boolean;
    currentPeriodEnd: number | null;
    cancelAtPeriodEnd: boolean;
    trialEnd: number | null;
    updatedAt: number;
  } | null;
}

interface Conversation {
  id: string;
  title: string;
  mode: string;
  doctrineProfile: string;
  isPinned: number;
  updatedAt: number;
  messageCount: number;
}

interface ChatMessage {
  researchRun?: ResearchRun;
  researchRunId?: string;
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt?: number;
  pending?: boolean;
}

interface Annotation {
  id: string;
  verseStart: number;
  verseEnd: number;
  highlightColor: "yellow" | "green" | "blue" | "pink" | "purple" | null;
  note: string | null;
  tagsJson?: string;
  notebookId?: string | null;
  isBookmarked: number;
}

interface StudyNotebook {
  id: string;
  title: string;
  color: string;
  annotationCount: number;
}

interface Prayer {
  id: string;
  title: string;
  body: string;
  category: string;
  scriptureReference: string | null;
  status: string;
  visibility: string;
  createdAt: number;
  updatedAt: number;
}

interface LibraryDocument {
  id: string;
  title: string;
  documentType: string;
  summary: string;
  indexingStatus: string;
  sizeBytes: number;
  excerpt?: string;
}

interface CommunityGroup {
  id: string;
  name: string;
  slug: string;
  groupType: string;
  visibility: string;
  description: string;
  role: string;
  memberCount: number;
}

interface CommunityPost {
  id: string;
  body: string;
  postType: string;
  scriptureReference: string | null;
  createdAt: number;
  authorName: string;
  username: string | null;
  commentCount: number;
  reactionCount: number;
}

interface CommunityPerson {
  userId: string;
  username: string;
  displayName: string;
  bio: string;
  image?: string | null;
  connectionStatus?: "incoming" | "outgoing" | "accepted" | null;
}

interface CommunityConnection extends CommunityPerson {
  status: "pending" | "accepted" | "declined";
  direction: "incoming" | "outgoing";
}

interface GroupInvitation {
  id: string;
  groupId: string;
  groupName: string;
  inviterUserId: string;
  inviterName: string;
  inviterUsername: string | null;
  createdAt: number;
}

interface CommunityComment {
  id: string;
  parentCommentId: string | null;
  body: string;
  depth: number;
  createdAt: number;
  authorId: string;
  authorName: string;
  username: string | null;
  reactionCount: number;
}

interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  targetUrl: string | null;
  createdAt: number;
  readAt: number | null;
}

interface DoctrineDocument {
  id: string;
  title: string;
  mimeType: string;
  sizeBytes: number;
  createdAt?: number;
  updatedAt?: number;
}

interface SupportRequest {
  id: string;
  requestType: "bug" | "feature";
  title: string;
  details: string;
  pageUrl: string | null;
  status: "open" | "reviewing" | "planned" | "resolved" | "closed";
  adminNotes: string;
  createdAt: number;
  updatedAt: number;
  resolvedAt: number | null;
}

function isAdministrator(user: MeResponse["user"] | null | undefined) {
  return (user?.role ?? "")
    .split(",")
    .some((role) => role.trim().toLowerCase() === "admin");
}

const navGroups: Array<{
  label?: string;
  items: Array<{ to: string; label: string; icon: Icon }>;
}> = [
  {
    items: [
      { to: brand.routes.askAssistant, label: "Ask Sola", icon: ChatCircleDots },
      { to: "/scripture/John/3", label: "Scripture", icon: BookOpenText },
      { to: "/sermons", label: "Sermons", icon: ChalkboardTeacher },
    ],
  },
  {
    label: "Study",
    items: [
      { to: "/study", label: "Study Hub", icon: NotePencil },
      { to: "/prayer", label: "Prayer Journal", icon: Heart },
    ],
  },
  {
    label: "Community",
    items: [
      { to: "/community", label: "Community", icon: Church },
    ],
  },
];

function doctrineLabel(profile?: Profile | null): string {
  if (profile?.doctrine_document_title) return profile.doctrine_document_title;
  return doctrineProfileLabel(profile?.doctrine_profile ?? "reformed_baptist");
}

function initials(name?: string): string {
  return (
    name
      ?.split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase() || "BA"
  );
}

function ProfilePlanRing({
  user,
  billing,
  compact = false,
}: {
  user: MeResponse["user"];
  billing: BillingState | null | undefined;
  compact?: boolean;
}) {
  const planKey = billing?.planKey ?? "free";
  const remaining = billing?.credits.unlimited
    ? 100
    : Math.max(0, Math.min(100, billing?.credits.percentRemaining ?? 0));
  const label = billing?.credits.unlimited
    ? "Administrator plan, unlimited Sola access"
    : `${billing?.plan.name ?? "Free"} plan, ${Math.round(remaining)}% of Sola credits remaining`;
  return (
    <span
      className={`plan-ring plan-${planKey} ${compact ? "compact" : ""}`}
      style={{ "--credits-remaining": `${remaining}%` } as CSSProperties}
      role="img"
      aria-label={label}
      title={label}
    >
      <span className="plan-ring-avatar">
        {user.image ? (
          <img src={user.image} alt="" referrerPolicy="no-referrer" />
        ) : (
          initials(user.name)
        )}
      </span>
    </span>
  );
}

export function App() {
  useEffect(()=>{
    const viewport=window.visualViewport;
    if(!viewport)return;
    const resize=()=>{if(viewport.scale===1)document.documentElement.style.setProperty('--visible-height',`${viewport.height}px`);};
    resize();viewport.addEventListener('resize',resize);
    return()=>{viewport.removeEventListener('resize',resize);document.documentElement.style.removeProperty('--visible-height');};
  },[]);
  const session = authClient.useSession();
  const location = useLocation();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [meLoaded, setMeLoaded] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [themePreference, setThemePreference] = useState<ThemePreference>(readThemePreference);
  const [systemDark, setSystemDark] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches,
  );
  const systemTheme: ResolvedTheme = systemDark ? "dark" : "light";
  const resolvedTheme: ResolvedTheme =
    themePreference === "system" ? systemTheme : themePreference;

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    setSystemDark(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(THEME_STORAGE_KEY, themePreference);
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.style.colorScheme = resolvedTheme;
    document
      .querySelector(
        `meta[name="theme-color"][media*="${resolvedTheme === "dark" ? "dark" : "light"}"]`,
      )
      ?.setAttribute(
        "content",
        resolvedTheme === "dark"
          ? brand.theme.dark.background
          : brand.theme.light.background,
      );
  }, [resolvedTheme, themePreference]);

  const refreshMe = async () => {
    if (!session.data?.user) {
      setMe(null);
      setMeLoaded(true);
      return;
    }
    try {
      const response = await api<MeResponse>("/api/me");
      setMe(response);
    } finally {
      setMeLoaded(true);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setMeLoaded(false);
    const loadCurrentUser = async () => {
      if (!session.data?.user) {
        if (!cancelled) {
          setMe(null);
          setMeLoaded(true);
        }
        return;
      }
      try {
        const response = await api<MeResponse>("/api/me");
        if (!cancelled) setMe(response);
      } finally {
        if (!cancelled) setMeLoaded(true);
      }
    };
    void loadCurrentUser();
    return () => {
      cancelled = true;
    };
  }, [session.data?.user?.id]);

  const openAuth = () => setAuthOpen(true);

  const scriptureRoute = location.pathname.startsWith("/scripture");
  const sermonBuilderRoute =
    location.pathname !== "/sermons/new" &&
    /^\/sermons\/[^/]+$/.test(location.pathname);

  return (
    <BrandSurfaceProvider surface={resolvedTheme}>
      <div className={`app ${scriptureRoute ? "scripture-app" : ""}`}>
      <Sidebar
        user={me?.user ?? null}
        profile={me?.profile ?? null}
        billing={me?.billing ?? null}
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        onAuth={openAuth}
        themePreference={themePreference}
        systemTheme={systemTheme}
        onThemeChange={setThemePreference}
      />
      <div className="shell">
        <Topbar
          user={me?.user ?? null}
          profile={me?.profile ?? null}
          billing={me?.billing ?? null}
          onMenu={() => setMobileOpen(true)}
          onAuth={openAuth}
        />
        <main
          className={`main-content ${
            location.pathname.startsWith("/ask-sola") ||
            location.pathname.startsWith("/scripture")
              ? "canvas-content"
              : sermonBuilderRoute
                ? "sermon-builder-content"
                : ""
          }`}
          id="main-content"
        >
          <Routes>
            <Route path="/" element={<Navigate replace to={brand.routes.askAssistant} />} />
            <Route
              path="/ask-sola"
              element={
                <Chat
                  user={me?.user ?? null}
                  billing={me?.billing ?? null}
                  onAuth={openAuth}
                />
              }
            />
            <Route
              path="/scripture"
              element={<Navigate replace to="/scripture/John/3" />}
            />
            <Route
              path="/scripture/:book/:chapter"
              element={
                <ScriptureReader
                  user={me?.user ?? null}
                  profile={me?.profile ?? null}
                  onAuth={openAuth}
                  onOpenNavigation={() => setMobileOpen(true)}
                />
              }
            />
            <Route
              path="/study"
              element={<StudyHub user={me?.user ?? null} onAuth={openAuth} />}
            />
            <Route
              path="/sermons"
              element={
                <SermonDashboard
                  user={me?.user ?? null}
                  billing={me?.billing ?? null}
                  onAuth={openAuth}
                />
              }
            />
            <Route
              path="/sermons/new"
              element={
                <NewSermon
                  user={me?.user ?? null}
                  billing={me?.billing ?? null}
                  onAuth={openAuth}
                />
              }
            />
            <Route
              path="/sermons/:id"
              element={
                <SermonBuilder
                  user={me?.user ?? null}
                  billing={me?.billing ?? null}
                  onAuth={openAuth}
                />
              }
            />
            <Route
              path="/sermons/:id/preach"
              element={
                <SermonPreach
                  user={me?.user ?? null}
                  billing={me?.billing ?? null}
                  onAuth={openAuth}
                />
              }
            />
            <Route
              path="/study-legacy"
              element={<StudyDashboard user={me?.user ?? null} onAuth={openAuth} />}
            />
            <Route path="/deep-study" element={<Navigate replace to="/ask-sola?deepStudy=true" />} />
            <Route path="/doctrine-guard" element={<Navigate replace to="/ask-sola?mode=doctrine" />} />
            <Route path="/berean" element={<Navigate replace to="/ask-sola?mode=berean" />} />
            <Route path="/library" element={<Navigate replace to="/study?tab=documents" />} />
            <Route path="/pastor" element={<Navigate replace to="/ask-sola?mode=pastor" />} />
            <Route path="/family" element={<Navigate replace to="/ask-sola?mode=family" />} />
            <Route
              path="/deep-study-legacy"
              element={
                <DeepStudy
                  user={me?.user ?? null}
                  billing={me?.billing ?? null}
                  onAuth={openAuth}
                />
              }
            />
            <Route
              path="/doctrine-guard-legacy"
              element={
                <PromptTool
                  icon={ShieldCheck}
                  title="DoctrineGuard"
                  subtitle="Check claims for biblical support, missing context, and tension with your selected doctrinal profile."
                  badge="AI-assisted review"
                  fieldLabel="Content to review"
                  placeholder="Paste a sermon excerpt, article, social post, or theological claim…"
                  action="Analyze content"
                  mode="doctrine"
                  promptPrefix="Review the following with DoctrineGuard. Separate exact biblical support, missing context, theological interpretation, denominational perspective, and AI suggestions. Do not judge motives:\n\n"
                  asideTitle="Transparent assessment"
                  asideCopy="DoctrineGuard flags claims for closer study. It does not pronounce a person faithful or unfaithful, and its conclusions remain fallible."
                  user={me?.user ?? null}
                  onAuth={openAuth}
                />
              }
            />
            <Route
              path="/berean-legacy"
              element={
                <PromptTool
                  icon={Scales}
                  title="Berean Mode"
                  subtitle="See your selected position and the strongest responsible opposing case side by side."
                  badge="Steelman enabled"
                  fieldLabel="Disputed theological question"
                  placeholder="Should baptism be administered only to professing believers?"
                  action="Compare interpretations"
                  mode="berean"
                  promptPrefix="Use Berean Mode for this question. Present the Reformed Baptist position first, then the strongest responsible opposing Christian interpretation. Cite Scripture, identify the interpretive hinge, and distinguish text, context, interpretation, and application:\n\n"
                  asideTitle="Responsible comparison"
                  asideCopy="Sola should explain disagreements fairly enough that a thoughtful advocate of each view would recognize the case."
                  user={me?.user ?? null}
                  onAuth={openAuth}
                />
              }
            />
            <Route
              path="/prayer"
              element={<PrayerJournal user={me?.user ?? null} onAuth={openAuth} />}
            />
            <Route
              path="/library-legacy"
              element={<Library user={me?.user ?? null} onAuth={openAuth} />}
            />
            <Route
              path="/pastor-legacy"
              element={
                <PromptTool
                  icon={ChalkboardTeacher}
                  title="Pastor / Teacher Workspace"
                  subtitle="Build outlines, lessons, discussion questions, and teaching material with Scripture kept in view."
                  badge="Ministry tools"
                  fieldLabel="Passage, theme, and audience"
                  placeholder="John 15:1–11 for a 25-minute adult Sunday school lesson on abiding in Christ…"
                  action="Create teaching plan"
                  mode="pastor"
                  promptPrefix="Create a pastor/teacher workspace plan from the following. Include exegetical context, outline, illustrations clearly labeled as suggestions, discussion questions, and passages to read in context. Do not write as though the AI has pastoral authority:\n\n"
                  asideTitle="Study before polish"
                  asideCopy="The workspace helps organize preparation. Teachers remain responsible to verify every quotation, interpretation, and application."
                  user={me?.user ?? null}
                  onAuth={openAuth}
                />
              }
            />
            <Route
              path="/family-legacy"
              element={
                <PromptTool
                  icon={UsersThree}
                  title="Family Mode"
                  subtitle="Kid-friendly explanations, family devotionals, catechism practice, and conversation guides."
                  badge="Age-aware"
                  fieldLabel="Question, topic, and age range"
                  placeholder="Explain forgiveness for ages 9–12 and include two discussion questions."
                  action="Create family answer"
                  mode="family"
                  promptPrefix="Use Family Mode to answer the following. Be truthful, concrete, age-appropriate, never manipulative, and clearly label any suggested prayer or activity as AI-generated:\n\n"
                  asideTitle="Faithful at the right level"
                  asideCopy="Family Mode simplifies vocabulary, not truth. Parents and guardians should review generated material before sharing it with children."
                  user={me?.user ?? null}
                  onAuth={openAuth}
                />
              }
            />
            <Route
              path="/community"
              element={<Community user={me?.user ?? null} onAuth={openAuth} />}
            />
            <Route
              path="/settings"
              element={
                <Settings
                  me={me}
                  onAuth={openAuth}
                  onRefresh={refreshMe}
                  themePreference={themePreference}
                  systemTheme={systemTheme}
                  onThemeChange={setThemePreference}
                />
              }
            />
            <Route
              path="/admin"
              element={
                <AdminDashboard
                  me={me}
                  onAuth={openAuth}
                />
              }
            />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/notifications" element={<Notifications user={me?.user ?? null} onAuth={openAuth} />} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route path="/terms" element={<LegalPage kind="terms" />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
      {authOpen ? <AuthDialog onClose={() => setAuthOpen(false)} /> : null}
      {session.data?.user && meLoaded && !me?.profile?.onboarding_completed ? (
        <Onboarding
          name={session.data.user.name}
          email={session.data.user.email}
          onComplete={refreshMe}
        />
      ) : null}
      <MobileBottomNav />
      </div>
    </BrandSurfaceProvider>
  );
}

function Sidebar({
  user,
  profile,
  billing,
  mobileOpen,
  onClose,
  onAuth,
  themePreference,
  systemTheme,
  onThemeChange,
}: {
  user: MeResponse["user"] | null;
  profile: Profile | null;
  billing: BillingState | null;
  mobileOpen: boolean;
  onClose: () => void;
  onAuth: () => void;
  themePreference: ThemePreference;
  systemTheme: ResolvedTheme;
  onThemeChange: (theme: ThemePreference) => void;
}) {
  const location = useLocation();
  const [accountOpen, setAccountOpen] = useState(false);
  const [chatHistoryOpen, setChatHistoryOpen] = useState(() =>
    location.pathname.startsWith(brand.routes.askAssistant),
  );
  const [deepStudyHistoryOpen, setDeepStudyHistoryOpen] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<Conversation[]>([]);
  const [deepStudyHistory, setDeepStudyHistory] = useState<DeepStudySummary[]>([]);
  const activeConversationId = new URLSearchParams(location.search).get("conversation");
  const activeStudyId = new URLSearchParams(location.search).get("study");
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      navGroups
        .filter((group) => group.label)
        .map((group) => [
          group.label!,
          group.items.some((item) => location.pathname.startsWith(item.to.split("/:")[0]!)),
        ]),
    ),
  );
  const active = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to.split("/:")[0]!);

  useEffect(() => {
    const activeGroup = navGroups.find(
      (group) => group.label && group.items.some((item) => active(item.to)),
    );
    if (activeGroup?.label) {
      setExpandedGroups((current) => ({ ...current, [activeGroup.label!]: true }));
    }
    if (location.pathname.startsWith(brand.routes.askAssistant)) setChatHistoryOpen(true);
    if (activeStudyId) setDeepStudyHistoryOpen(true);
    setAccountOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;
    const loadConversationHistory = async () => {
      if (!user) {
        if (!cancelled) {
          setConversationHistory([]);
          setDeepStudyHistory([]);
        }
        return;
      }
      try {
        const [response, deepStudies] = await Promise.all([
          api<{ conversations: Conversation[] }>("/api/conversations"),
          api<{ studies: DeepStudySummary[] }>("/api/deep-studies"),
        ]);
        if (!cancelled) {
          setConversationHistory(response.conversations);
          setDeepStudyHistory(deepStudies.studies);
        }
      } catch {
        if (!cancelled) {
          setConversationHistory([]);
          setDeepStudyHistory([]);
        }
      }
    };
    void loadConversationHistory();
    window.addEventListener(CONVERSATIONS_CHANGED_EVENT, loadConversationHistory);
    return () => {
      cancelled = true;
      window.removeEventListener(CONVERSATIONS_CHANGED_EVENT, loadConversationHistory);
    };
  }, [user?.id]);

  const closeSidebar = () => {
    setAccountOpen(false);
    onClose();
  };

  return (
    <>
      <aside className={`sidebar ${mobileOpen ? "open" : ""}`} aria-label="Primary navigation">
        <button className="mobile-sidebar-close" onClick={onClose} aria-label="Close navigation">
          <X size={20} />
        </button>
        <Link className="brand" to={brand.routes.askAssistant} onClick={closeSidebar} aria-label={`${brand.officialProductName} Ask Sola`}>
          <BrandLockup compact />
        </Link>
        <p className="brand-tagline">{brand.copy.positioningLabel}</p>
        <Link
          className="new-chat"
          to={brand.routes.askAssistant}
          onClick={() => {
            window.dispatchEvent(new Event(NEW_CONVERSATION_EVENT));
            closeSidebar();
          }}
        >
          <Plus size={18} weight="bold" />
          New chat
        </Link>
        <nav className="nav-list">
          {navGroups.map((group, groupIndex) => {
            const expanded = !group.label || expandedGroups[group.label] === true;
            return (
              <div className="nav-group" key={group.label ?? groupIndex}>
                {group.label ? (
                  <button
                    type="button"
                    className="nav-section-toggle"
                    aria-expanded={expanded}
                    onClick={() =>
                      setExpandedGroups((current) => ({
                        ...current,
                        [group.label!]: !expanded,
                      }))
                    }
                  >
                    <span>{group.label}</span>
                    {expanded ? <CaretDown size={13} /> : <CaretRight size={13} />}
                  </button>
                ) : null}
                {expanded
                  ? group.items.map((item) => {
                      if (item.to === "/admin" && !isAdministrator(user)) {
                        return null;
                      }
                      const NavIcon = item.icon;
                      if (item.to === brand.routes.askAssistant) {
                        return (
                          <div className="nav-chat-section" key={item.to}>
                            <div className={`nav-chat-row ${active(item.to) ? "active" : ""}`}>
                              <Link
                                className={`nav-item nav-chat-link ${active(item.to) ? "active" : ""}`}
                                to={item.to}
                                onClick={closeSidebar}
                              >
                                <NavIcon size={18} />
                                <span className="nav-label">{item.label}</span>
                              </Link>
                              <button
                                type="button"
                                className="nav-chat-toggle"
                                aria-label={`${chatHistoryOpen ? "Hide" : "Show"} conversation history`}
                                aria-expanded={chatHistoryOpen}
                                onClick={() => setChatHistoryOpen((open) => !open)}
                              >
                                {chatHistoryOpen ? <CaretDown size={13} /> : <CaretRight size={13} />}
                              </button>
                            </div>
                            {chatHistoryOpen ? (
                              <div className="nav-conversation-history" aria-label="Conversation history">
                                {!user ? (
                                  <p className="nav-history-empty">Sign in to save conversations.</p>
                                ) : conversationHistory.length ? (
                                  conversationHistory.slice(0, 12).map((conversation) => (
                                    <Link
                                      className={`nav-conversation ${
                                        activeConversationId === conversation.id ? "active" : ""
                                      }`}
                                      to={`${brand.routes.askAssistant}?conversation=${encodeURIComponent(conversation.id)}`}
                                      title={conversation.title}
                                      onClick={closeSidebar}
                                      key={conversation.id}
                                    >
                                      <span>{conversation.title}</span>
                                      <small>{new Date(conversation.updatedAt * 1000).toLocaleDateString()}</small>
                                    </Link>
                                  ))
                                ) : (
                                  <p className="nav-history-empty">No saved conversations yet.</p>
                                )}
                              </div>
                            ) : null}
                            <button
                              type="button"
                              className="nav-subsection-toggle"
                              aria-expanded={deepStudyHistoryOpen}
                              onClick={() => setDeepStudyHistoryOpen((open) => !open)}
                            >
                              <span><Sparkle size={13} /> Deep Studies</span>
                              {deepStudyHistoryOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                            </button>
                            {deepStudyHistoryOpen ? (
                              <div className="nav-conversation-history" aria-label="Saved Deep Studies">
                                {!user ? (
                                  <p className="nav-history-empty">Sign in to save Deep Studies.</p>
                                ) : deepStudyHistory.length ? (
                                  deepStudyHistory.slice(0, 12).map((study) => (
                                    <Link
                                      className={`nav-conversation ${activeStudyId === study.id ? "active" : ""}`}
                                      to={`${brand.routes.askAssistant}?study=${encodeURIComponent(study.id)}`}
                                      title={study.title}
                                      onClick={closeSidebar}
                                      key={study.id}
                                    >
                                      <span>{study.title}</span>
                                      <small>{study.progressLabel}</small>
                                    </Link>
                                  ))
                                ) : (
                                  <p className="nav-history-empty">No Deep Studies yet.</p>
                                )}
                              </div>
                            ) : null}
                          </div>
                        );
                      }
                      return (
                        <Link
                          className={`nav-item ${active(item.to) ? "active" : ""}`}
                          to={item.to}
                          key={item.to}
                          onClick={closeSidebar}
                        >
                          <NavIcon size={18} />
                          <span className="nav-label">{item.label}</span>
                        </Link>
                      );
                    })
                  : null}
              </div>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          {user && accountOpen ? (
            <div className="account-menu" role="menu" aria-label="Account and appearance">
              <div className="account-usage" aria-label="Sola credit usage">
                <div>
                  <strong>{billing?.plan.name ?? "Free"}</strong>
                  {billing?.subscription?.status === "trialing" ? (
                    <span className="pill purple">Trial</span>
                  ) : null}
                </div>
                <span>
                  {billing?.credits.unlimited
                    ? "Unlimited Sola access"
                    : `${Math.floor(billing?.credits.available ?? 0).toLocaleString()} credits · ${Math.round(billing?.credits.percentRemaining ?? 0)}%`}
                </span>
                <div
                  className="account-usage-bar"
                  role="progressbar"
                  aria-label="Sola credits remaining"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(billing?.credits.percentRemaining ?? 0)}
                >
                  <span
                    style={{
                      width: `${Math.max(0, Math.min(100, billing?.credits.percentRemaining ?? 0))}%`,
                    }}
                  />
                </div>
                <small>
                  {billing?.credits.unlimited
                    ? "All features enabled"
                    : billing?.credits.refreshAt
                    ? `Refreshes ${new Date(billing.credits.refreshAt * 1000).toLocaleDateString()}`
                    : "AI access requires a plan"}
                </small>
              </div>
              {isAdministrator(user) ? (
                <Link className="account-menu-row" to="/admin" role="menuitem" onClick={closeSidebar}>
                  <ShieldStar size={17} />
                  Admin dashboard
                </Link>
              ) : null}
              <Link className="account-menu-row" to="/settings" role="menuitem" onClick={closeSidebar}>
                <GearSix size={17} />
                Settings
              </Link>
              <div className="account-theme" aria-label={`Theme: ${themePreference}`}>
                {([
                  ["system", Monitor, "System"],
                  ["light", Sun, "Light"],
                  ["dark", Moon, "Dark"],
                ] as Array<[ThemePreference, Icon, string]>).map(([value, ThemeIcon, label]) => (
                  <button
                    type="button"
                    className={themePreference === value ? "active" : ""}
                    onClick={() => onThemeChange(value)}
                    aria-label={`${label} theme`}
                    aria-pressed={themePreference === value}
                    title={value === "system" ? `System (${systemTheme})` : `${label} theme`}
                    key={value}
                  >
                    <ThemeIcon size={16} />
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="account-menu-row"
                role="menuitem"
                onClick={() => void authClient.signOut()}
              >
                <SignOut size={17} />
                Sign out
              </button>
            </div>
          ) : null}
          <button
            className="profile-mini"
            onClick={user ? () => setAccountOpen((open) => !open) : onAuth}
            aria-expanded={user ? accountOpen : undefined}
          >
            {user ? (
              <ProfilePlanRing user={user} billing={billing} compact />
            ) : (
              <span className="avatar">{initials()}</span>
            )}
            <span className="meta">
              <span className="name">{user?.name ?? "Sign in"}</span>
              <span className="role">
                {user ? `@${profile?.username ?? "finish-profile"}` : "Sync your study"}
              </span>
            </span>
            {user ? <CaretDown size={17} /> : <SignIn size={18} />}
          </button>
        </div>
      </aside>
      {mobileOpen ? <button className="sidebar-scrim" onClick={onClose} aria-label="Close navigation" /> : null}
    </>
  );
}

function Topbar({
  user,
  profile,
  billing,
  onMenu,
  onAuth,
}: {
  user: MeResponse["user"] | null;
  profile: Profile | null;
  billing: BillingState | null;
  onMenu: () => void;
  onAuth: () => void;
}) {
  const navigate = useNavigate();
  return (
    <header className="topbar">
      <div className="topbar-left">
        <button className="top-icon mobile-menu-btn" onClick={onMenu} aria-label="Open navigation">
          <List size={22} />
        </button>
        <Link className="mobile-brand" to={brand.routes.askAssistant}>
          <BrandLockup compact />
        </Link>
        <button className="doctrine-select" onClick={() => navigate("/settings")}>
          <BibleAloneSymbol className="doctrine-brand-symbol" decorative />
          <span>
            <span className="label">{doctrineLabel(profile)}</span>
            <span className="sub">Doctrine profile</span>
          </span>
          <CaretDown size={14} />
        </button>
      </div>
      <div className="topbar-right">
        <span className="demo-status">Scripture-first</span>
        <button className="top-icon" aria-label="Notifications" onClick={() => navigate("/notifications")}>
          <Bell size={20} />
        </button>
        <button className={user ? "top-avatar-button" : "btn primary small-btn"} onClick={user ? () => navigate("/settings") : onAuth}>
          {user ? (
            <ProfilePlanRing user={user} billing={billing} />
          ) : (
            <>
              <SignIn size={16} />
              Sign in
            </>
          )}
        </button>
      </div>
    </header>
  );
}

function _Home({
  user,
  onAuth,
}: {
  user: MeResponse["user"] | null;
  onAuth: () => void;
}) {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState("general");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!prompt.trim()) return;
    if (!user) {
      onAuth();
      return;
    }
    navigate(brand.routes.askAssistant, {
      state: { prompt: prompt.trim(), mode, autoSend: true },
    });
  };

  const tools: Array<{ icon: Icon; title: string; copy: string; to: string }> = [
    { icon: BookOpenText, title: "Study Scripture", copy: "Read the complete offline KJV and keep the text in view.", to: "/scripture/John/3" },
    { icon: ShieldCheck, title: "DoctrineGuard", copy: "Check claims for support, context, and overstatement.", to: "/doctrine-guard" },
    { icon: Scales, title: "Berean Mode", copy: "Compare strong, responsible Christian interpretations.", to: "/berean" },
    { icon: ChalkboardTeacher, title: "Build a lesson", copy: "Create an outline, questions, handout, and study plan.", to: "/pastor" },
    { icon: UsersThree, title: "Family Mode", copy: "Explain difficult truths at an age-appropriate level.", to: "/family" },
  ];

  return (
    <div className="view home-grid">
      <section className="home-main">
        <div className="home-hero">
          <BibleAloneSymbol className="hero-cross" decorative />
          <h1>{user ? `Welcome back, ${user.name.split(" ")[0]}` : brand.officialProductName}</h1>
          <p>{brand.tagline}</p>
          <span className="home-campaign-line">{brand.copy.campaignTagline}</span>
        </div>
        <form className="home-composer" onSubmit={submit}>
          <label className="sr-only" htmlFor="home-prompt">Ask Sola</label>
          <textarea
            id="home-prompt"
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="Ask Sola anything—Scripture, writing, planning, coding, family, ministry…"
          />
          <div className="composer-foot">
            <div className="mode-chips" aria-label="Response mode">
              {[
                ["general", Sparkle, "Everyday"],
                ["scripture", BookOpenText, "Scripture"],
                ["berean", Scales, "Berean"],
              ].map(([value, ModeIcon, label]) => {
                const ChipIcon = ModeIcon as Icon;
                return (
                  <button
                    type="button"
                    className={`mode-chip ${mode === value ? "active" : ""}`}
                    onClick={() => setMode(String(value))}
                    key={String(value)}
                  >
                    <ChipIcon size={14} />
                    {String(label)}
                  </button>
                );
              })}
            </div>
            <button className="send-btn" type="submit" aria-label="Ask Sola">
              <ArrowUp size={18} weight="bold" />
            </button>
          </div>
        </form>
        <div className="tools-head">
          <h2 className="section-title">Study and create with Sola</h2>
          <Link className="top-icon" to="/settings" aria-label="Open settings">
            <CaretRight size={18} />
          </Link>
        </div>
        <div className="tool-grid">
          {tools.map((tool) => {
            const ToolIcon = tool.icon;
            return (
              <button className="tool-card" onClick={() => navigate(tool.to)} key={tool.title}>
                <span className="tool-icon"><ToolIcon size={24} /></span>
                <h3>{tool.title}</h3>
                <p>{tool.copy}</p>
              </button>
            );
          })}
        </div>
        <section className="authority-note">
          <div className="authority-icon"><LockKey size={20} /></div>
          <div>
            <strong>Scripture carries the authority. Sola does not.</strong>
            <p>Sola is a fallible AI study tool. Verify its commentary, read citations in context, and stay rooted in prayer, personal study, faithful pastors, teachers, and the local church.</p>
          </div>
        </section>
      </section>
      <aside className="home-rail">
        <section className="card rail-card">
          <h3>Scripture for today</h3>
          <div className="verse-art" role="img" aria-label="Open Bible in warm morning light" />
          <blockquote className="daily-verse">
            “Thy word is a lamp unto my feet, and a light unto my path.”
            <cite>Psalm 119:105 KJV</cite>
          </blockquote>
          <Link className="rail-link" to="/scripture/Psalms/119?verse=105">
            <span>Read in context</span><CaretRight size={14} />
          </Link>
        </section>
        <section className="card rail-card">
          <h3>Reading plan</h3>
          <div className="reading-box">
            <div className="reading-title">Gospel of John</div>
            <div className="progress"><span style={{ width: "14%" }} /></div>
            <div className="reading-meta"><span>3 chapters</span><span>Day 3 of 21</span></div>
          </div>
          <Link className="rail-link" to="/scripture/John/3">
            <span>Continue reading</span><CaretRight size={14} />
          </Link>
        </section>
        <section className="card rail-card">
          <div className="ai-label"><Sparkle size={13} /> AI transparency</div>
          <p className="small muted rail-copy">Generated commentary is labeled. Scripture citations open directly in the reader.</p>
        </section>
      </aside>
    </div>
  );
}

function Chat({
  user,
  billing,
  onAuth,
}: {
  user: MeResponse["user"] | null;
  billing: BillingState | null;
  onAuth: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState<string | undefined>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [deepStudies, setDeepStudies] = useState<DeepStudyDetail[]>([]);
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState("general");
  const [deepStudyEnabled, setDeepStudyEnabled] = useState(false);
  const [answerLength, setAnswerLength] = useState<AnswerLength>("standard");
  const [researchProgress, setResearchProgress] = useState("");
  const [selectedAnswerId, setSelectedAnswerId] = useState<string | null>(null);
  const [searchNotes, setSearchNotes] = useState(false);
  const [selectedNoteIds, setSelectedNoteIds] = useState<string[]>([]);
  const [noteAttachments, setNoteAttachments] = useState<NoteAttachment[]>([]);
  const [removedNoteIds, setRemovedNoteIds] = useState<string[]>([]);
  const [scriptureSelections, setScriptureSelections] = useState<ScriptureSelection[]>([]);
  const [focusedStudy, setFocusedStudy] = useState<DeepStudyDetail | null>(null);
  const [focusedStudySources, setFocusedStudySources] = useState<DeepStudySource[]>([]);
  const [savedNote, setSavedNote] = useState<{ id: string; title: string } | null>(null);
  const [organizationId, setOrganizationId] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState("");
  const [teaser, setTeaser] = useState<{
    opening: string;
    researchRun: ResearchRun;
    lockedWordCount: number;
    remainingPreviews: number;
    callToAction: string;
  } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const activeResearchRef = useRef<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const initialHandled = useRef(false);

  const loadConversation = async (id: string) => {
    const response = await api<{
      messages: ChatMessage[];
      researchRuns: {runId:string;messageId:string;status:string;error:string|null}[];
      deepStudies: DeepStudyDetail[];
    }>(`/api/conversations/${id}/messages`);
    setActiveId(id);
    const hydrated = await Promise.all(response.messages.map(async message => message.researchRunId ? {...message,researchRun:await api<ResearchRun>(`/api/research/runs/${message.researchRunId}`)} : message));
    setMessages(hydrated);
    setDeepStudies(response.deepStudies ?? []);
    setError("");
    abortRef.current?.abort();
    const pending=response.researchRuns?.find(run=>!["complete","failed","canceled"].includes(run.status));
    const latest=response.researchRuns?.[0];
    if(!pending){
      setStreaming(false);
      if(latest?.status==="failed")setError(latest.error??"Research was interrupted. Please retry your question.");
      return;
    }
    const abort=new AbortController();
    abortRef.current=abort;
    activeResearchRef.current=pending.runId;
    setStreaming(true);
    setMessages(current=>current.some(message=>message.id===pending.messageId)?current:[...current,{id:pending.messageId,role:"assistant",content:"",pending:true}]);
    void resumeResearch(pending.runId,event=>{
      if(abort.signal.aborted)return;
      if(event.progressLabel)setResearchProgress(event.progressLabel);
      if(event.researchRun)setMessages(current=>current.map(message=>message.id===pending.messageId?{...message,researchRun:event.researchRun!,content:event.researchRun!.answer??"",pending:false}:message));
      if(event.type==="done"){setResearchProgress("");setSelectedAnswerId(pending.messageId);}
    },abort.signal).catch(caught=>{
      if(!abort.signal.aborted){setError(caught instanceof Error?caught.message:String(caught));setMessages(current=>current.filter(message=>!message.pending));}
    }).finally(()=>{if(abortRef.current===abort){setStreaming(false);abortRef.current=null;}});
  };

  useEffect(()=>{
    if(!user)return;
    const saved=sessionStorage.getItem('sola-active-research');
    if(!saved){
      const pending=sessionStorage.getItem('sola-pending-request');
      if(pending){
        try{const receipt=JSON.parse(pending) as {requestId:string};
          void api<{runId:string;conversationId:string;messageId:string}>(`/api/research/requests/${receipt.requestId}`).then(result=>{
            sessionStorage.setItem('sola-active-research',JSON.stringify(result));sessionStorage.removeItem('sola-pending-request');
            if(!location.state)navigate(`${brand.routes.askAssistant}?conversation=${encodeURIComponent(result.conversationId)}`,{replace:true});
          }).catch(()=>undefined);
        }catch{sessionStorage.removeItem('sola-pending-request');}
      }
      return;
    }
    let stored:{runId:string;conversationId:string;messageId:string};
    try{stored=JSON.parse(saved);}catch{sessionStorage.removeItem('sola-active-research');return;}
    if(!new URLSearchParams(location.search).has("conversation") && !location.state){
      navigate(`${brand.routes.askAssistant}?conversation=${encodeURIComponent(stored.conversationId)}`,{replace:true});
    }
  },[user?.id]);

  const requestedConversationId = useMemo(
    () => new URLSearchParams(location.search).get("conversation"),
    [location.search],
  );
  const requestedStudyId = useMemo(
    () => new URLSearchParams(location.search).get("study"),
    [location.search],
  );

  useEffect(() => {
    if (!requestedConversationId) {
      setActiveId(undefined);
      setMessages([]);
      setDeepStudies([]);
      setError("");
      return;
    }
    if (!user) return;
    void loadConversation(requestedConversationId).catch((caught) =>
      setError(caught instanceof Error ? caught.message : "That conversation could not be opened."),
    );
  }, [requestedConversationId, user?.id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryMode = params.get("mode");
    if (queryMode && ["general", "scripture", "berean", "doctrine", "pastor", "family"].includes(queryMode)) {
      setMode(queryMode);
    }
    setDeepStudyEnabled(params.get("deepStudy") === "true");
  }, [location.search]);

  useEffect(() => {
    if (!requestedStudyId || !user) {
      setFocusedStudy(null);
      setFocusedStudySources([]);
      return;
    }
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const result = await api<{ study: DeepStudyDetail; sources: DeepStudySource[] }>(
          `/api/deep-studies/${encodeURIComponent(requestedStudyId)}`,
        );
        if (stopped) return;
        setFocusedStudy(result.study);
        setFocusedStudySources(result.sources);
        if (["queued", "planning", "gathering", "analyzing", "writing"].includes(result.study.status)) {
          timer = setTimeout(() => void poll(), 4_000);
        }
      } catch (caught) {
        if (!stopped) {
          setError(caught instanceof Error ? caught.message : "The Deep Study could not be opened.");
        }
      }
    };
    void poll();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [requestedStudyId, user?.id]);

  useEffect(() => {
    if (
      !user ||
      (!selectedNoteIds.length &&
        !searchNotes &&
        !/#([\p{L}\p{N}_-]{1,40})/u.test(prompt))
    ) {
      setNoteAttachments([]);
      return;
    }
    const timer = setTimeout(() => {
      void api<{ notes: NoteAttachment[] }>("/api/study/notes/resolve-context", {
        method: "POST",
        body: JSON.stringify({
          prompt,
          noteIds: selectedNoteIds,
          searchNotes,
        }),
      })
        .then((result) =>
          setNoteAttachments(
            result.notes.filter((note) => !removedNoteIds.includes(note.id)),
          ),
        )
        .catch(() => setNoteAttachments([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [prompt, searchNotes, selectedNoteIds.join("|"), user?.id, removedNoteIds]);

  useEffect(() => {
    messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, deepStudies, streaming]);

  useEffect(() => {
    if (
      !activeId ||
      !deepStudies.some((study) =>
        ["queued", "planning", "gathering", "analyzing", "writing"].includes(study.status),
      )
    ) {
      return;
    }
    const timer = window.setInterval(() => {
      void loadConversation(activeId).then(() => notifyConversationHistoryChanged()).catch(() => undefined);
    }, 4_000);
    return () => window.clearInterval(timer);
  }, [activeId, deepStudies.map((study) => `${study.id}:${study.status}`).join("|")]);

  const saveStudyNote = async (source: Record<string, unknown>) => {
    if (!user) {
      onAuth();
      return;
    }
    try {
      const result = await api<{ id: string; title: string }>("/api/study/notes", {
        method: "POST",
        body: JSON.stringify({ source, tags: [] }),
      });
      setSavedNote(result);
      window.setTimeout(() => setSavedNote(null), 6_000);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The study note could not be saved.");
    }
  };

  const send = async (rawPrompt = prompt, requestedMode = mode, selectedScripture = scriptureSelections) => {
    const content = rawPrompt.trim();
    if (!content || streaming) return;
    if (!user) {
      onAuth();
      return;
    }
    if (deepStudyEnabled) {
      if (billing?.planKey !== "max" && !billing?.credits.unlimited) {
        setError("Sola Deep Study is included with Bible Alone Max.");
        return;
      }
      setPrompt("");
      setError("");
      setStreaming(true);
      const localUserId = crypto.randomUUID();
      setMessages((current) => [...current, { id: localUserId, role: "user", content }]);
      try {
        const created = await api<{
          id: string;
          conversationId: string;
          requestMessageId: string;
          status: string;
        }>("/api/deep-studies", {
          method: "POST",
          body: JSON.stringify({
            question: content,
            ...(activeId ? { conversationId: activeId } : {}),
            mode: requestedMode,
            noteIds: noteAttachments.map((note) => note.id),
            searchNotes,
            scriptureSelections: selectedScripture,
          }),
        });
        setActiveId(created.conversationId);
        setMessages((current) =>
          current.map((message) =>
            message.id === localUserId ? { ...message, id: created.requestMessageId } : message,
          ),
        );
        setDeepStudies((current) => [
          ...current,
          {
            id: created.id,
            title: content.slice(0, 96),
            question: content,
            status: created.status,
            progress: 2,
            progressLabel: "Queued",
            reportMarkdown: null,
            errorMessage: null,
            requestMessageId: created.requestMessageId,
            framingMode: requestedMode,
            conversationId: created.conversationId,
            createdAt: Math.floor(Date.now() / 1_000),
            updatedAt: Math.floor(Date.now() / 1_000),
            completedAt: null,
          },
        ]);
        setDeepStudyEnabled(false);
        setNoteAttachments([]);
        setSelectedNoteIds([]);
        setScriptureSelections([]);
        setRemovedNoteIds([]);
        navigate(`${brand.routes.askAssistant}?conversation=${encodeURIComponent(created.conversationId)}`, {
          replace: true,
        });
        notifyConversationHistoryChanged();
      } catch (caught) {
        setMessages((current) => current.filter((message) => message.id !== localUserId));
        setError(caught instanceof Error ? caught.message : "The Deep Study could not be started.");
      } finally {
        setStreaming(false);
      }
      return;
    }
    if (billing?.planKey === "free" && !organizationId) {
      setPrompt("");
      setError("");
      setTeaser(null);
      setStreaming(true);
      setMessages([{ id: crypto.randomUUID(), role: "user", content }]);
      try {
        const preview = await api<{
          opening: string;
          researchRun: ResearchRun;
          lockedWordCount: number;
          remainingPreviews: number;
          callToAction: string;
        }>("/api/teaser", {
          method: "POST",
          body: JSON.stringify({ prompt: content }),
        });
        setTeaser(preview);
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Sola could not create a preview.");
      } finally {
        setStreaming(false);
      }
      return;
    }
    setTeaser(null);
    setPrompt("");
    setError("");
    setStreaming(true);
    const localUserId = crypto.randomUUID();
    const localAssistantId = crypto.randomUUID();
    setMessages((current) => [
      ...current,
      { id: localUserId, role: "user", content },
      { id: localAssistantId, role: "assistant", content: "", pending: true },
    ]);
    abortRef.current = new AbortController();
    try {
      await streamChat(
        {
          answerLength,
          prompt: content,
          ...(activeId ? { conversationId: activeId } : {}),
          ...(organizationId ? { organizationId } : {}),
          mode: requestedMode,
          noteIds: noteAttachments.map((note) => note.id),
          searchNotes,
          scriptureSelections: selectedScripture,
        },
        (event) => {
          if (event.type === "research") {
            if(event.progressLabel) setResearchProgress(event.progressLabel);
            if(event.researchRun) setMessages(current=>current.map(message=>message.pending?{...message,researchRun:event.researchRun!}:message));
          }
          if (event.type === "meta") {
            if(event.runId) activeResearchRef.current=event.runId;
            if (event.conversationId) setActiveId(event.conversationId);
            if (event.messageId) {
              setMessages((current) =>
                current.map((message) =>
                  message.id === localAssistantId ? { ...message, id: event.messageId! } : message,
                ),
              );
            }
          }
          if (event.type === "delta" && event.content) {
            setMessages((current) =>
              current.map((message) =>
                message.id === localAssistantId || message.pending
                  ? { ...message, content: message.content + event.content!, pending: true }
                  : message,
              ),
            );
          }
          if (event.type === "done") {
            setResearchProgress("");
            setSelectedAnswerId(event.messageId ?? null);
            setMessages((current) => current.map((message) => ({ ...message, pending: false })));
          }
          if (event.type === "error") setError(event.error ?? "The response was interrupted.");
        },
        abortRef.current.signal,
      );
      notifyConversationHistoryChanged();
      setNoteAttachments([]);
      setSelectedNoteIds([]);
      setRemovedNoteIds([]);
      setScriptureSelections([]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Sola could not respond.");
      setMessages((current) => current.filter((message) => !message.pending));
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  useEffect(() => {
    if (initialHandled.current) return;
    const incoming = location.state as {
      prompt?: string;
      mode?: string;
      autoSend?: boolean;
      noteIds?: string[];
      scriptureSelections?: ScriptureSelection[];
    } | null;
    if (
      !incoming?.prompt &&
      !incoming?.noteIds?.length &&
      !incoming?.scriptureSelections?.length
    ) return;
    initialHandled.current = true;
    if (incoming.noteIds?.length) setSelectedNoteIds(incoming.noteIds);
    if (incoming.scriptureSelections?.length) {
      setScriptureSelections(incoming.scriptureSelections);
    }
    setMode(incoming.mode ?? "general");
    if (incoming.prompt) {
      if (
        incoming.autoSend &&
        !incoming.noteIds?.length
      ) {
        void send(incoming.prompt, incoming.mode ?? "general",incoming.scriptureSelections??[]);
      } else {
        setPrompt(incoming.prompt);
      }
    }
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, user?.id]);

  const startNew = () => {
    abortRef.current?.abort();
    setActiveId(undefined);
    setMessages([]);
    setDeepStudies([]);
    setPrompt("");
    setDeepStudyEnabled(false);
    setSearchNotes(false);
    setSelectedNoteIds([]);
    setNoteAttachments([]);
    setScriptureSelections([]);
    setRemovedNoteIds([]);
    setError("");
    setTeaser(null);
    navigate(brand.routes.askAssistant);
  };

  useEffect(() => {
    const handleNewConversation = () => {
      abortRef.current?.abort();
      setActiveId(undefined);
      setMessages([]);
      setDeepStudies([]);
      setPrompt("");
      setDeepStudyEnabled(false);
      setSearchNotes(false);
      setSelectedNoteIds([]);
      setNoteAttachments([]);
      setScriptureSelections([]);
      setRemovedNoteIds([]);
      setError("");
      setTeaser(null);
      navigate(brand.routes.askAssistant);
    };
    window.addEventListener(NEW_CONVERSATION_EVENT, handleNewConversation);
    return () => window.removeEventListener(NEW_CONVERSATION_EVENT, handleNewConversation);
  }, [navigate]);

  const toggleRecording = async () => {
    if (!user) return onAuth();
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setError("Voice input is not supported by this browser.");
      return;
    }
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;
      const preferredType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "";
      const recorder = new MediaRecorder(stream, preferredType ? { mimeType: preferredType } : undefined);
      const chunks: BlobPart[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size) chunks.push(event.data);
      };
      recorder.onstop = async () => {
        setRecording(false);
        setTranscribing(true);
        recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
        const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
        const form = new FormData();
        form.set("file", blob, "sola-recording.webm");
        try {
          const response = await fetch("/api/speech/stt", {
            method: "POST",
            body: form,
            credentials: "include",
          });
          const result = (await response.json()) as { text?: string; error?: string };
          if (!response.ok) throw new Error(result.error ?? "The recording could not be transcribed.");
          setPrompt((current) => `${current}${current && result.text ? " " : ""}${result.text ?? ""}`);
        } catch (caught) {
          setError(caught instanceof Error ? caught.message : "The recording could not be transcribed.");
        } finally {
          setTranscribing(false);
          recorderRef.current = null;
          recordingStreamRef.current = null;
        }
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      setError("Microphone access was not granted.");
    }
  };

  useEffect(
    () => () => {
      if (recorderRef.current?.state === "recording") recorderRef.current.stop();
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
    },
    [],
  );

  return (
    <div className="view chat-layout">
      <section className="chat-panel">
        <header className="chat-head">
          <div className="chat-title-row">
            <div>
              <SolaLabel className="chat-head-title">
                {activeId ? "Conversation with Sola" : "Ask Sola"}
              </SolaLabel>
              <div className="tiny muted">AI-generated commentary · Verify Scripture in context</div>
            </div>
          </div>
          <div className="row">
            {activeId ? (
              <button
                className="message-action"
                type="button"
                onClick={() =>
                  void saveStudyNote({
                    sourceType: "conversation",
                    conversationId: activeId,
                  })
                }
              >
                <NotePencil size={14} /> Save conversation
              </button>
            ) : null}
            {activeId ? (
              <button
                className="top-icon"
                onClick={async () => {
                  await api(`/api/conversations/${activeId}`, { method: "DELETE" });
                  startNew();
                  notifyConversationHistoryChanged();
                }}
                aria-label="Delete conversation"
              >
                <Trash size={17} />
              </button>
            ) : null}
          </div>
        </header>
        <StudyWorkspace runId={focusedStudy?.researchRunId??deepStudies.find(study=>study.id===selectedAnswerId)?.researchRunId} answerRuns={messages.flatMap(message=>message.researchRun?[message.researchRun]:[])} selections={scriptureSelections} run={(messages.find(message=>message.id===selectedAnswerId) ?? [...messages].reverse().find(message=>message.role==="assistant"))?.researchRun ?? teaser?.researchRun ?? null} onAsk={question=>setPrompt(question)}>
        <div className="chat-messages" ref={messagesRef} aria-live="polite">
          {focusedStudy ? (
            <DeepStudyArtifact
              study={focusedStudy}
              sources={focusedStudySources}
              onSave={(excerpt) =>
                void saveStudyNote({
                  sourceType: "deep-study",
                  deepStudyId: focusedStudy.id,
                  ...(excerpt ? { excerpt } : {}),
                })
              }
            />
          ) : messages.length ? (
            messages.map((message) => (
              <div data-research-run={message.researchRun?.id} className="message-sequence" key={message.id} onClick={()=>{if(message.role==="assistant")setSelectedAnswerId(message.id);}}>
                <Message
                  message={message}
                  onSave={() =>
                    void saveStudyNote({ sourceType: "message", messageId: message.id })
                  }
                />
                {message.role==="assistant"&&message.researchRun?<button type="button" className="source-link answer-study-select" aria-pressed={(selectedAnswerId??[...messages].reverse().find(item=>item.role==="assistant")?.id)===message.id} onClick={()=>setSelectedAnswerId(message.id)}>Study this answer</button>:null}
                {deepStudies
                  .filter((study) => study.requestMessageId === message.id)
                  .map((study) => (
                    <DeepStudyArtifact
                      study={study}
                      sources={study.sources ?? []}
                      onSave={(excerpt) =>
                        void saveStudyNote({
                          sourceType: "deep-study",
                          deepStudyId: study.id,
                          ...(excerpt ? { excerpt } : {}),
                        })
                      }
                      key={study.id}
                    />
                  ))}
              </div>
            ))
          ) : (
            <ChatWelcome onSuggestion={(suggestion) => void send(suggestion)} />
          )}
          {teaser ? (
            <article className="teaser-answer">
              <div className="ai-label"><SolaMark decorative /> Sola preview · not saved</div>
              <MarkdownContent>{teaser.opening}</MarkdownContent>
              <div
                className="teaser-locked"
                aria-label={`${teaser.lockedWordCount} additional words available with a trial`}
              >
                <span />
                <span />
                <span />
                <span />
              </div>
              <div className="teaser-cta">
                <div>
                  <strong>{teaser.callToAction}</strong>
                  <small>{teaser.remainingPreviews} lifetime previews remaining</small>
                </div>
                <Link className="btn primary" to="/settings?billing=plans">
                  See plans
                </Link>
              </div>
            </article>
          ) : null}
          {error ? (
            <div className="chat-error" role="alert"><WarningCircle size={17} /> {error}{!streaming&&messages.some(message=>message.role==="user") ? <button type="button" className="ghost-button" onClick={()=>void send([...messages].reverse().find(message=>message.role==="user")!.content)}>Retry</button>:null}</div>
          ) : null}
        </div>
        </StudyWorkspace>
        {streaming && researchProgress ? <p className="research-progress" role="status">{researchProgress}…</p> : null}
        <div className="chat-composer-wrap">
          {scriptureSelections.length ? (
            <div className="note-attachment-row scripture-attachment-row" aria-label="Scripture attached to this message">
              {scriptureSelections.flatMap((selection, selectionIndex) =>
                selection.ranges.map((range, rangeIndex) => (
                  <span className="note-attachment-chip scripture-attachment-chip" key={`${selectionIndex}:${rangeIndex}:${scriptureRangeLabel(range)}`}>
                    <BookOpenText size={13} />
                    <span>{scriptureRangeLabel(range)} · {bibleVersion(selection.versionId).abbreviation}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${scriptureRangeLabel(range)}`}
                      onClick={() =>
                        setScriptureSelections((current) =>
                          current
                            .map((candidate, currentSelectionIndex) =>
                              currentSelectionIndex === selectionIndex
                                ? {
                                    ...candidate,
                                    ranges: candidate.ranges.filter((_, currentRangeIndex) => currentRangeIndex !== rangeIndex),
                                  }
                                : candidate,
                            )
                            .filter((candidate) => candidate.ranges.length > 0),
                        )
                      }
                    >
                      <X size={12} />
                    </button>
                  </span>
                )),
              )}
            </div>
          ) : null}
          {noteAttachments.length ? (
            <div className="note-attachment-row" aria-label="Study notes attached to this message">
              {noteAttachments.map((note) => (
                <span className="note-attachment-chip" key={note.id}>
                  <NotePencil size={13} />
                  <span>{note.title}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${note.title}`}
                    onClick={() => {
                      setRemovedNoteIds((current) => [...current, note.id]);
                      setSelectedNoteIds((current) =>
                        current.filter((candidate) => candidate !== note.id),
                      );
                      setNoteAttachments((current) =>
                        current.filter((candidate) => candidate.id !== note.id),
                      );
                    }}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          <form
            className="chat-composer"
            onSubmit={(event) => {
              event.preventDefault();
              void send();
            }}
          >
            <label className="sr-only" htmlFor="chat-prompt">Message Sola</label>
            <textarea
              id="chat-prompt"
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  void send();
                }
              }}
              placeholder={user ? "Message Sola…" : "Sign in to ask Sola…"}
              disabled={streaming}
            />
            <div className="composer-context-controls">
              <label className="answer-length">Answer length <select aria-label="Answer length" value={answerLength} onChange={event=>setAnswerLength(event.target.value as AnswerLength)}><option value="brief">Brief</option><option value="standard">Standard</option><option value="detailed">Detailed</option></select></label>
              <button
                className={`composer-context-toggle ${deepStudyEnabled ? "active" : ""}`}
                type="button"
                aria-pressed={deepStudyEnabled}
                onClick={() => setDeepStudyEnabled((enabled) => !enabled)}
              >
                <Sparkle size={14} /> Deep Study
                {billing?.planKey !== "max" && !billing?.credits.unlimited ? <small>Max</small> : null}
              </button>
              <button
                className={`composer-context-toggle ${searchNotes ? "active" : ""}`}
                type="button"
                aria-pressed={searchNotes}
                onClick={() => setSearchNotes((enabled) => !enabled)}
              >
                <MagnifyingGlass size={14} /> Search my notes
              </button>
              {prompt.trim() ? (
                <button
                  className="composer-context-toggle"
                  type="button"
                  onClick={() =>
                    void saveStudyNote({
                      sourceType: "manual",
                      body: prompt.trim(),
                    })
                  }
                >
                  <NotePencil size={14} /> Save draft
                </button>
              ) : null}
            </div>
            <div className="chat-composer-foot">
              <div className="row">
                <select
                  className="chat-mode-select"
                  value={mode}
                  onChange={(event) => setMode(event.target.value)}
                  aria-label="Response mode"
                >
                  <option value="general">Everyday</option>
                  <option value="scripture">Scripture Mode</option>
                  <option value="berean">Berean Mode</option>
                  <option value="doctrine">DoctrineGuard</option>
                  <option value="pastor">Pastor / Teacher</option>
                  <option value="family">Family Mode</option>
                </select>
                {billing?.organizations.some((organization) =>
                  ["active", "trialing"].includes(organization.status ?? ""),
                ) ? (
                  <select
                    className="chat-mode-select"
                    value={organizationId}
                    onChange={(event) => setOrganizationId(event.target.value)}
                    aria-label="Credit balance"
                  >
                    <option value="">Personal credits</option>
                    {billing.organizations
                      .filter((organization) =>
                        ["active", "trialing"].includes(organization.status ?? ""),
                      )
                      .map((organization) => (
                        <option value={organization.organizationId} key={organization.organizationId}>
                          {organization.name} · {Math.floor(organization.available)} credits
                        </option>
                      ))}
                  </select>
                ) : null}
                <button
                  className={`composer-tool ${recording ? "recording" : ""}`}
                  type="button"
                  title={recording ? "Stop recording" : "Voice input"}
                  onClick={() => void toggleRecording()}
                  disabled={streaming || transcribing}
                  aria-pressed={recording}
                >
                  <Microphone size={17} />
                </button>
                <span className="tiny muted desktop-only">
                  {recording ? "Listening… select again to stop" : transcribing ? "Transcribing…" : "Voice input"}
                </span>
              </div>
              {streaming ? (
                <button
                  className="send-btn stop"
                  type="button"
                  onClick={() => {
                    const id=activeResearchRef.current;
                    if(id) void api(`/api/research/runs/${id}/cancel`,{method:"POST"}).then(()=>abortRef.current?.abort()).catch(caught=>setError(String(caught)));
                  }}
                  aria-label="Stop Sola"
                >
                  <X size={17} weight="bold" />
                </button>
              ) : (
                <button className="send-btn" type="submit" aria-label={deepStudyEnabled ? "Begin Deep Study" : "Send message"}>
                  {deepStudyEnabled ? <Sparkle size={18} weight="bold" /> : <ArrowUp size={18} weight="bold" />}
                </button>
              )}
            </div>
          </form>
          <p className="composer-disclaimer">
            Sola can make mistakes. Scripture is authoritative; AI commentary is not.
          </p>
        </div>
      </section>
      {savedNote ? (
        <div className="note-saved-toast" role="status">
          <CheckCircle size={18} />
          <span>Saved “{savedNote.title}”</span>
          <Link to={`/study?note=${encodeURIComponent(savedNote.id)}`}>Edit</Link>
          <button
            type="button"
            onClick={async () => {
              await api(`/api/study/notes/${savedNote.id}`, { method: "DELETE" });
              setSavedNote(null);
            }}
          >
            Undo
          </button>
        </div>
      ) : null}
    </div>
  );
}

function DeepStudyArtifact({
  study,
  sources,
  onSave,
}: {
  study: DeepStudyDetail;
  sources: DeepStudySource[];
  onSave: (excerpt?: string) => void;
}) {
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const artifactRef = useRef<HTMLElement>(null);
  const active = ["queued", "planning", "gathering", "analyzing", "writing"].includes(
    study.status,
  );
  return (
    <article data-research-run={study.researchRunId} ref={artifactRef} className="deep-study-artifact" aria-label={`Deep Study: ${study.title}`}>
      <header className="deep-study-report-head">
        <div>
          <span className="ai-label"><SolaMark decorative /> AI-generated research assistance</span>
          <h2>{study.title}</h2>
          <small>{study.framingMode ? `${study.framingMode} framing` : "Scripture-centered research"}</small>
        </div>
        <div className="row">
          {study.reportMarkdown ? (
            <button className="message-action" type="button" onClick={() => onSave()}>
              <NotePencil size={14} /> Save report as note
            </button>
          ) : null}
          {study.reportMarkdown ? (
            <button
              className="message-action"
              type="button"
              onClick={() => {
                const selection = window.getSelection();
                const excerpt = selection?.toString().trim() ?? "";
                const anchor = selection?.anchorNode;
                if (!excerpt || !anchor || !artifactRef.current?.contains(anchor)) {
                  setCancelError("Select part of this report first, then choose Save selection.");
                  return;
                }
                onSave(excerpt);
              }}
            >
              <NotePencil size={14} /> Save selection
            </button>
          ) : null}
          {active ? (
            <button
              className="message-action"
              type="button"
              disabled={canceling}
              onClick={async () => {
                setCanceling(true);
                setCancelError("");
                try {
                  await api(`/api/deep-studies/${study.id}/cancel`, { method: "POST" });
                  notifyConversationHistoryChanged();
                } catch (caught) {
                  setCancelError(caught instanceof Error ? caught.message : "The study could not be canceled.");
                } finally {
                  setCanceling(false);
                }
              }}
            >
              <X size={14} /> {canceling ? "Canceling…" : "Cancel"}
            </button>
          ) : null}
        </div>
      </header>
      <p className="deep-study-status" role="status">{study.progressLabel}</p>
      {cancelError ? <div className="chat-error">{cancelError}</div> : null}
      {study.errorMessage ? <div className="chat-error">{study.errorMessage}</div> : null}
      {study.reportMarkdown ? (
        <MarkdownContent className="deep-study-markdown ai-commentary">
          {study.reportMarkdown}
        </MarkdownContent>
      ) : ["failed", "canceled"].includes(study.status) ? (
        <div className="deep-study-waiting">
          <WarningCircle size={28} />
          <p>{study.status === "canceled" ? "This study was canceled." : "This study did not complete. You can begin it again from Ask Sola."}</p>
        </div>
      ) : (
        <div className="deep-study-waiting compact">
          <Sparkle size={28} />
          <p>Sola is gathering permitted sources and preparing a clearly labeled report. You can keep chatting while it works.</p>
        </div>
      )}
      {sources.length ? (
        <details className="source-register">
          <summary>{sources.length} provenance-tracked sources</summary>
          {sources.map((source, index) => (
            <article key={`${source.title}-${index}`}>
              <strong>{source.title}</strong>
              <span>{[source.sourceType, source.edition, source.language].filter(Boolean).join(" · ")}</span>
              <small>{source.license ?? "License not specified"} · {source.attribution ?? "Attribution not specified"}</small>
              {source.url ? <a href={source.url} target="_blank" rel="noreferrer">Open source</a> : null}
            </article>
          ))}
        </details>
      ) : null}
      <div className="doctrinal-boundary compact">
        <ShieldCheck size={18} />
        <p>Sola’s report is fallible. Verify citations and conclusions in context.</p>
      </div>
    </article>
  );
}

function Message({
  message,
  onSave,
}: {
  message: ChatMessage;
  onSave: () => void;
}) {
  const [speaking, setSpeaking] = useState(false);
  const [speechError, setSpeechError] = useState("");
  const references = useMemo(() => {
    const matches = message.content.match(
      /(?:[1-3]\s)?(?:Genesis|Exodus|Leviticus|Numbers|Deuteronomy|Joshua|Judges|Ruth|Samuel|Kings|Chronicles|Ezra|Nehemiah|Esther|Job|Psalms?|Proverbs|Ecclesiastes|Song of Solomon|Isaiah|Jeremiah|Lamentations|Ezekiel|Daniel|Hosea|Joel|Amos|Obadiah|Jonah|Micah|Nahum|Habakkuk|Zephaniah|Haggai|Zechariah|Malachi|Matthew|Mark|Luke|John|Acts|Romans|Corinthians|Galatians|Ephesians|Philippians|Colossians|Thessalonians|Timothy|Titus|Philemon|Hebrews|James|Peter|Jude|Revelation)\s+\d{1,3}:\d{1,3}(?:[-–]\d{1,3})?/gi,
    );
    return [...new Set(matches ?? [])].slice(0, 12);
  }, [message.content]);

  const speak = async () => {
    if (speaking) return;
    setSpeaking(true);
    setSpeechError("");
    try {
      const response = await fetch("/api/speech/tts", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: message.content }),
      });
      if (!response.ok) {
        const result = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(result.error ?? "Sola's voice is unavailable.");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => {
        URL.revokeObjectURL(url);
        setSpeaking(false);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        setSpeechError("The audio could not be played.");
        setSpeaking(false);
      };
      await audio.play();
    } catch (caught) {
      setSpeechError(caught instanceof Error ? caught.message : "Sola's voice is unavailable.");
      setSpeaking(false);
    }
  };

  return (
    <article className={`message ${message.role === "assistant" ? "ai" : "user"}`}>
      <div className="message-avatar" aria-hidden="true">
        {message.role === "assistant" ? <SolaMark decorative /> : "You"}
      </div>
      <div className="message-bubble">
        {message.role === "assistant" ? (
          <>
            <div className="generated-label"><SolaMark decorative /> Sola-generated commentary</div>
            {message.pending && !message.content ? (
              <div className="thinking" aria-label="Sola is thinking"><span /><span /><span /></div>
            ) : (
              <MarkdownContent>{message.content}</MarkdownContent>
            )}
            {references.length ? (
              <div className="citation-chips" aria-label="Scripture citations">
                {references.map((reference) => (
                  <Link className="citation-chip" to={referencePath(reference)} key={reference}>
                    <BookOpenText size={12} /> {reference}
                  </Link>
                ))}
              </div>
            ) : null}
            {message.content ? (
              <div className="message-actions">
                <button
                  className="message-action"
                  onClick={() => void navigator.clipboard.writeText(message.content)}
                >
                  Copy
                </button>
                <button
                  className="message-action"
                  title="Listen to Sola"
                  onClick={() => void speak()}
                  disabled={speaking}
                >
                  <SpeakerHigh size={15} />
                  {speaking ? "Playing…" : "Listen"}
                </button>
                <button className="message-action" onClick={onSave}>
                  <NotePencil size={14} /> Save note
                </button>
              </div>
            ) : null}
            {speechError ? <div className="tiny chat-error">{speechError}</div> : null}
          </>
        ) : (
          <>
            <p className="user-message-text">{message.content}</p>
            <div className="message-actions">
              <button className="message-action" onClick={onSave}>
                <NotePencil size={14} /> Save note
              </button>
            </div>
          </>
        )}
      </div>
    </article>
  );
}

function ChatWelcome({ onSuggestion }: { onSuggestion: (value: string) => void }) {
  const suggestions = [
    "Help me plan a focused week with time for work, family, church, and rest.",
    "Explain John 3 in its literary and historical context.",
    "Compare Reformed Baptist and Presbyterian views of baptism.",
    "Rewrite this announcement so it is warm, clear, and concise.",
  ];
  return (
    <div className="chat-welcome">
      <span className="chat-welcome-icon"><Sparkle size={28} /></span>
      <h2>What would you like to explore?</h2>
      <p>Sola can help with ordinary life and deeper study without pretending AI is spiritual authority.</p>
      <div className="suggestion-grid">
        {suggestions.map((suggestion) => (
          <button onClick={() => onSuggestion(suggestion)} key={suggestion}>
            {suggestion}<CaretRight size={14} />
          </button>
        ))}
      </div>
      <div className="chat-welcome-dashboard">
        <Link className="welcome-dashboard-card" to="/scripture/Psalms/119?verse=105">
          <span className="eyebrow"><BookOpenText size={13} /> Scripture for today</span>
          <blockquote>“Thy word is a lamp unto my feet, and a light unto my path.”</blockquote>
          <small>Psalm 119:105 KJV · Read in context</small>
        </Link>
        <Link className="welcome-dashboard-card" to="/scripture/John/3">
          <span className="eyebrow"><BookmarkSimple size={13} /> Continue reading</span>
          <strong>Gospel of John</strong>
          <span className="progress"><span style={{ width: "14%" }} /></span>
          <small>Day 3 of 21</small>
        </Link>
        <Link className="welcome-dashboard-card" to="/study">
          <span className="eyebrow"><NotePencil size={13} /> Study Hub</span>
          <strong>Notes, documents, bookmarks, and reading</strong>
          <small>Open your private study workspace</small>
        </Link>
      </div>
      <p className="chat-welcome-authority">
        Scripture carries the authority. Sola is a fallible AI tool; verify its commentary and citations in context.
      </p>
    </div>
  );
}

function _LegacyScripture({
  user,
  onAuth,
}: {
  user: MeResponse["user"] | null;
  onAuth: () => void;
}) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const bookName = decodeURIComponent(params.book ?? "John");
  const bookIndex = Math.max(0, bibleBooks.findIndex((book) => book === bookName));
  const chapterNumber = Math.max(1, Number(params.chapter) || 3);
  const [bible, setBible] = useState<BibleData | null>(null);
  const [loadError, setLoadError] = useState("");
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [notebooks, setNotebooks] = useState<StudyNotebook[]>([]);
  const [selectedVerse, setSelectedVerse] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [tags, setTags] = useState("");
  const [notebookId, setNotebookId] = useState("");
  const [search, setSearch] = useState("");
  const [mobileBooksOpen, setMobileBooksOpen] = useState(false);
  const [mobileStudyOpen, setMobileStudyOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [readingChapter, setReadingChapter] = useState(false);
  const readerUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const verseParam = new URLSearchParams(location.search).get("verse");
  const chapter = bible?.[bookIndex]?.[chapterNumber - 1] ?? [];

  useEffect(() => {
    setLoadError("");
    loadKjv().then(setBible).catch((error) => setLoadError(String(error)));
  }, []);

  const loadAnnotations = async () => {
    if (!user) {
      setAnnotations([]);
      return;
    }
    const response = await api<{ annotations: Annotation[] }>(
      `/api/annotations?book=${encodeURIComponent(bibleBooks[bookIndex]!)}&chapter=${chapterNumber}&translation=kjv`,
    );
    setAnnotations(response.annotations);
  };

  const loadNotebooks = async () => {
    if (!user) return setNotebooks([]);
    const response = await api<{ notebooks: StudyNotebook[] }>("/api/study/notebooks");
    setNotebooks(response.notebooks);
  };

  useEffect(() => {
    void loadAnnotations();
    void loadNotebooks();
  }, [user?.id, bookIndex, chapterNumber]);

  useEffect(() => {
    if (!user) return;
    void api("/api/reading-history", {
      method: "POST",
      body: JSON.stringify({
        translation: "kjv",
        book: bibleBooks[bookIndex],
        chapter: chapterNumber,
        verse: verseParam ? Number(verseParam) : null,
      }),
    }).catch(() => undefined);
  }, [user?.id, bookIndex, chapterNumber, verseParam]);

  useEffect(() => {
    if (!verseParam) return;
    setSelectedVerse(Number(verseParam));
    setTimeout(() => {
      const verse = document.getElementById(`verse-${verseParam}`);
      const scroller = verse?.closest<HTMLElement>(".bible-scroll");
      if (verse && scroller) {
        scroller.scrollTop = verse.offsetTop - scroller.clientHeight / 2;
      }
    }, 100);
  }, [verseParam, chapter.length]);

  useEffect(() => {
    setMobileBooksOpen(false);
    setMobileStudyOpen(false);
    setMobileSearchOpen(false);
    if (readerUtteranceRef.current) {
      window.speechSynthesis?.cancel();
      readerUtteranceRef.current = null;
      setReadingChapter(false);
    }
  }, [bookIndex, chapterNumber]);

  useEffect(
    () => () => {
      if (readerUtteranceRef.current) window.speechSynthesis?.cancel();
    },
    [],
  );

  const selectedAnnotation = annotations.find(
    (annotation) => annotation.verseStart === selectedVerse,
  );

  useEffect(() => {
    setNote(selectedAnnotation?.note ?? "");
    setTags(() => {
      try {
        const parsed = JSON.parse(selectedAnnotation?.tagsJson ?? "[]");
        return Array.isArray(parsed) ? parsed.join(", ") : "";
      } catch {
        return "";
      }
    });
    setNotebookId(selectedAnnotation?.notebookId ?? "");
  }, [selectedAnnotation?.id, selectedVerse]);

  const saveAnnotation = async (
    color = selectedAnnotation?.highlightColor ?? null,
    noteText = note || null,
    bookmarked = Boolean(selectedAnnotation?.isBookmarked),
    nextTags = tags,
    nextNotebookId = notebookId,
  ) => {
    if (!selectedVerse) return;
    if (!user) {
      onAuth();
      return;
    }
    await api("/api/annotations", {
      method: "PUT",
      body: JSON.stringify({
        translation: "kjv",
        book: bibleBooks[bookIndex],
        chapter: chapterNumber,
        verseStart: selectedVerse,
        verseEnd: selectedVerse,
        highlightColor: color,
        note: noteText,
        tags: nextTags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
        notebookId: nextNotebookId || null,
        isBookmarked: bookmarked,
      }),
    });
    await loadAnnotations();
  };

  const clearAnnotation = async () => {
    await saveAnnotation(null, null, false, "", "");
    setNote("");
    setTags("");
    setNotebookId("");
  };

  const askAboutVerse = () => {
    if (!selectedVerse) return;
    const verseText = chapter[selectedVerse - 1];
    navigate(brand.routes.askAssistant, {
      state: {
        mode: "scripture",
        autoSend: true,
        prompt: `Explain ${bibleBooks[bookIndex]} ${chapterNumber}:${selectedVerse} in its literary and historical context. Keep the exact KJV text distinct from commentary:\n\n“${verseText}”`,
      },
    });
  };

  const goToReference = (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseReference(search);
    if (!parsed) {
      setLoadError("Try a reference such as John 3:16.");
      return;
    }
    navigate(referencePath(search));
    setSearch("");
  };

  const maxChapter = bible?.[bookIndex]?.length ?? 1;

  const toggleChapterAudio = () => {
    if (!("speechSynthesis" in window) || !chapter.length) {
      setLoadError("Audio reading is not available in this browser.");
      return;
    }
    if (readingChapter) {
      window.speechSynthesis.cancel();
      readerUtteranceRef.current = null;
      setReadingChapter(false);
      return;
    }
    const utterance = new SpeechSynthesisUtterance(
      `${bibleBooks[bookIndex]} chapter ${chapterNumber}. ${chapter
        .map((text, index) => `Verse ${index + 1}. ${text}`)
        .join(" ")}`,
    );
    utterance.rate = 0.92;
    utterance.onend = () => {
      readerUtteranceRef.current = null;
      setReadingChapter(false);
    };
    utterance.onerror = () => {
      readerUtteranceRef.current = null;
      setReadingChapter(false);
    };
    readerUtteranceRef.current = utterance;
    setReadingChapter(true);
    window.speechSynthesis.speak(utterance);
  };

  return (
    <div className="view scripture-view">
      <header className="page-header">
        <div>
          <h1 className="page-title">Scripture</h1>
          <p className="page-subtitle">Read the public-domain KJV offline, highlight, take notes, and study with Sola.</p>
        </div>
        <div className="page-actions">
          <span className="pill green"><CheckCircle size={14} /> Complete KJV built in</span>
          <button className="btn" onClick={() => navigate("/settings")}>
            <DownloadSimple size={16} /> Export notes
          </button>
        </div>
      </header>
      <div className="bible-layout">
        <aside className={`card bible-nav ${mobileBooksOpen ? "mobile-books-open" : ""}`}>
          <div className="bible-nav-head">
            <strong className="bible-nav-title"><BookOpenText size={16} /> 66 books</strong>
            <button
              type="button"
              className="bible-books-toggle"
              aria-expanded={mobileBooksOpen}
              onClick={() => setMobileBooksOpen((open) => !open)}
            >
              <span><BookOpenText size={16} /> {bibleBooks[bookIndex]}</span>
              <span className="muted">
                {mobileBooksOpen ? <>Close <X size={14} /></> : <>Books <CaretDown size={14} /></>}
              </span>
            </button>
          </div>
          <div className="book-list">
            <div className="testament-label">Old Testament</div>
            {bibleBooks.slice(0, 39).map((book, index) => (
              <BibleBookButton key={book} book={book} index={index} active={index === bookIndex} bible={bible} />
            ))}
            <div className="testament-label">New Testament</div>
            {bibleBooks.slice(39).map((book, localIndex) => {
              const index = localIndex + 39;
              return <BibleBookButton key={book} book={book} index={index} active={index === bookIndex} bible={bible} />;
            })}
          </div>
          <div className="chapter-grid">
            {Array.from({ length: maxChapter }, (_, index) => index + 1).map((chapter) => (
              <Link
                className={`chapter-btn ${chapter === chapterNumber ? "active" : ""}`}
                to={`/scripture/${encodeURIComponent(bibleBooks[bookIndex]!)}/${chapter}`}
                key={chapter}
              >
                {chapter}
              </Link>
            ))}
          </div>
        </aside>
        <article className="card bible-reader">
          <div className="reader-mobile-controls">
            <button type="button" className="reader-icon-button" onClick={() => navigate(-1)} aria-label="Leave reader">
              <ArrowLeft size={21} />
            </button>
            <div className="row">
              <button
                type="button"
                className={`reader-icon-button ${readingChapter ? "active" : ""}`}
                onClick={toggleChapterAudio}
                aria-label={readingChapter ? "Stop reading chapter" : "Listen to chapter"}
              >
                {readingChapter ? <Pause size={21} weight="fill" /> : <SpeakerHigh size={21} />}
              </button>
              <button
                type="button"
                className={`reader-icon-button ${mobileSearchOpen ? "active" : ""}`}
                onClick={() => setMobileSearchOpen((open) => !open)}
                aria-label={mobileSearchOpen ? "Close Bible search" : "Search the Bible"}
                aria-expanded={mobileSearchOpen}
              >
                <MagnifyingGlass size={21} />
              </button>
              <button
                type="button"
                className="reader-icon-button"
                disabled={!selectedVerse}
                onClick={() => setMobileStudyOpen(true)}
                aria-label={selectedVerse ? `Study verse ${selectedVerse} with Sola` : "Select a verse to study"}
              >
                <Sparkle size={20} />
              </button>
              <span className="reader-translation-pill">KJV</span>
            </div>
          </div>
          {mobileSearchOpen ? (
            <form className="reader-mobile-search" onSubmit={goToReference}>
              <input
                className="input"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Go to John 3:16"
                aria-label="Go to Bible reference"
                autoFocus
              />
              <button className="reader-icon-button" aria-label="Open reference">
                <ArrowRight size={19} />
              </button>
            </form>
          ) : null}
          <div className="bible-toolbar">
            <select className="select" aria-label="Bible translation" value="kjv" disabled>
              <option value="kjv">KJV · Public domain</option>
            </select>
            <form className="bible-search" onSubmit={goToReference}>
              <input
                className="input"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Go to John 3:16"
                aria-label="Go to Bible reference"
              />
              <button className="btn icon-btn small-btn" aria-label="Open reference">
                <MagnifyingGlass size={16} />
              </button>
            </form>
            <div className="row">
              <button
                type="button"
                className="btn mobile-study-button"
                disabled={!selectedVerse}
                onClick={() => setMobileStudyOpen(true)}
              >
                <Sparkle size={15} />
                {selectedVerse ? `Study v.${selectedVerse}` : "Select a verse"}
              </button>
              <Link
                className={`top-icon ${chapterNumber <= 1 ? "disabled" : ""}`}
                to={`/scripture/${encodeURIComponent(bibleBooks[bookIndex]!)}/${Math.max(1, chapterNumber - 1)}`}
                aria-label="Previous chapter"
              >
                <ArrowLeft size={17} />
              </Link>
              <Link
                className={`top-icon ${chapterNumber >= maxChapter ? "disabled" : ""}`}
                to={`/scripture/${encodeURIComponent(bibleBooks[bookIndex]!)}/${Math.min(maxChapter, chapterNumber + 1)}`}
                aria-label="Next chapter"
              >
                <ArrowRight size={17} />
              </Link>
            </div>
          </div>
          <div className="bible-scroll">
            <div className="chapter-heading">
              <h2 className="book-name">
                <span>{bibleBooks[bookIndex]}</span>
                <strong>{chapterNumber}</strong>
              </h2>
              <div className="translation">King James Version · Public domain</div>
            </div>
            {loadError ? (
              <div className="empty-state"><WarningCircle size={26} /><h3>Chapter unavailable</h3><p>{loadError}</p></div>
            ) : !bible ? (
              <div className="bible-loading"><div className="thinking"><span /><span /><span /></div><p>Opening the offline KJV…</p></div>
            ) : (
              <div className="bible-text">
                {chapter.map((text, index) => {
                  const verseNumber = index + 1;
                  const annotation = annotations.find((item) => item.verseStart === verseNumber);
                  return (
                    <button
                      id={`verse-${verseNumber}`}
                      className={[
                        "verse",
                        selectedVerse === verseNumber ? "selected" : "",
                        annotation?.highlightColor ? `hl-${annotation.highlightColor}` : "",
                        annotation?.note ? "has-note" : "",
                      ].join(" ")}
                      onClick={() => setSelectedVerse(verseNumber)}
                      key={verseNumber}
                      aria-label={`${bibleBooks[bookIndex]} ${chapterNumber}:${verseNumber}. ${text}`}
                    >
                      <sup className="verse-num">{verseNumber}</sup>{text}{" "}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <footer className="reader-mobile-footer">
            <button
              type="button"
              className={`reader-play-button ${readingChapter ? "active" : ""}`}
              onClick={toggleChapterAudio}
              aria-label={readingChapter ? "Stop reading chapter" : "Listen to chapter"}
            >
              {readingChapter ? <Pause size={22} weight="fill" /> : <Play size={22} weight="fill" />}
            </button>
            <div className="reader-chapter-switcher">
              <Link
                className={chapterNumber <= 1 ? "disabled" : ""}
                to={`/scripture/${encodeURIComponent(bibleBooks[bookIndex]!)}/${Math.max(1, chapterNumber - 1)}`}
                aria-label="Previous chapter"
              >
                <ArrowLeft size={21} />
              </Link>
              <button
                type="button"
                onClick={() => setMobileBooksOpen(true)}
                aria-label={`Choose book or chapter. Current chapter ${bibleBooks[bookIndex]} ${chapterNumber}`}
              >
                {bibleBooks[bookIndex]} {chapterNumber}
              </button>
              <Link
                className={chapterNumber >= maxChapter ? "disabled" : ""}
                to={`/scripture/${encodeURIComponent(bibleBooks[bookIndex]!)}/${Math.min(maxChapter, chapterNumber + 1)}`}
                aria-label="Next chapter"
              >
                <ArrowRight size={21} />
              </Link>
            </div>
          </footer>
        </article>
        <button
          type="button"
          className={`bible-ai-backdrop ${mobileStudyOpen ? "open" : ""}`}
          aria-label="Close verse study tools"
          onClick={() => setMobileStudyOpen(false)}
        />
        <aside className={`card bible-ai ${mobileStudyOpen ? "mobile-study-open" : ""}`}>
          <div className="bible-ai-head">
            <div className="row spread">
              <div className="ai-label"><Sparkle size={13} /> Study with Sola</div>
              <button
                type="button"
                className="top-icon bible-ai-close"
                aria-label="Close verse study tools"
                onClick={() => setMobileStudyOpen(false)}
              >
                <X size={17} />
              </button>
            </div>
            <p className="tiny muted">Scripture remains visually and functionally primary.</p>
          </div>
          <div className="bible-ai-content">
            {selectedVerse ? (
              <div className="stack">
                <div className="selected-scripture">
                  <div className="scripture-label">Direct Scripture · KJV</div>
                  <blockquote>
                    <sup>{selectedVerse}</sup> {chapter[selectedVerse - 1]}
                  </blockquote>
                  <strong>{bibleBooks[bookIndex]} {chapterNumber}:{selectedVerse}</strong>
                </div>
                <div className="annotation-tools" aria-label="Highlight color">
                  {(["yellow", "green", "blue", "pink", "purple"] as const).map((color) => (
                    <button
                      className={`color-choice ${selectedAnnotation?.highlightColor === color ? "active" : ""}`}
                      onClick={() => void saveAnnotation(color)}
                      aria-label={`${color} highlight`}
                      key={color}
                    >
                      <span className={`color-dot ${color}`} />
                    </button>
                  ))}
                  <button
                    className="top-icon"
                    onClick={() => void clearAnnotation()}
                    aria-label="Clear annotation"
                  >
                    <Trash size={15} />
                  </button>
                  <button
                    className={`top-icon ${selectedAnnotation?.isBookmarked ? "active" : ""}`}
                    onClick={() =>
                      void saveAnnotation(
                        selectedAnnotation?.highlightColor ?? null,
                        note || null,
                        !selectedAnnotation?.isBookmarked,
                      )
                    }
                    aria-label="Bookmark verse"
                  >
                    <BookmarkSimple size={16} weight={selectedAnnotation?.isBookmarked ? "fill" : "regular"} />
                  </button>
                </div>
                <div className="field">
                  <label htmlFor="verse-note">Your private note</label>
                  <textarea
                    id="verse-note"
                    className="textarea compact"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Observation, question, cross-reference, application…"
                  />
                </div>
                <div className="field">
                  <label htmlFor="verse-tags">Tags</label>
                  <input
                    id="verse-tags"
                    className="input"
                    value={tags}
                    onChange={(event) => setTags(event.target.value)}
                    placeholder="promise, gospel, memorize"
                  />
                </div>
                <div className="field">
                  <label htmlFor="verse-notebook">Notebook</label>
                  <select
                    id="verse-notebook"
                    className="select"
                    value={notebookId}
                    onChange={(event) => setNotebookId(event.target.value)}
                  >
                    <option value="">No notebook</option>
                    {notebooks.map((notebook) => (
                      <option value={notebook.id} key={notebook.id}>
                        {notebook.title}
                      </option>
                    ))}
                  </select>
                  <Link className="tiny-link" to="/study">Manage notebooks</Link>
                </div>
                <button className="btn" onClick={() => void saveAnnotation()}>
                  <NotePencil size={16} /> Save note
                </button>
                <button className="btn primary" onClick={askAboutVerse}>
                  <Sparkle size={16} /> Ask Sola to explain
                </button>
              </div>
            ) : (
              <>
                <div className="ai-note">
                  <strong>Select a verse.</strong><br />
                  Then highlight, bookmark, write a private note, or ask Sola about its context.
                </div>
                <div className="divider" />
                <div className="action-list">
                  <button
                    className="action-row"
                    onClick={() =>
                      navigate(brand.routes.askAssistant, {
                        state: {
                          autoSend: true,
                          mode: "scripture",
                          prompt: `Summarize ${bibleBooks[bookIndex]} ${chapterNumber}. Distinguish the biblical text, literary context, interpretation, and application.`,
                        },
                      })
                    }
                  >
                    <span className="action-icon"><Sparkle size={16} /></span>Summarize this chapter
                  </button>
                  <button className="action-row" onClick={() => setSelectedVerse(1)}>
                    <span className="action-icon"><NotePencil size={16} /></span>Start a chapter note
                  </button>
                  <button
                    className="action-row"
                    onClick={() =>
                      navigate(brand.routes.askAssistant, {
                        state: {
                          autoSend: true,
                          mode: "berean",
                          prompt: `Show the main responsible Christian interpretive disagreements in ${bibleBooks[bookIndex]} ${chapterNumber}.`,
                        },
                      })
                    }
                  >
                    <span className="action-icon"><Scales size={16} /></span>Compare interpretations
                  </button>
                </div>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function StudyDashboard({
  user,
  onAuth,
}: {
  user: MeResponse["user"] | null;
  onAuth: () => void;
}) {
  type StudyAnnotation = {
    id: string;
    book: string;
    chapter: number;
    verseStart: number;
    verseEnd: number;
    note: string | null;
    highlightColor: string | null;
    tagsJson: string;
    notebookId: string | null;
    updatedAt: number;
  };
  type RecentReading = {
    book: string;
    chapter: number;
    verse: number | null;
    readAt: number;
  };
  const [overview, setOverview] = useState<{
    recent: RecentReading[];
    bookmarks: StudyAnnotation[];
    notes: StudyAnnotation[];
    notebooks: StudyNotebook[];
  } | null>(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [color, setColor] = useState("purple");
  const [error, setError] = useState("");

  const load = async () => {
    if (!user) return setOverview(null);
    const response = await api<NonNullable<typeof overview>>("/api/study/overview");
    setOverview(response);
  };

  useEffect(() => {
    void load();
  }, [user?.id]);

  const createNotebook = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      await api("/api/study/notebooks", {
        method: "POST",
        body: JSON.stringify({ title, color }),
      });
      setTitle("");
      setCreating(false);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The notebook could not be created.");
    }
  };

  const annotationLink = (item: StudyAnnotation) =>
    `/scripture/${encodeURIComponent(item.book)}/${item.chapter}?verse=${item.verseStart}`;

  return (
    <div className="view">
      <header className="page-header">
        <div>
          <h1 className="page-title">Study Notes</h1>
          <p className="page-subtitle">Your bookmarks, notes, tags, notebooks, and recent reading—organized around Scripture.</p>
        </div>
        <div className="page-actions">
          <button className="btn primary" onClick={() => (user ? setCreating(true) : onAuth())}>
            <Plus size={16} /> New notebook
          </button>
        </div>
      </header>
      {!user ? (
        <SignInCard
          title="Keep your Scripture study together"
          copy="Sign in to sync bookmarks, private notes, tags, notebooks, and reading history."
          onAuth={onAuth}
        />
      ) : !overview ? (
        <div className="card empty-state"><div className="thinking"><span /><span /><span /></div><p>Opening your study desk…</p></div>
      ) : (
        <div className="feature-grid">
          <section className="card panel">
            <div className="space-between"><h2 className="panel-title">Notebooks</h2><span className="pill">{overview.notebooks.length}</span></div>
            {overview.notebooks.length ? (
              <div className="thread-list">
                {overview.notebooks.map((notebook) => (
                  <div className="thread-item" key={notebook.id}>
                    <div className="title">{notebook.title}</div>
                    <div className="preview">{notebook.annotationCount} saved passages · {notebook.color}</div>
                  </div>
                ))}
              </div>
            ) : <p className="panel-sub">Create a notebook, then assign a verse note from the Scripture reader.</p>}
          </section>
          <section className="card panel">
            <div className="space-between"><h2 className="panel-title">Bookmarks</h2><span className="pill">{overview.bookmarks.length}</span></div>
            {overview.bookmarks.length ? overview.bookmarks.slice(0, 12).map((item) => (
              <Link className="action-row" to={annotationLink(item)} key={item.id}>
                <span className="action-icon"><BookmarkSimple size={16} /></span>
                <span>{item.book} {item.chapter}:{item.verseStart}{item.verseEnd !== item.verseStart ? `–${item.verseEnd}` : ""}</span>
              </Link>
            )) : <p className="panel-sub">Bookmark a verse in the Scripture reader to see it here.</p>}
          </section>
          <section className="card panel">
            <div className="space-between"><h2 className="panel-title">Private notes</h2><span className="pill">{overview.notes.length}</span></div>
            {overview.notes.length ? overview.notes.slice(0, 12).map((item) => {
              let parsedTags: string[] = [];
              try {
                const candidate = JSON.parse(item.tagsJson || "[]");
                if (Array.isArray(candidate)) parsedTags = candidate;
              } catch {
                parsedTags = [];
              }
              return (
                <Link className="study-note-row" to={annotationLink(item)} key={item.id}>
                  <strong>{item.book} {item.chapter}:{item.verseStart}</strong>
                  <span>{item.note}</span>
                  {parsedTags.length ? <small>{parsedTags.map((tag) => `#${tag}`).join(" ")}</small> : null}
                </Link>
              );
            }) : <p className="panel-sub">Select a verse, write an observation, and save it privately.</p>}
          </section>
          <section className="card panel">
            <div className="space-between"><h2 className="panel-title">Recent reading</h2><span className="pill">{overview.recent.length}</span></div>
            {overview.recent.length ? overview.recent.map((item, index) => (
              <Link
                className="action-row"
                to={`/scripture/${encodeURIComponent(item.book)}/${item.chapter}${item.verse ? `?verse=${item.verse}` : ""}`}
                key={`${item.book}-${item.chapter}-${item.verse ?? 0}-${index}`}
              >
                <span className="action-icon"><BookOpenText size={16} /></span>
                <span>{item.book} {item.chapter}{item.verse ? `:${item.verse}` : ""}</span>
              </Link>
            )) : <p className="panel-sub">Your latest chapters will appear here as you read.</p>}
          </section>
        </div>
      )}
      {creating ? (
        <Modal title="Create a study notebook" onClose={() => setCreating(false)}>
          <form className="stack" onSubmit={createNotebook}>
            <div className="field"><label htmlFor="notebook-title">Notebook title</label><input id="notebook-title" className="input" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={100} required /></div>
            <div className="field"><label htmlFor="notebook-color">Color</label><select id="notebook-color" className="select" value={color} onChange={(event) => setColor(event.target.value)}><option value="purple">Purple</option><option value="blue">Blue</option><option value="green">Green</option><option value="yellow">Yellow</option><option value="pink">Pink</option></select></div>
            {error ? <div className="form-error"><WarningCircle size={16} /> {error}</div> : null}
            <div className="modal-actions"><button type="button" className="btn" onClick={() => setCreating(false)}>Cancel</button><button className="btn primary">Create notebook</button></div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}

function StudyHub({
  user,
  onAuth,
}: {
  user: MeResponse["user"] | null;
  onAuth: () => void;
}) {
  type StudyAnnotation = ScriptureBookmark & {
    note: string | null;
    highlightColor: string | null;
    tagsJson: string;
    notebookId: string | null;
  };
  type RecentReading = {
    book: string;
    chapter: number;
    verse: number | null;
    readAt: number;
  };
  type Overview = {
    recent: RecentReading[];
    bookmarks: StudyAnnotation[];
    notes: StudyAnnotation[];
    notebooks: StudyNotebook[];
  };
  type NoteDetailResponse = {
    note: Omit<StudyNote, "tags">;
    tags: Array<{ tag: string; origin: "manual" | "automatic" }>;
    revisions: Array<{
      id: string;
      version: number;
      title: string;
      summary: string;
      createdAt: number;
    }>;
  };
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedTab = params.get("tab");
  const activeTab = ["notes", "notebooks", "documents", "bookmarks", "reading"].includes(
    requestedTab ?? "",
  )
    ? requestedTab!
    : "notes";
  const requestedNoteId = params.get("note");
  const [overview, setOverview] = useState<Overview | null>(null);
  const [studyNotes, setStudyNotes] = useState<StudyNote[]>([]);
  const [noteResearchIds,setNoteResearchIds]=useState<string[]>([]);
  const [noteResearchIndex,setNoteResearchIndex]=useState(0);
  const [noteResearchRuns,setNoteResearchRuns]=useState<ResearchRun[]>([]);
  useEffect(()=>{
    const controller=new AbortController();setNoteResearchIndex(Math.max(0,noteResearchIds.length-1));setNoteResearchRuns([]);
    if(noteResearchIds.length)void Promise.all(noteResearchIds.map(id=>api<ResearchRun>(`/api/research/runs/${id}`,{signal:controller.signal}))).then(setNoteResearchRuns).catch(()=>undefined);
    return()=>controller.abort();
  },[noteResearchIds.join(",")]);
  const [selectedNote, setSelectedNote] = useState<StudyNote | null>(null);
  const [revisions, setRevisions] = useState<NoteDetailResponse["revisions"]>([]);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [noteTags, setNoteTags] = useState("");
  const [noteNotebookId, setNoteNotebookId] = useState("");
  const [noteSearch, setNoteSearch] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [newNote, setNewNote] = useState(false);
  const [notebookOpen, setNotebookOpen] = useState(false);
  const [notebookTitle, setNotebookTitle] = useState("");
  const [notebookColor, setNotebookColor] = useState("purple");
  const [error, setError] = useState("");

  const load = async () => {
    if (!user) {
      setOverview(null);
      setStudyNotes([]);
      return;
    }
    const [overviewResponse, notesResponse] = await Promise.all([
      api<Overview>("/api/study/overview"),
      api<{ notes: StudyNote[] }>("/api/study/notes"),
    ]);
    setOverview(overviewResponse);
    setStudyNotes(notesResponse.notes);
  };

  useEffect(() => {
    void load().catch((caught) =>
      setError(caught instanceof Error ? caught.message : "The Study Hub could not be opened."),
    );
  }, [user?.id]);

  const openNote = async (id: string, updateUrl = true) => {
    setError("");
    try {
      const response = await api<NoteDetailResponse>(
        `/api/study/notes/${encodeURIComponent(id)}`,
      );
      const note: StudyNote = {
        ...response.note,
        tags: response.tags.map((tag) => tag.tag),
      };
      setSelectedNote(note);
      setNoteResearchIds((JSON.parse(note.sourceContextJson||'{}') as {researchRunIds?:string[]}).researchRunIds??[]);
      setRevisions(response.revisions);
      setNoteTitle(note.title);
      setNoteBody(note.body);
      setNoteTags(note.tags.join(", "));
      setNoteNotebookId(note.notebookId ?? "");
      setNewNote(false);
      if (updateUrl) {
        navigate(`/study?tab=notes&note=${encodeURIComponent(id)}`, { replace: true });
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The study note could not be opened.");
    }
  };

  useEffect(() => {
    if (requestedNoteId && user && requestedNoteId !== selectedNote?.id) {
      void openNote(requestedNoteId, false);
    }
  }, [requestedNoteId, user?.id]);

  const beginNote = () => {
    if (!user) return onAuth();
    setSelectedNote(null);
    setNoteResearchIds([]);
    setRevisions([]);
    setNoteTitle("");
    setNoteBody("");
    setNoteTags("");
    setNoteNotebookId("");
    setNewNote(true);
    navigate("/study?tab=notes");
  };

  const saveNote = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) return onAuth();
    setSavingNote(true);
    setError("");
    const tags = noteTags
      .split(/[,\s]+/)
      .map((tag) => tag.replace(/^#/, "").trim())
      .filter(Boolean);
    try {
      let id = selectedNote?.id;
      if (id) {
        await api(`/api/study/notes/${id}`, {
          method: "PATCH",
          body: JSON.stringify({
            title: noteTitle,
            body: noteBody,
            tags,
            notebookId: noteNotebookId || null,
          }),
        });
      } else {
        const created = await api<{ id: string }>("/api/study/notes", {
          method: "POST",
          body: JSON.stringify({
            source: {
              sourceType: "manual",
              title: noteTitle || undefined,
              body: noteBody,
            },
            tags,
            notebookId: noteNotebookId || undefined,
          }),
        });
        id = created.id;
      }
      await load();
      await openNote(id!);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The study note could not be saved.");
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async () => {
    if (!selectedNote) return;
    try {
      await api(`/api/study/notes/${selectedNote.id}`, { method: "DELETE" });
      setSelectedNote(null);
      setNewNote(false);
      navigate("/study?tab=notes", { replace: true });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The study note could not be deleted.");
    }
  };

  const createNotebook = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await api("/api/study/notebooks", {
        method: "POST",
        body: JSON.stringify({ title: notebookTitle, color: notebookColor }),
      });
      setNotebookTitle("");
      setNotebookOpen(false);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The notebook could not be created.");
    }
  };

  const visibleNotes = studyNotes.filter((note) => {
    const query = noteSearch.trim().toLocaleLowerCase().replace(/^#/, "");
    return (
      !query ||
      `${note.title} ${note.summary} ${note.tags.join(" ")}`
        .toLocaleLowerCase()
        .includes(query)
    );
  });
  const suggestedQuestions = (() => {
    if (!selectedNote) return [];
    try {
      const parsed = JSON.parse(selectedNote.suggestedQuestionsJson);
      return Array.isArray(parsed)
        ? parsed.filter((value): value is string => typeof value === "string").slice(0, 3)
        : [];
    } catch {
      return [];
    }
  })();
  const annotationLink = (item: StudyAnnotation) =>
    scriptureBookmarkPath(item);

  const removeBookmark = async (bookmark: StudyAnnotation) => {
    try {
      await api(`/api/bookmarks/${encodeURIComponent(bookmark.id)}`, { method: "DELETE" });
      setOverview((current) =>
        current
          ? {
              ...current,
              bookmarks: current.bookmarks.filter((item) => item.id !== bookmark.id),
            }
          : current,
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The bookmark could not be removed.");
    }
  };

  return (
    <div className="view study-hub">
      <header className="page-header">
        <div>
          <h1 className="page-title">Study Hub</h1>
          <p className="page-subtitle">Private notes, approved documents, bookmarks, and reading—organized for continued study.</p>
        </div>
        <div className="page-actions">
          <Link className="btn" to="/sermons">
            <ChalkboardTeacher size={16} /> Sermons
          </Link>
          <button className="btn primary" onClick={beginNote}><Plus size={16} /> New note</button>
        </div>
      </header>
      {!user ? (
        <SignInCard
          title="Keep your Scripture study together"
          copy="Sign in to sync private notes, documents, bookmarks, notebooks, and reading history."
          onAuth={onAuth}
        />
      ) : !overview ? (
        <div className="card empty-state"><div className="thinking"><span /><span /><span /></div><p>Opening your study desk…</p></div>
      ) : (
        <>
          <div className="study-hub-tabs" role="tablist" aria-label="Study Hub sections">
            {([
              ["notes", "Notes", studyNotes.length],
              ["notebooks", "Notebooks", overview.notebooks.length],
              ["documents", "Documents", null],
              ["bookmarks", "Bookmarks", overview.bookmarks.length],
              ["reading", "Recent reading", overview.recent.length],
            ] as const).map(([key, label, count]) => (
              <button
                className={activeTab === key ? "active" : ""}
                type="button"
                role="tab"
                aria-selected={activeTab === key}
                onClick={() => navigate(`/study?tab=${key}`)}
                key={key}
              >
                {label}{typeof count === "number" ? <span>{count}</span> : null}
              </button>
            ))}
          </div>
          {error ? <div className="chat-error"><WarningCircle size={17} /> {error}</div> : null}
          {activeTab === "notes" ? (
            <div className="study-hub-layout">
              <section className="card panel study-note-browser">
                <div className="study-note-search">
                  <MagnifyingGlass size={17} />
                  <input value={noteSearch} onChange={(event) => setNoteSearch(event.target.value)} placeholder="Search notes or #tags" aria-label="Search study notes" />
                </div>
                <div className="study-note-list">
                  {visibleNotes.length ? visibleNotes.map((note) => (
                    <button className={`study-note-list-item ${selectedNote?.id === note.id ? "active" : ""}`} type="button" onClick={() => void openNote(note.id)} key={note.id}>
                      <strong>{note.title}</strong>
                      <span>{note.summary || "Open this note to continue studying."}</span>
                      {note.tags.length ? <small>{note.tags.map((tag) => `#${tag}`).join(" ")}</small> : null}
                    </button>
                  )) : (
                    <div className="empty-state compact">
                      <NotePencil size={25} />
                      <h3>{noteSearch ? "No notes matched" : "Your study notes begin here"}</h3>
                      <p>Save any message, conversation, Deep Study, Scripture annotation, or your own writing.</p>
                    </div>
                  )}
                </div>
              </section>
              <section className="card panel study-note-editor">
                {selectedNote || newNote ? (
                  <><label hidden={noteResearchIds.length<2}>Saved answer
                    <select value={noteResearchIndex} onChange={event=>setNoteResearchIndex(Number(event.target.value))}>
                      {noteResearchIds.map((id,index)=><option value={index} key={id}>Answer {index+1}</option>)}
                    </select>
                  </label><StudyWorkspace runId={noteResearchIds[noteResearchIndex]} answerRuns={noteResearchRuns}><form className="stack" onSubmit={(event) => void saveNote(event)}>
                    <div className="space-between">
                      <span className="ai-label"><NotePencil size={13} /> {selectedNote ? "Private study note" : "New private note"}</span>
                      {selectedNote?.metadataStatus === "pending" ? <span className="pill">Organizing…</span> : null}
                    </div>
                    <div className="field">
                      <label htmlFor="study-note-title">Title</label>
                      <input id="study-note-title" className="input" value={noteTitle} onChange={(event) => setNoteTitle(event.target.value)} maxLength={120} placeholder="A clear title will be suggested automatically" />
                    </div>
                    <div className="field">
                      <label htmlFor="study-note-body">Note</label>
                      <textarea id="study-note-body" className="textarea study-note-body" value={noteBody} onChange={(event) => setNoteBody(event.target.value)} maxLength={250000} required placeholder="Write in Markdown, paste research, or save content from Ask Sola." />
                    </div>
                    <div className="study-note-fields">
                      <div className="field">
                        <label htmlFor="study-note-tags">Tags</label>
                        <input id="study-note-tags" className="input" value={noteTags} onChange={(event) => setNoteTags(event.target.value)} placeholder="gospels, grace, #sermon" />
                      </div>
                      <div className="field">
                        <label htmlFor="study-note-notebook">Notebook</label>
                        <select id="study-note-notebook" className="select" value={noteNotebookId} onChange={(event) => setNoteNotebookId(event.target.value)}>
                          <option value="">No notebook</option>
                          {overview.notebooks.map((notebook) => <option value={notebook.id} key={notebook.id}>{notebook.title}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="study-note-editor-actions">
                      {selectedNote ? (
                        <>
                          <button className="btn" type="button" onClick={() => navigate(brand.routes.askAssistant, { state: { prompt: `Help me continue studying "${selectedNote.title}".`, noteIds: [selectedNote.id] } })}><Sparkle size={15} /> Continue with Sola</button>
                          <button className="btn danger" type="button" onClick={() => void deleteNote()}><Trash size={15} /> Delete</button>
                        </>
                      ) : null}
                      <button className="btn primary" disabled={savingNote}>{savingNote ? "Saving…" : "Save note"}</button>
                    </div>
                    {suggestedQuestions.length ? (
                      <div className="suggested-note-questions">
                        <span>Suggested next questions</span>
                        {suggestedQuestions.map((question) => (
                          <button type="button" onClick={() => navigate(brand.routes.askAssistant, { state: { prompt: question, noteIds: [selectedNote!.id] } })} key={question}><Sparkle size={14} /> {question}</button>
                        ))}
                      </div>
                    ) : null}
                    {revisions.length > 1 ? <p className="tiny muted">{revisions.length} saved revisions — your edit history is preserved.</p> : null}
                  </form></StudyWorkspace></>
                ) : (
                  <div className="empty-state">
                    <NotePencil size={30} />
                    <h3>Select a note or start writing</h3>
                    <p>Manual tags are preserved. Sola can suggest a title, summary, tags, and follow-up questions after the note is safely saved.</p>
                    <button className="btn primary" onClick={beginNote}><Plus size={15} /> New note</button>
                  </div>
                )}
              </section>
            </div>
          ) : null}
          {activeTab === "notebooks" ? (
            <section className="card panel">
              <div className="space-between"><h2 className="panel-title">Notebooks</h2><button className="btn primary" onClick={() => setNotebookOpen(true)}><Plus size={15} /> New notebook</button></div>
              {overview.notebooks.length ? <div className="thread-list">{overview.notebooks.map((notebook) => (
                <div className="thread-item" key={notebook.id}><div className="title">{notebook.title}</div><div className="preview">{notebook.annotationCount} saved passages · {notebook.color}</div></div>
              ))}</div> : <p className="panel-sub">Create a notebook, then group notes and Scripture annotations inside it.</p>}
            </section>
          ) : null}
          {activeTab === "documents" ? <div className="study-documents-panel"><Library user={user} onAuth={onAuth} /></div> : null}
          {activeTab === "bookmarks" ? (
            <section className="card panel">
              <div className="space-between"><h2 className="panel-title">Bookmarks</h2><span className="pill">{overview.bookmarks.length}</span></div>
              {overview.bookmarks.length ? overview.bookmarks.map((item) => (
                <div className="study-bookmark-row" key={item.id}>
                  <Link className="action-row" to={annotationLink(item)}>
                    <span className="action-icon"><BookmarkSimple size={16} weight="fill" /></span>
                    <span>
                      <strong>{item.book} {item.chapter}:{item.verseStart}{item.verseEnd !== item.verseStart ? `–${item.verseEnd}` : ""}</strong>
                      <small>{bibleVersion(item.translation).abbreviation}</small>
                    </span>
                  </Link>
                  <button className="top-icon" type="button" onClick={() => void removeBookmark(item)} aria-label={`Remove bookmark ${item.book} ${item.chapter}:${item.verseStart}`}>
                    <Trash size={16} />
                  </button>
                </div>
              )) : <p className="panel-sub">Bookmark a verse in the Scripture reader to see it here.</p>}
            </section>
          ) : null}
          {activeTab === "reading" ? (
            <section className="card panel">
              <div className="space-between"><h2 className="panel-title">Recent reading</h2><span className="pill">{overview.recent.length}</span></div>
              {overview.recent.length ? overview.recent.map((item, index) => (
                <Link className="action-row" to={`/scripture/${encodeURIComponent(item.book)}/${item.chapter}${item.verse ? `?verse=${item.verse}` : ""}`} key={`${item.book}-${item.chapter}-${item.verse ?? 0}-${index}`}><span className="action-icon"><BookOpenText size={16} /></span><span>{item.book} {item.chapter}{item.verse ? `:${item.verse}` : ""}</span></Link>
              )) : <p className="panel-sub">Your latest chapters will appear here as you read.</p>}
            </section>
          ) : null}
        </>
      )}
      {notebookOpen ? (
        <Modal title="Create a study notebook" onClose={() => setNotebookOpen(false)}>
          <form className="stack" onSubmit={(event) => void createNotebook(event)}>
            <div className="field"><label htmlFor="hub-notebook-title">Notebook title</label><input id="hub-notebook-title" className="input" value={notebookTitle} onChange={(event) => setNotebookTitle(event.target.value)} maxLength={100} required /></div>
            <div className="field"><label htmlFor="hub-notebook-color">Color</label><select id="hub-notebook-color" className="select" value={notebookColor} onChange={(event) => setNotebookColor(event.target.value)}><option value="purple">Purple</option><option value="blue">Blue</option><option value="green">Green</option><option value="yellow">Yellow</option><option value="pink">Pink</option></select></div>
            <div className="modal-actions"><button type="button" className="btn" onClick={() => setNotebookOpen(false)}>Cancel</button><button className="btn primary">Create notebook</button></div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}

function BibleBookButton({
  book,
  index,
  active,
  bible,
}: {
  book: string;
  index: number;
  active: boolean;
  bible: BibleData | null;
}) {
  return (
    <Link className={`book-btn ${active ? "active" : ""}`} to={`/scripture/${encodeURIComponent(book)}/1`}>
      <span>{book}</span>
      <span className="chap-count">{bible?.[index]?.length ?? "—"}</span>
    </Link>
  );
}

function PromptTool({
  icon: ToolIcon,
  title,
  subtitle,
  badge,
  fieldLabel,
  placeholder,
  action,
  mode,
  promptPrefix,
  asideTitle,
  asideCopy,
  user,
  onAuth,
}: {
  icon: Icon;
  title: string;
  subtitle: string;
  badge: string;
  fieldLabel: string;
  placeholder: string;
  action: string;
  mode: string;
  promptPrefix: string;
  asideTitle: string;
  asideCopy: string;
  user: MeResponse["user"] | null;
  onAuth: () => void;
}) {
  const navigate = useNavigate();
  const [content, setContent] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!content.trim()) return;
    if (!user) {
      onAuth();
      return;
    }
    navigate(brand.routes.askAssistant, {
      state: { prompt: promptPrefix + content.trim(), mode, autoSend: true },
    });
  };
  return (
    <div className="view">
      <header className="page-header">
        <div>
          <h1 className="page-title">{title}</h1>
          <p className="page-subtitle">{subtitle}</p>
        </div>
        <div className="page-actions"><span className="pill purple"><ToolIcon size={14} /> {badge}</span></div>
      </header>
      <div className="feature-grid">
        <form className="card panel" onSubmit={submit}>
          <div className="field">
            <label htmlFor={`${mode}-prompt`}>{fieldLabel}</label>
            <textarea
              id={`${mode}-prompt`}
              className="textarea guard-input"
              value={content}
              onChange={(event) => setContent(event.target.value)}
              placeholder={placeholder}
            />
          </div>
          <div className="space-between tool-submit-row">
            <span className="tiny muted">{content.trim() ? `${content.trim().split(/\s+/).length} words` : "Nothing is sent until you continue"}</span>
            <button className="btn primary" type="submit">
              <Sparkle size={16} /> {action}
            </button>
          </div>
        </form>
        <aside className="card panel">
          <div className="ai-label"><Sparkle size={13} /> AI-generated analysis</div>
          <h2 className="panel-title aside-heading">{asideTitle}</h2>
          <p className="panel-sub">{asideCopy}</p>
          <div className="authority-stack">
            <div><BookOpenText size={17} /><span><strong>Scripture</strong><small>Direct quotations and references</small></span></div>
            <div><Books size={17} /><span><strong>Context</strong><small>History and literary setting</small></span></div>
            <div><Church size={17} /><span><strong>Interpretation</strong><small>Named theological perspectives</small></span></div>
            <div><Sparkle size={17} /><span><strong>AI suggestions</strong><small>Fallible generated commentary</small></span></div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function PrayerJournal({
  user,
  onAuth,
}: {
  user: MeResponse["user"] | null;
  onAuth: () => void;
}) {
  const [prayers, setPrayers] = useState<Prayer[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [scripture, setScripture] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!user) {
      setPrayers([]);
      return;
    }
    const response = await api<{ prayers: Prayer[] }>("/api/prayers");
    setPrayers(response.prayers);
  };
  useEffect(() => {
    void load();
  }, [user?.id]);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) {
      onAuth();
      return;
    }
    setSaving(true);
    try {
      await api("/api/prayers", {
        method: "POST",
        body: JSON.stringify({
          title,
          body,
          category: "Personal",
          scriptureReference: scripture.trim() || null,
        }),
      });
      setTitle("");
      setBody("");
      setScripture("");
      setFormOpen(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="view">
      <header className="page-header">
        <div>
          <h1 className="page-title">Prayer Journal</h1>
          <p className="page-subtitle">Private, encrypted prayer notes with optional Scripture-grounded writing help.</p>
        </div>
        <div className="page-actions">
          <span className="pill green"><LockKey size={14} /> Private by default</span>
          <button className="btn primary" onClick={() => (user ? setFormOpen(true) : onAuth())}>
            <Plus size={16} /> New prayer
          </button>
        </div>
      </header>
      <div className="prayer-layout">
        <section>
          {!user ? (
            <SignInCard title="Your prayer journal stays private" copy="Sign in to save encrypted prayer notes and sync them across your devices." onAuth={onAuth} />
          ) : prayers.length ? (
            <div className="prayer-list">
              {prayers.map((prayer) => (
                <article className={`prayer-card ${prayer.status === "answered" ? "answered" : ""}`} key={prayer.id}>
                  <span className="prayer-status"><Heart size={15} /></span>
                  <h3>{prayer.title}</h3>
                  <p>{prayer.body}</p>
                  {prayer.scriptureReference ? (
                    <Link className="citation-chip" to={referencePath(prayer.scriptureReference)}>
                      <BookOpenText size={12} /> {prayer.scriptureReference}
                    </Link>
                  ) : null}
                  <div className="prayer-meta"><span>{prayer.category}</span><span>{new Date(prayer.updatedAt * 1000).toLocaleDateString()}</span></div>
                  <div className="message-actions">
                    <button
                      className="message-action"
                      onClick={async () => {
                        await api(`/api/prayers/${prayer.id}`, {
                          method: "PATCH",
                          body: JSON.stringify({
                            status: prayer.status === "answered" ? "active" : "answered",
                          }),
                        });
                        await load();
                      }}
                    >
                      <CheckCircle size={14} /> {prayer.status === "answered" ? "Mark active" : "Mark answered"}
                    </button>
                    <button
                      className="message-action"
                      onClick={async () => {
                        await api(`/api/prayers/${prayer.id}`, { method: "DELETE" });
                        await load();
                      }}
                    >
                      <Trash size={14} /> Delete
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="card empty-state">
              <span className="empty-icon"><Heart size={28} /></span>
              <h3>Begin your private journal</h3>
              <p>Write honestly and specifically. Nothing is shared unless you deliberately share a specific item.</p>
              <button className="btn primary" onClick={() => setFormOpen(true)}><Plus size={15} /> Add a prayer</button>
            </div>
          )}
        </section>
        <aside className="card panel">
          <div className="ai-label"><Sparkle size={13} /> Prayer writing assistant</div>
          <p className="panel-sub aside-heading">A writing aid—not a mediator, spiritual authority, or substitute for prayer.</p>
          <div className="ai-note">
            <strong>Privacy boundary</strong><br />
            Journal text is encrypted before it is stored. It is never shared with a group by default.
          </div>
          <div className="divider" />
          <Link className="action-row standalone" to="/ask-sola" state={{ mode: "scripture", prompt: "Help me organize a prayer for wisdom in a difficult decision. Clearly label the result as an AI-generated suggestion.", autoSend: false }}>
            <span className="action-icon"><Sparkle size={16} /></span>Ask Sola to help write
          </Link>
        </aside>
      </div>
      {formOpen ? (
        <Modal title="New private prayer" onClose={() => setFormOpen(false)}>
          <form onSubmit={save} className="stack">
            <div className="field">
              <label htmlFor="prayer-title">Title</label>
              <input id="prayer-title" className="input" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} required />
            </div>
            <div className="field">
              <label htmlFor="prayer-body">Prayer or request</label>
              <textarea id="prayer-body" className="textarea" value={body} onChange={(event) => setBody(event.target.value)} maxLength={30000} required />
            </div>
            <div className="field">
              <label htmlFor="prayer-scripture">Scripture reference (optional)</label>
              <input id="prayer-scripture" className="input" value={scripture} onChange={(event) => setScripture(event.target.value)} placeholder="Philippians 4:6–7" />
            </div>
            <div className="modal-actions">
              <button className="btn" type="button" onClick={() => setFormOpen(false)}>Cancel</button>
              <button className="btn primary" disabled={saving}>{saving ? "Encrypting…" : "Save privately"}</button>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}

function Library({
  user,
  onAuth,
}: {
  user: MeResponse["user"] | null;
  onAuth: () => void;
}) {
  const [query, setQuery] = useState("");
  const [documents, setDocuments] = useState<LibraryDocument[]>([]);
  const [results, setResults] = useState<LibraryDocument[] | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [documentType, setDocumentType] = useState("notes");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!user) return setDocuments([]);
    const response = await api<{ documents: LibraryDocument[] }>("/api/library");
    setDocuments(response.documents);
  };

  useEffect(() => {
    void load();
  }, [user?.id]);

  const searchLibrary = async (event: FormEvent) => {
    event.preventDefault();
    if (!query.trim()) return setResults(null);
    if (!user) return onAuth();
    setError("");
    try {
      const response = await api<{ documents: LibraryDocument[] }>(
        `/api/library/search?q=${encodeURIComponent(query)}`,
      );
      setResults(response.documents);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The library search failed.");
    }
  };

  const addDocument = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) return onAuth();
    setSaving(true);
    setError("");
    try {
      await api("/api/library", {
        method: "POST",
        body: JSON.stringify({ title, content, documentType }),
      });
      setTitle("");
      setContent("");
      setDocumentType("notes");
      setFormOpen(false);
      setResults(null);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The document could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const visibleDocuments = results ?? documents;

  return (
    <div className="view">
      <header className="page-header">
        <div>
          <h1 className="page-title">Library</h1>
          <p className="page-subtitle">Approved sermons, statements of faith, ministry guides, and church resources.</p>
        </div>
        <div className="page-actions">
          <span className="pill purple"><ShieldCheck size={14} /> Source-aware answers</span>
          <button className="btn primary" onClick={() => (user ? setFormOpen(true) : onAuth())}><Plus size={16} /> Add document</button>
        </div>
      </header>
      <section className="card panel library-search-panel">
        <form
          className="library-search-form"
          onSubmit={(event) => void searchLibrary(event)}
        >
          <MagnifyingGlass size={19} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Ask across sermons, bylaws, statements, and guides…" />
          <button className="btn soft"><Sparkle size={15} /> AI search</button>
        </form>
      </section>
      {error ? <div className="chat-error"><WarningCircle size={17} /> {error}</div> : null}
      <div className="feature-grid library-layout">
        <section>
          {visibleDocuments.length ? (
            <div className="prayer-list">
              {visibleDocuments.map((document) => (
                <article className="card prayer-card" key={document.id}>
                  <span className="prayer-status"><Books size={15} /></span>
                  <h3>{document.title}</h3>
                  <p>{document.excerpt ? document.excerpt.replace(/<\/?mark>/g, "") : document.summary || "Private approved source"}</p>
                  <div className="prayer-meta">
                    <span>{document.documentType.replaceAll("_", " ")}</span>
                    <span>{document.indexingStatus ?? "ready"}</span>
                    {!results ? (
                      <button
                        className="message-action"
                        onClick={async () => {
                          await api(`/api/library/${document.id}`, { method: "DELETE" });
                          await load();
                        }}
                      >
                        <Trash size={13} /> Remove
                      </button>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="card empty-state">
              <span className="empty-icon"><Books size={28} /></span>
              <h3>{results ? "No approved sources matched" : user ? "Your approved library is ready" : "Sign in to create a library"}</h3>
              <p>{results ? "Try broader terms or add another approved source." : user ? "Add plain text from a sermon, statement of faith, ministry guide, or your own study notes. It stays private by default." : "Personal and church libraries remain separated, permissioned, and private by default."}</p>
              <button className="btn primary" onClick={() => (user ? setFormOpen(true) : onAuth())}>
                {user ? <><Plus size={15} /> Add first document</> : <><SignIn size={15} /> Sign in</>}
              </button>
            </div>
          )}
        </section>
        <aside className="card panel">
          <div className="ai-label"><Sparkle size={13} /> Library AI</div>
          <p className="panel-sub aside-heading">Answers must cite approved sources and separate them from general knowledge.</p>
          <div className="authority-stack">
            <div><ShieldCheck size={17} /><span><strong>Permissioned</strong><small>Only authorized people see private files</small></span></div>
            <div><MagnifyingGlass size={17} /><span><strong>Source cited</strong><small>Every library claim links to its source</small></span></div>
            <div><WarningCircle size={17} /><span><strong>Prompt-safe</strong><small>Document instructions cannot override Sola</small></span></div>
          </div>
        </aside>
      </div>
      {formOpen ? (
        <Modal title="Add an approved source" onClose={() => setFormOpen(false)}>
          <form className="stack" onSubmit={addDocument}>
            <div className="field"><label htmlFor="library-title">Title</label><input id="library-title" className="input" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} required /></div>
            <div className="field"><label htmlFor="library-type">Source type</label><select id="library-type" className="select" value={documentType} onChange={(event) => setDocumentType(event.target.value)}><option value="sermon">Sermon</option><option value="statement">Statement of faith</option><option value="guide">Ministry guide</option><option value="notes">Study notes</option><option value="other">Other</option></select></div>
            <div className="field"><label htmlFor="library-content">Approved text</label><textarea id="library-content" className="textarea" value={content} onChange={(event) => setContent(event.target.value)} maxLength={250000} required /></div>
            <p className="tiny muted">Only add content you are authorized to store. Source text remains private unless you deliberately share it.</p>
            <div className="modal-actions"><button type="button" className="btn" onClick={() => setFormOpen(false)}>Cancel</button><button className="btn primary" disabled={saving}>{saving ? "Indexing…" : "Save and index"}</button></div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}

function Community({
  user,
  onAuth,
}: {
  user: MeResponse["user"] | null;
  onAuth: () => void;
}) {
  const [groups, setGroups] = useState<CommunityGroup[]>([]);
  const [activeGroup, setActiveGroup] = useState<CommunityGroup | null>(null);
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [groupForm, setGroupForm] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [groupType, setGroupType] = useState("bible_study");
  const [postBody, setPostBody] = useState("");
  const [scriptureReference, setScriptureReference] = useState("");
  const [connections, setConnections] = useState<CommunityConnection[]>([]);
  const [invitations, setInvitations] = useState<GroupInvitation[]>([]);
  const [people, setPeople] = useState<CommunityPerson[]>([]);
  const [peopleQuery, setPeopleQuery] = useState("");
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteUsername, setInviteUsername] = useState("");
  const [commentsFor, setCommentsFor] = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, CommunityComment[]>>({});
  const [commentBody, setCommentBody] = useState("");
  const [error, setError] = useState("");

  const loadGroups = async () => {
    if (!user) return setGroups([]);
    const response = await api<{ groups: CommunityGroup[] }>("/api/community/groups");
    setGroups(response.groups);
    setActiveGroup((current) => current ?? response.groups[0] ?? null);
  };

  const loadPosts = async (group: CommunityGroup | null) => {
    if (!group) return setPosts([]);
    const response = await api<{ posts: CommunityPost[] }>(
      `/api/community/groups/${group.id}/posts`,
    );
    setPosts(response.posts);
  };

  const loadCommunityMeta = async () => {
    if (!user) {
      setConnections([]);
      setInvitations([]);
      return;
    }
    const [connectionResponse, invitationResponse] = await Promise.all([
      api<{ connections: CommunityConnection[] }>("/api/community/connections"),
      api<{ invitations: GroupInvitation[] }>("/api/community/invitations"),
    ]);
    setConnections(connectionResponse.connections);
    setInvitations(invitationResponse.invitations);
  };

  const loadComments = async (postId: string) => {
    const response = await api<{ comments: CommunityComment[] }>(
      `/api/community/posts/${postId}/comments`,
    );
    setComments((current) => ({ ...current, [postId]: response.comments }));
  };

  useEffect(() => {
    void loadGroups();
    void loadCommunityMeta();
  }, [user?.id]);
  useEffect(() => {
    void loadPosts(activeGroup);
  }, [activeGroup?.id]);

  const createGroup = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      await api("/api/community/groups", {
        method: "POST",
        body: JSON.stringify({ name, description, groupType, visibility: "private" }),
      });
      setName("");
      setDescription("");
      setGroupForm(false);
      setActiveGroup(null);
      await loadGroups();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The group could not be created.");
    }
  };

  const createPost = async (event: FormEvent) => {
    event.preventDefault();
    if (!activeGroup || !postBody.trim()) return;
    setError("");
    try {
      await api(`/api/community/groups/${activeGroup.id}/posts`, {
        method: "POST",
        body: JSON.stringify({
          body: postBody,
          scriptureReference: scriptureReference.trim() || null,
          postType: "discussion",
        }),
      });
      setPostBody("");
      setScriptureReference("");
      await loadPosts(activeGroup);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The post could not be shared.");
    }
  };

  const runPeopleSearch = async () => {
    if (peopleQuery.trim().length < 2) return;
    const response = await api<{ people: CommunityPerson[] }>(
      `/api/community/people?q=${encodeURIComponent(peopleQuery.trim())}`,
    );
    setPeople(response.people);
  };

  const searchPeople = async (event: FormEvent) => {
    event.preventDefault();
    await runPeopleSearch();
  };

  const inviteToGroup = async (event: FormEvent) => {
    event.preventDefault();
    if (!activeGroup) return;
    setError("");
    try {
      await api(`/api/community/groups/${activeGroup.id}/invitations`, {
        method: "POST",
        body: JSON.stringify({ username: inviteUsername.replace(/^@/, "") }),
      });
      setInviteUsername("");
      setInviteOpen(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The invitation could not be sent.");
    }
  };

  const respondToInvitation = async (id: string, action: "accept" | "decline") => {
    await api(`/api/community/invitations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ action }),
    });
    await Promise.all([loadGroups(), loadCommunityMeta()]);
  };

  const respondToConnection = async (
    userId: string,
    action: "accept" | "decline",
  ) => {
    await api(`/api/community/connections/${userId}`, {
      method: "PATCH",
      body: JSON.stringify({ action }),
    });
    await loadCommunityMeta();
  };

  const addComment = async (event: FormEvent, postId: string) => {
    event.preventDefault();
    if (!commentBody.trim()) return;
    await api(`/api/community/posts/${postId}/comments`, {
      method: "POST",
      body: JSON.stringify({ body: commentBody, parentCommentId: null }),
    });
    setCommentBody("");
    await Promise.all([loadComments(postId), loadPosts(activeGroup)]);
  };

  return (
    <div className="view">
      <header className="page-header">
        <div>
          <h1 className="page-title">Community</h1>
          <p className="page-subtitle">Christian community infrastructure shaped for fellowship, study, and safety—not endless engagement.</p>
        </div>
        <div className="page-actions">
          <button className="btn" onClick={() => (user ? setPeopleOpen(true) : onAuth())}>
            <MagnifyingGlass size={16} /> Find people
          </button>
          <button className="btn primary" onClick={() => (user ? setGroupForm(true) : onAuth())}><Plus size={16} /> Create group</button>
        </div>
      </header>
      {!user ? (
        <SignInCard title="Your community starts with consent" copy="Sign in to join invitation-based groups. Private notes and prayer-journal entries are never shared automatically." onAuth={onAuth} />
      ) : (
        <section className="community-welcome card">
          <div><span className="pill green"><CheckCircle size={13} /> No public engagement feed</span><h2>{groups.length ? "Your invitation-based groups" : "Build a healthier group space"}</h2><p>Create a Bible study, class, or church group with clear roles, moderation, mute controls, and deliberately shared content.</p></div>
          <Church size={58} />
        </section>
      )}
      {user && (invitations.length || connections.some((item) => item.status === "pending" && item.direction === "incoming")) ? (
        <section className="card panel community-inbox">
          <div className="space-between"><h2 className="panel-title">Invitations and requests</h2><span className="pill purple">{invitations.length + connections.filter((item) => item.status === "pending" && item.direction === "incoming").length}</span></div>
          {invitations.map((invitation) => (
            <div className="setting-row" key={invitation.id}>
              <div className="setting-copy"><h3>{invitation.groupName}</h3><p>{invitation.inviterName} invited you to this private group.</p></div>
              <div className="row"><button className="btn small-btn" onClick={() => void respondToInvitation(invitation.id, "decline")}>Decline</button><button className="btn primary small-btn" onClick={() => void respondToInvitation(invitation.id, "accept")}>Accept</button></div>
            </div>
          ))}
          {connections.filter((item) => item.status === "pending" && item.direction === "incoming").map((connection) => (
            <div className="setting-row" key={connection.userId}>
              <div className="setting-copy"><h3>{connection.displayName}</h3><p>@{connection.username} would like to connect.</p></div>
              <div className="row"><button className="btn small-btn" onClick={() => void respondToConnection(connection.userId, "decline")}>Decline</button><button className="btn primary small-btn" onClick={() => void respondToConnection(connection.userId, "accept")}>Accept</button></div>
            </div>
          ))}
        </section>
      ) : null}
      {error ? <div className="chat-error"><WarningCircle size={17} /> {error}</div> : null}
      {user && groups.length ? (
        <div className="community-workspace">
          <aside className="card panel">
            <h2 className="panel-title">Groups</h2>
            <div className="thread-list">
              {groups.map((group) => (
                <button className={`thread-item ${activeGroup?.id === group.id ? "active" : ""}`} onClick={() => setActiveGroup(group)} key={group.id}>
                  <div className="title">{group.name}</div>
                  <div className="preview">{group.memberCount} {group.memberCount === 1 ? "member" : "members"} · {group.role}</div>
                </button>
              ))}
            </div>
          </aside>
          <section className="community-feed">
            <header className="card panel">
              <div className="space-between"><span className="pill purple">{activeGroup?.groupType.replaceAll("_", " ")}</span>{["owner", "admin"].includes(activeGroup?.role ?? "") ? <button className="btn small-btn" onClick={() => setInviteOpen(true)}><Plus size={14} /> Invite by username</button> : null}</div>
              <h2 className="panel-title">{activeGroup?.name}</h2><p className="panel-sub">{activeGroup?.description || "A private Christian community space."}</p>
            </header>
            <form className="card panel stack" onSubmit={createPost}>
              <div className="field"><label htmlFor="community-post">Share with this group</label><textarea id="community-post" className="textarea" value={postBody} onChange={(event) => setPostBody(event.target.value)} placeholder="Start a thoughtful discussion…" maxLength={10000} required /></div>
              <div className="field"><label htmlFor="community-scripture">Scripture reference (optional)</label><input id="community-scripture" className="input" value={scriptureReference} onChange={(event) => setScriptureReference(event.target.value)} placeholder="Acts 17:11" maxLength={80} /></div>
              <div className="space-between"><span className="tiny muted">Nothing private is attached automatically.</span><button className="btn primary"><Plus size={15} /> Share deliberately</button></div>
            </form>
            {posts.length ? posts.map((post) => (
              <article className="card panel community-post" key={post.id}>
                <div className="space-between"><strong>{post.authorName}</strong><span className="tiny muted">{new Date(post.createdAt * 1000).toLocaleString()}</span></div>
                <p>{post.body}</p>
                {post.scriptureReference ? <Link className="citation-chip" to={referencePath(post.scriptureReference)}><BookOpenText size={12} /> {post.scriptureReference}</Link> : null}
                <div className="message-actions">
                  <button className="message-action" onClick={async () => { await api("/api/community/reactions", { method: "POST", body: JSON.stringify({ targetType: "post", targetId: post.id, emoji: "amen" }) }); await loadPosts(activeGroup); }}><Heart size={14} /> Amen · {post.reactionCount}</button>
                  <button className="message-action" onClick={async () => {
                    const next = commentsFor === post.id ? null : post.id;
                    setCommentsFor(next);
                    if (next) await loadComments(post.id);
                  }}><ChatCircleDots size={14} /> {post.commentCount} comments</button>
                  <button className="message-action" onClick={() => void api("/api/reports", { method: "POST", body: JSON.stringify({ targetType: "post", targetId: post.id, reason: "other", details: "Submitted from the group feed for moderator review." }) }).then(() => setError("Report submitted privately for moderator review."))}><ShieldCheck size={14} /> Report</button>
                </div>
                {commentsFor === post.id ? (
                  <div className="comment-thread">
                    {(comments[post.id] ?? []).map((comment) => (
                      <div className="comment-row" style={{ marginLeft: `${Math.min(comment.depth, 5) * 16}px` }} key={comment.id}>
                        <strong>{comment.authorName}</strong>
                        <span>{comment.body}</span>
                        <small>@{comment.username ?? "member"} · {new Date(comment.createdAt * 1000).toLocaleString()}</small>
                      </div>
                    ))}
                    <form className="row comment-form" onSubmit={(event) => void addComment(event, post.id)}>
                      <input className="input" value={commentBody} onChange={(event) => setCommentBody(event.target.value)} placeholder="Add a thoughtful comment…" maxLength={5000} required />
                      <button className="btn primary small-btn">Comment</button>
                    </form>
                  </div>
                ) : null}
              </article>
            )) : <div className="card empty-state"><ChatCircleDots size={28} /><h3>Start the first discussion</h3><p>Share a question, reading insight, or resource with this group.</p></div>}
          </section>
        </div>
      ) : user ? (
        <div className="community-grid">
          {[
            [Church, "Church groups", "Private church spaces with visible roles and conservative defaults."],
            [UsersThree, "Bible studies", "Shared reading plans, discussions, and explicitly shared notes."],
            [ChalkboardTeacher, "Classes", "Teacher-led lessons without engagement-maximizing feed mechanics."],
          ].map(([CardIcon, title, copy]) => {
            const ItemIcon = CardIcon as Icon;
            return <article className="card community-card" key={String(title)}><span><ItemIcon size={23} /></span><h3>{String(title)}</h3><p>{String(copy)}</p><button className="rail-link" onClick={() => setGroupForm(true)}>Create safely <CaretRight size={14} /></button></article>;
          })}
        </div>
      ) : null}
      <section className="safety-strip">
        <ShieldCheck size={23} />
        <div><strong>Conservative privacy and youth-safety defaults</strong><p>Blocking, reporting, moderation queues, audit history, bounded comment nesting, and age-aware onboarding are built into the data model.</p></div>
      </section>
      {groupForm ? (
        <Modal title="Create a private group" onClose={() => setGroupForm(false)}>
          <form className="stack" onSubmit={createGroup}>
            <div className="field"><label htmlFor="group-name">Group name</label><input id="group-name" className="input" value={name} onChange={(event) => setName(event.target.value)} maxLength={100} required /></div>
            <div className="field"><label htmlFor="group-type">Group type</label><select id="group-type" className="select" value={groupType} onChange={(event) => setGroupType(event.target.value)}><option value="bible_study">Bible study</option><option value="church">Church</option><option value="class">Class</option><option value="ministry">Ministry</option></select></div>
            <div className="field"><label htmlFor="group-description">Description</label><textarea id="group-description" className="textarea" value={description} onChange={(event) => setDescription(event.target.value)} maxLength={1000} /></div>
            <p className="tiny muted">The group starts private. You are the owner and no one joins without a deliberate invitation.</p>
            <div className="modal-actions"><button type="button" className="btn" onClick={() => setGroupForm(false)}>Cancel</button><button className="btn primary">Create group</button></div>
          </form>
        </Modal>
      ) : null}
      {inviteOpen ? (
        <Modal title={`Invite to ${activeGroup?.name ?? "group"}`} onClose={() => setInviteOpen(false)}>
          <form className="stack" onSubmit={inviteToGroup}>
            <div className="field"><label htmlFor="invite-username">Bible Alone username</label><div className="username-field"><span>@</span><input id="invite-username" className="input" value={inviteUsername} onChange={(event) => setInviteUsername(event.target.value.replace(/[^a-zA-Z0-9_]/g, ""))} minLength={3} maxLength={30} required /></div></div>
            <p className="tiny muted">The person chooses whether to join. An invitation never exposes private group content.</p>
            <div className="modal-actions"><button type="button" className="btn" onClick={() => setInviteOpen(false)}>Cancel</button><button className="btn primary">Send invitation</button></div>
          </form>
        </Modal>
      ) : null}
      {peopleOpen ? (
        <Modal title="Find Bible Alone people" onClose={() => setPeopleOpen(false)}>
          <form className="row" onSubmit={searchPeople}>
            <input className="input" value={peopleQuery} onChange={(event) => setPeopleQuery(event.target.value)} placeholder="Search a username or display name" minLength={2} maxLength={80} required />
            <button className="btn primary"><MagnifyingGlass size={15} /> Search</button>
          </form>
          <div className="people-results">
            {people.map((person) => (
              <div className="setting-row" key={person.userId}>
                <div className="setting-copy"><h3>{person.displayName}</h3><p>@{person.username}{person.bio ? ` · ${person.bio}` : ""}</p></div>
                {person.connectionStatus === "accepted" ? <span className="pill green"><Check size={13} /> Connected</span> : person.connectionStatus === "incoming" ? <button className="btn primary small-btn" onClick={() => void respondToConnection(person.userId, "accept")}>Accept</button> : person.connectionStatus === "outgoing" ? <span className="pill">Request sent</span> : <button className="btn small-btn" onClick={async () => { await api(`/api/community/connections/${encodeURIComponent(person.username)}`, { method: "POST" }); await runPeopleSearch(); }}><Plus size={14} /> Connect</button>}
              </div>
            ))}
            {peopleQuery.length >= 2 && !people.length ? <p className="panel-sub">No visible profiles matched yet.</p> : null}
          </div>
          {connections.some((item) => item.status === "accepted") ? (
            <div className="divider-list">
              <h3>Connections</h3>
              {connections.filter((item) => item.status === "accepted").map((connection) => <p key={connection.userId}>{connection.displayName} <span className="muted">@{connection.username}</span></p>)}
            </div>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}

interface DeepStudySummary {
  id: string;
  title: string;
  question: string;
  status: string;
  progress: number;
  progressLabel: string;
  createdAt: number;
  updatedAt: number;
  completedAt: number | null;
  conversationId?: string | null;
  requestMessageId?: string | null;
  framingMode?: string;
}

function DeepStudy({
  user,
  billing,
  onAuth,
}: {
  user: MeResponse["user"] | null;
  billing: BillingState | null;
  onAuth: () => void;
}) {
  const [studies, setStudies] = useState<DeepStudySummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DeepStudyDetail | null>(null);
  const [sources, setSources] = useState<DeepStudySource[]>([]);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const active = detail && ["queued", "planning", "gathering", "analyzing", "writing"].includes(detail.status);

  const loadStudies = async () => {
    if (!user) return;
    const result = await api<{ studies: DeepStudySummary[] }>("/api/deep-studies");
    setStudies(result.studies);
    setSelectedId((current) => current ?? result.studies[0]?.id ?? null);
  };

  const loadDetail = async (id: string) => {
    const result = await api<{ study: DeepStudyDetail; sources: DeepStudySource[] }>(
      `/api/deep-studies/${encodeURIComponent(id)}`,
    );
    setDetail(result.study);
    setSources(result.sources);
    setStudies((current) =>
      current.map((study) =>
        study.id === result.study.id ? { ...study, ...result.study } : study,
      ),
    );
  };

  useEffect(() => {
    if (!user) {
      setStudies([]);
      setDetail(null);
      return;
    }
    void loadStudies().catch((caught) =>
      setError(caught instanceof Error ? caught.message : "Studies could not be loaded."),
    );
  }, [user?.id]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setSources([]);
      return;
    }
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const result = await api<{ study: DeepStudyDetail; sources: DeepStudySource[] }>(
          `/api/deep-studies/${encodeURIComponent(selectedId)}`,
        );
        if (stopped) return;
        setDetail(result.study);
        setSources(result.sources);
        setStudies((current) =>
          current.map((study) =>
            study.id === result.study.id ? { ...study, ...result.study } : study,
          ),
        );
        if (["queued", "planning", "gathering", "analyzing", "writing"].includes(result.study.status)) {
          timer = setTimeout(() => void poll(), 4_000);
        }
      } catch (caught) {
        if (!stopped) {
          setError(caught instanceof Error ? caught.message : "The study could not be loaded.");
        }
      }
    };
    void poll();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [selectedId]);

  const createStudy = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) return onAuth();
    if (billing?.planKey !== "max") {
      setError("Sola Deep Study is included with Bible Alone Max.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await api<{ id: string }>("/api/deep-studies", {
        method: "POST",
        body: JSON.stringify({ question }),
      });
      setQuestion("");
      setSelectedId(result.id);
      await loadStudies();
      setSelectedId(result.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The study could not be started.");
    } finally {
      setLoading(false);
    }
  };

  const cancelStudy = async () => {
    if (!selectedId) return;
    setLoading(true);
    setError("");
    try {
      await api(`/api/deep-studies/${encodeURIComponent(selectedId)}/cancel`, {
        method: "POST",
      });
      await Promise.all([loadStudies(), loadDetail(selectedId)]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The study could not be canceled.");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="view">
        <SignInCard
          title="Sign in for Sola Deep Study"
          copy="Deep Study creates durable, source-tracked research reports and saves them to your Bible Alone account."
          onAuth={onAuth}
        />
      </div>
    );
  }

  return (
    <div className="view deep-study-view">
      <header className="page-header">
        <div>
          <div className="eyebrow"><Sparkle size={15} /> Max workspace</div>
          <h1 className="page-title">Sola Deep Study</h1>
          <p className="page-subtitle">
            Durable, source-tracked biblical research with visible progress and saved reports.
          </p>
        </div>
        <span className={`pill ${billing?.planKey === "max" ? "gold" : ""}`}>
          {billing?.plan.name ?? "Free"} · {Math.floor(billing?.credits.available ?? 0)} credits
        </span>
      </header>

      <div className="deep-study-layout">
        <aside className="deep-study-list">
          <form className="deep-study-create" onSubmit={createStudy}>
            <label htmlFor="deep-study-question">Research question</label>
            <textarea
              id="deep-study-question"
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              minLength={12}
              maxLength={8_000}
              placeholder="Compare the major interpretations of Romans 9 in their literary, historical, and canonical context."
            />
            <small>Up to 200 credits are reserved; unused credits are returned.</small>
            {billing?.planKey === "max" ? (
              <button
                className="btn primary"
                disabled={loading || question.trim().length < 12 || Boolean(active)}
              >
                <Sparkle size={16} />
                {loading ? "Starting…" : "Begin Deep Study"}
              </button>
            ) : (
              <Link className="btn primary" to="/settings?billing=plans">
                <Sparkle size={16} /> Upgrade to Max
              </Link>
            )}
          </form>
          <div className="deep-study-history">
            <h2>Saved studies</h2>
            {studies.length ? (
              studies.map((study) => (
                <button
                  type="button"
                  className={selectedId === study.id ? "active" : ""}
                  onClick={() => setSelectedId(study.id)}
                  key={study.id}
                >
                  <strong>{study.title}</strong>
                  <span>{study.progressLabel}</span>
                </button>
              ))
            ) : (
              <p>No Deep Studies yet.</p>
            )}
          </div>
        </aside>

        <section className="deep-study-report settings-surface">
          {detail ? (
            <>
              <div className="deep-study-report-head">
                <div>
                  <span className="ai-label"><Sparkle size={14} /> AI-generated research assistance</span>
                  <h2>{detail.title}</h2>
                </div>
                {active ? (
                  <button className="btn" type="button" disabled={loading} onClick={() => void cancelStudy()}>
                    <X size={15} /> Cancel and refund unused
                  </button>
                ) : null}
              </div>
              <p className="deep-study-status" role="status">{detail.progressLabel}</p>
              {detail.errorMessage ? <div className="chat-error">{detail.errorMessage}</div> : null}
              {detail.reportMarkdown ? (
                <StudyWorkspace runId={detail.researchRunId}><MarkdownContent className="deep-study-markdown ai-commentary">
                  {detail.reportMarkdown}
                </MarkdownContent></StudyWorkspace>
              ) : ["failed", "canceled"].includes(detail.status) ? (
                <div className="deep-study-waiting">
                  <WarningCircle size={28} />
                  <p>
                    {detail.status === "canceled"
                      ? "This study was canceled."
                      : "This study did not complete. You can begin it again from the research form."}
                  </p>
                </div>
              ) : (
                <div className="deep-study-waiting">
                  <Sparkle size={28} />
                  <p>Sola is gathering permitted sources and preparing a clearly labeled report.</p>
                </div>
              )}
              {sources.length ? (
                <details className="source-register">
                  <summary>{sources.length} provenance-tracked sources</summary>
                  {sources.map((source, index) => (
                    <article key={`${source.title}-${index}`}>
                      <strong>{source.title}</strong>
                      <span>{[source.sourceType, source.edition, source.language].filter(Boolean).join(" · ")}</span>
                      <small>{source.license ?? "License not specified"} · {source.attribution ?? "Attribution not specified"}</small>
                      {source.url ? <a href={source.url} target="_blank" rel="noreferrer">Open source</a> : null}
                    </article>
                  ))}
                </details>
              ) : null}
            </>
          ) : (
            <div className="deep-study-empty">
              <Books size={34} />
              <h2>Choose or begin a study</h2>
              <p>
                Scripture quotations, textual observations, history, interpretation,
                denominational perspectives, web evidence, and Sola’s synthesis remain distinct.
              </p>
            </div>
          )}
        </section>
      </div>
      {error ? <div className="chat-error"><WarningCircle size={17} /> {error}</div> : null}
      <section className="settings-section doctrinal-boundary">
        <ShieldCheck size={22} />
        <div>
          <strong>Sola’s report is fallible.</strong>
          <p>Verify citations and conclusions through Scripture, prayer, wise teachers, and the local church.</p>
        </div>
      </section>
      </div>
  );
}

function formatBillingPrice(price: BillingPrice) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: price.currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(price.unitAmount / 100);
}

function formatBillingAmount(
  unitAmount: number,
  currency = "usd",
  maximumFractionDigits = 0,
) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(unitAmount / 100);
}

const PERSONAL_PLAN_ORDER = ["core", "pro", "max"] as const;

const PERSONAL_PLAN_MARKETING: Record<
  (typeof PERSONAL_PLAN_ORDER)[number],
  { name: string; eyebrow: string; description: string; features: string[] }
> = {
  core: {
    name: "Core",
    eyebrow: "A faithful start",
    description: "Everyday help and focused Scripture study without a large commitment.",
    features: ["200 credits every month", "Everyday and Scripture modes", "Notes and Bible reading stay free"],
  },
  pro: {
    name: "Pro",
    eyebrow: "Most popular",
    description: "More room for regular study, family discipleship, doctrine, and ministry.",
    features: ["500 credits every month", "All standard Sola study modes", "Built for regular weekly use"],
  },
  max: {
    name: "Max",
    eyebrow: "Deepest access",
    description: "Maximum monthly capacity with long-form, source-aware Sola Deep Study.",
    features: ["3,000 credits every month", "Sola Deep Study included", "One active deep study at a time"],
  },
};

function billingStatusLabel(status: string) {
  const labels: Record<string, string> = {
    active: "Active",
    trialing: "Trial",
    past_due: "Payment due",
    unpaid: "Unpaid",
    paused: "Paused",
    incomplete: "Incomplete",
    incomplete_expired: "Expired",
    canceled: "Canceled",
  };
  return labels[status] ?? status.replaceAll("_", " ");
}

function verifiedStripeUrl(value: string, expectedHost: string) {
  const url = new URL(value);
  if (url.protocol !== "https:" || url.hostname !== expectedHost) {
    throw new Error("Stripe returned an unexpected destination.");
  }
  return url.href;
}

function Settings({
  me,
  onAuth,
  onRefresh,
  themePreference,
  systemTheme,
  onThemeChange,
}: {
  me: MeResponse | null;
  onAuth: () => void;
  onRefresh: () => Promise<void>;
  themePreference: ThemePreference;
  systemTheme: ResolvedTheme;
  onThemeChange: (theme: ThemePreference) => void;
}) {
  const location = useLocation();
  const billingReturn = new URLSearchParams(location.search).get("billing");
  const [tab, setTab] = useState(billingReturn ? "billing" : "appearance");
  const [message, setMessage] = useState(() => {
    if (billingReturn === "success") {
      return "Checkout completed. Stripe is confirming your subscription now.";
    }
    if (billingReturn === "canceled") return "Checkout was canceled. You were not charged.";
    if (billingReturn === "return") return "Your Stripe billing portal session is complete.";
    return "";
  });
  const [editingProfile, setEditingProfile] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [billingAction, setBillingAction] = useState("");
  const [personalBillingInterval, setPersonalBillingInterval] =
    useState<BillingPrice["interval"]>("year");
  const [churchSeats, setChurchSeats] = useState(5);
  const [churchInterval, setChurchInterval] = useState<"month" | "year">("month");
  const [churchOrganizationId, setChurchOrganizationId] = useState("");
  const settingsNavRef = useRef<HTMLElement>(null);
  const settingsSections = [
    ["appearance", Monitor, "Appearance"],
    ["profile", UserCircle, "Profile & doctrine"],
    ["ai", Sparkle, "Sola"],
    ["billing", CreditCard, "Billing"],
    ["support", Bug, "Support & feedback"],
    ["bible", BookOpenText, "Bible reader"],
    ["privacy", LockKey, "Privacy & data"],
  ] as const;
  const isNativePlatform = Capacitor.isNativePlatform();
  const personalPlanOptions = useMemo(() => {
    if (!billing) return [];
    return PERSONAL_PLAN_ORDER.flatMap((planKey) => {
      const monthly = billing.prices.find(
        (price) => price.planKey === planKey && price.interval === "month",
      );
      const yearly = billing.prices.find(
        (price) => price.planKey === planKey && price.interval === "year",
      );
      const selected = personalBillingInterval === "year" ? yearly : monthly;
      return monthly && yearly && selected
        ? [{ planKey, monthly, yearly, selected }]
        : [];
    });
  }, [billing, personalBillingInterval]);

  useEffect(() => {
    settingsNavRef.current
      ?.querySelector(".active")
      ?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [tab]);

  useEffect(() => {
    if (!me || tab !== "billing") {
      setBilling(null);
      return;
    }
    let active = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const load = async (attempt = 0) => {
      if (attempt === 0) setBillingLoading(true);
      try {
        const result = await api<BillingState>("/api/billing");
        if (!active) return;
        setBilling(result);
        setChurchOrganizationId((current) =>
          current ||
          result.organizations.find((organization) =>
            ["owner", "admin"].includes(organization.role),
          )?.organizationId ||
          "",
        );
        setBillingError("");
        if (
          billingReturn === "success" &&
          !result.subscription?.entitled &&
          attempt < 3
        ) {
          retryTimer = setTimeout(() => void load(attempt + 1), 1_500);
        } else {
          setBillingLoading(false);
          if (billingReturn === "success" && result.subscription?.entitled) {
            setMessage("Your Stripe subscription is active.");
          }
        }
      } catch (caught) {
        if (!active) return;
        setBillingError(
          caught instanceof Error ? caught.message : "Billing details could not be loaded.",
        );
        setBillingLoading(false);
      }
    };
    void load();
    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [billingReturn, me?.user.id, tab]);

  const beginCheckout = async (
    planKey: Exclude<BillingPrice["planKey"], "church">,
    interval: BillingPrice["interval"],
  ) => {
    const actionKey = `${planKey}-${interval}`;
    setBillingAction(actionKey);
    setBillingError("");
    try {
      const result = await api<{ url: string }>("/api/billing/checkout", {
        method: "POST",
        body: JSON.stringify({ planKey, interval }),
      });
      window.location.assign(verifiedStripeUrl(result.url, "checkout.stripe.com"));
    } catch (caught) {
      setBillingError(caught instanceof Error ? caught.message : "Checkout could not be opened.");
      setBillingAction("");
    }
  };

  const beginTopUp = async (topUpKey: BillingState["topUps"][number]["key"]) => {
    setBillingAction(topUpKey);
    setBillingError("");
    try {
      const result = await api<{ url: string }>("/api/billing/top-up", {
        method: "POST",
        body: JSON.stringify({ topUpKey }),
      });
      window.location.assign(verifiedStripeUrl(result.url, "checkout.stripe.com"));
    } catch (caught) {
      setBillingError(caught instanceof Error ? caught.message : "The credit pack could not be opened.");
      setBillingAction("");
    }
  };

  const beginChurchCheckout = async (organizationId: string) => {
    setBillingAction(`church-${organizationId}`);
    setBillingError("");
    try {
      const result = await api<{ url: string }>("/api/billing/church-checkout", {
        method: "POST",
        body: JSON.stringify({
          organizationId,
          interval: churchInterval,
          seatCount: churchSeats,
        }),
      });
      window.location.assign(verifiedStripeUrl(result.url, "checkout.stripe.com"));
    } catch (caught) {
      setBillingError(caught instanceof Error ? caught.message : "Church checkout could not be opened.");
      setBillingAction("");
    }
  };

  const openBillingPortal = async () => {
    setBillingAction("portal");
    setBillingError("");
    try {
      const result = await api<{ url: string }>("/api/billing/portal", {
        method: "POST",
      });
      window.location.assign(verifiedStripeUrl(result.url, "billing.stripe.com"));
    } catch (caught) {
      setBillingError(
        caught instanceof Error ? caught.message : "The billing portal could not be opened.",
      );
      setBillingAction("");
    }
  };

  const downloadExport = async () => {
    if (!me) return onAuth();
    const data = await api<Record<string, unknown>>("/api/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `bible-alone-export-${new Date().toISOString().slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="view">
      <header className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Configure your profile, doctrine, Bible reader, AI behavior, billing, privacy, and data.</p>
        </div>
        <div className="page-actions"><span className="demo-status">Bible Alone v0.1</span></div>
      </header>
      <div className="settings-layout">
        <label className="settings-mobile-picker">
          <span>Settings section</span>
          <select value={tab} onChange={(event) => setTab(event.target.value)}>
            {settingsSections.map(([value, , label]) => (
              <option value={value} key={value}>{label}</option>
            ))}
          </select>
        </label>
        <aside className="settings-nav" ref={settingsNavRef}>
          {settingsSections.map(([value, SettingsIcon, label]) => {
            const ItemIcon = SettingsIcon as Icon;
            return (
              <button className={tab === value ? "active" : ""} onClick={() => setTab(String(value))} key={String(value)}>
                <ItemIcon size={16} /> {String(label)}
              </button>
            );
          })}
        </aside>
        <div>
          {tab === "appearance" ? (
            <section className="settings-section settings-surface">
              <h2 className="panel-title">Appearance</h2>
              <p className="panel-sub">
                Bible Alone follows your device by default. Your choice stays on this device.
              </p>
              <div className="theme-options" role="radiogroup" aria-label="Color theme">
                {([
                  ["system", Monitor, "System", `Follow this device · currently ${systemTheme}`],
                  ["light", Sun, "Light", "Use the light reading theme"],
                  ["dark", Moon, "Dark", "Use the low-glare dark theme"],
                ] as Array<[ThemePreference, Icon, string, string]>).map(
                  ([value, ThemeIcon, label, description]) => (
                    <button
                      type="button"
                      className={themePreference === value ? "active" : ""}
                      role="radio"
                      aria-checked={themePreference === value}
                      onClick={() => onThemeChange(value)}
                      key={value}
                    >
                      <span className="theme-option-icon"><ThemeIcon size={20} /></span>
                      <span>
                        <strong>{label}</strong>
                        <small>{description}</small>
                      </span>
                      {themePreference === value ? <CheckCircle size={18} weight="fill" /> : null}
                    </button>
                  ),
                )}
              </div>
            </section>
          ) : !me ? (
            <SignInCard
              title={
                tab === "billing"
                  ? "Sign in to view billing"
                  : tab === "support"
                    ? "Sign in to contact support"
                    : "Sync settings across devices"
              }
              copy={
                tab === "billing"
                  ? "Subscriptions and receipts are tied to your private Bible Alone account."
                  : tab === "support"
                    ? "Paid members can report errors and request features from their Bible Alone account."
                  : "Sign in to configure your doctrine profile, response preferences, privacy, and exports."
              }
              onAuth={onAuth}
            />
          ) : tab === "profile" ? (
            <section className="settings-section settings-surface">
              <h2 className="panel-title">Profile and doctrine</h2>
              <p className="panel-sub">Sola starts from your selected profile while naming responsible Christian disagreement.</p>
              <div className="setting-row">
                <div className="setting-copy"><h3>Signed in as</h3><p>{me.user.email}</p></div>
                <span className="pill green"><Check size={13} /> Secure session</span>
              </div>
              <div className="setting-row">
                <div className="setting-copy"><h3>Username</h3><p>@{me.profile?.username}</p></div>
                <strong>{me.profile?.display_name}</strong>
              </div>
              <div className="setting-row">
                <div className="setting-copy"><h3>Doctrine profile</h3><p>Controls default framing, never the biblical text.</p></div>
                <span className="pill purple">{doctrineLabel(me.profile)}</span>
              </div>
              <button className="btn" onClick={() => setEditingProfile(true)}><NotePencil size={15} /> Review onboarding choices</button>
            </section>
          ) : tab === "ai" ? (
            <section className="settings-section settings-surface">
              <h2 className="panel-title">Sola response behavior</h2>
              <p className="panel-sub">AI-generated output is fallible, visibly labeled, and subordinate to Scripture.</p>
              <div className="setting-row"><div className="setting-copy"><h3>Response depth</h3><p>Choose concise, balanced, or deep answers.</p></div><span className="pill">{me.profile?.response_depth ?? "balanced"}</span></div>
              <div className="setting-row"><div className="setting-copy"><h3>Multiple interpretations</h3><p>Name responsible disagreement when it matters.</p></div><span className={`switch ${me.profile?.show_multiple_interpretations ? "on" : ""}`} /></div>
              <div className="setting-row"><div className="setting-copy"><h3>Scripture Mode</h3><p>Keep biblical text and context prominent for study questions.</p></div><span className={`switch ${me.profile?.scripture_mode ? "on" : ""}`} /></div>
            </section>
          ) : tab === "billing" && me.billing.credits.unlimited ? (
            <section className="settings-section settings-surface">
              <span className="eyebrow"><ShieldStar size={14} weight="fill" /> Administrator access</span>
              <h2 className="panel-title">Unlimited Bible Alone access</h2>
              <p className="panel-sub">
                Every Sola mode, including Deep Study, is enabled without deducting credits.
                Provider usage is still measured for cost and reliability monitoring.
              </p>
              <div className="billing-usage-card admin-unlimited-card">
                <div>
                  <strong>Unlimited Sola access</strong>
                  <span>100% available</span>
                </div>
                <div className="billing-usage-track plan-max" aria-hidden="true">
                  <span style={{ width: "100%" }} />
                </div>
                <small>No subscription or top-up is required for this administrator account.</small>
              </div>
              <Link className="btn primary" to="/admin">
                <ShieldStar size={16} /> Open admin dashboard
              </Link>
            </section>
          ) : tab === "billing" ? (
            <section className="settings-section settings-surface">
              <h2 className="panel-title">Billing</h2>
              <p className="panel-sub">
                Stripe securely handles checkout, payment details, invoices, and subscription management. Bible Alone never stores your card number.
              </p>
              {billingLoading && !billing ? (
                <div className="billing-loading">Loading billing details…</div>
              ) : billing ? (
                <>
                  <div className="setting-row">
                    <div className="setting-copy">
                      <h3>Current plan</h3>
                      <p>
                        {billing.plan.name} · {billing.plan.monthlyCredits.toLocaleString()} monthly Sola credits
                      </p>
                    </div>
                    <span className={`pill ${billing.subscription?.entitled ? "green" : ""}`}>
                      {billing.subscription
                        ? billingStatusLabel(billing.subscription.status)
                        : "Free"}
                    </span>
                  </div>
                  <div className="billing-usage-card">
                    <div>
                      <strong>{Math.floor(billing.credits.available).toLocaleString()} credits available</strong>
                      <span>{Math.round(billing.credits.percentRemaining)}% remaining</span>
                    </div>
                    <div
                      className={`billing-usage-track plan-${billing.planKey}`}
                      role="progressbar"
                      aria-label="Sola credits remaining"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(billing.credits.percentRemaining)}
                    >
                      <span style={{ width: `${billing.credits.percentRemaining}%` }} />
                    </div>
                    <small>
                      {billing.credits.included.toLocaleString()} included ·{" "}
                      {billing.credits.topUp.toLocaleString()} purchased ·{" "}
                      {billing.credits.reserved.toLocaleString()} reserved
                      {billing.credits.refreshAt
                        ? ` · refreshes ${new Date(billing.credits.refreshAt * 1000).toLocaleDateString()}`
                        : ""}
                    </small>
                  </div>
                  {billing.subscription?.status === "trialing" && billing.subscription.trialEnd ? (
                    <div className="billing-trial-notice">
                      <strong>$0 today · trial active</strong>
                      <p>
                        Your first charge is scheduled for{" "}
                        {new Date(billing.subscription.trialEnd * 1000).toLocaleDateString()}.
                        Cancel before then from Manage billing to avoid the charge.
                      </p>
                    </div>
                  ) : null}
                  {billing.subscription?.currentPeriodEnd ? (
                    <div className="setting-row">
                      <div className="setting-copy">
                        <h3>
                          {billing.subscription.cancelAtPeriodEnd ? "Access through" : "Next renewal"}
                        </h3>
                        <p>
                          {billing.subscription.cancelAtPeriodEnd
                            ? "The subscription is set to end after this billing period."
                            : "Stripe will manage the next scheduled renewal."}
                        </p>
                      </div>
                      <strong>
                        {new Date(
                          billing.subscription.currentPeriodEnd * 1000,
                        ).toLocaleDateString()}
                      </strong>
                    </div>
                  ) : null}
                  {isNativePlatform ? (
                    <div className="billing-native-note">
                      <CreditCard size={20} />
                      <div>
                        <strong>Web billing</strong>
                        <p>
                          Purchases are not offered inside the mobile app. Existing subscriptions
                          purchased on BibleAlone.app sync here automatically.
                        </p>
                      </div>
                    </div>
                  ) : billing.configured ? (
                    <>
                      {billing.subscription &&
                      !["canceled", "incomplete_expired"].includes(
                        billing.subscription.status,
                      ) ? (
                        <>
                          <button
                            type="button"
                            className="btn primary"
                            disabled={Boolean(billingAction)}
                            onClick={() => void openBillingPortal()}
                          >
                            <CreditCard size={15} />
                            {billingAction === "portal" ? "Opening Stripe…" : "Manage, upgrade, or cancel"}
                          </button>
                          <div className="billing-topups">
                            <h3>Add Sola credits</h3>
                            <p>Purchased credits are used after included credits and expire after 12 months.</p>
                            <div>
                              {billing.topUps.map((topUp) => (
                                <button
                                  type="button"
                                  className="billing-topup-card"
                                  key={topUp.key}
                                  disabled={!topUp.configured || Boolean(billingAction)}
                                  onClick={() => void beginTopUp(topUp.key)}
                                >
                                  <strong>{topUp.credits.toLocaleString()} credits</strong>
                                  <span>
                                    {new Intl.NumberFormat("en-US", {
                                      style: "currency",
                                      currency: "USD",
                                      maximumFractionDigits: 0,
                                    }).format(topUp.unitAmount / 100)}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="billing-offers">
                          {billing.trialAvailable ? (
                            <div className="billing-offer-intro">
                              <div>
                                <span className="eyebrow">
                                  <Sparkle size={14} weight="fill" /> 30-day trial included
                                </span>
                                <h3>Study with Sola for $0 today</h3>
                                <p>
                                  Get the full plan you choose for 30 days. We will remind you
                                  seven days before your first charge, and you can cancel anytime.
                                </p>
                              </div>
                              <div
                                className="billing-trial-value"
                                aria-label="Up to a 100 dollar value included free for 30 days"
                              >
                                <s>Up to $100 value</s>
                                <strong>FREE</strong>
                                <small>for your first 30 days</small>
                              </div>
                            </div>
                          ) : (
                            <div className="billing-offer-intro compact">
                              <div>
                                <span className="eyebrow">
                                  <Sparkle size={14} weight="fill" /> Choose your Sola plan
                                </span>
                                <h3>More room to study, ask, and prepare</h3>
                                <p>Annual plans include two months free. Cancel anytime from Stripe.</p>
                              </div>
                            </div>
                          )}

                          <div className="billing-interval-row">
                            <div
                              className="billing-interval-toggle"
                              role="radiogroup"
                              aria-label="Personal plan billing interval"
                            >
                              <button
                                type="button"
                                role="radio"
                                aria-checked={personalBillingInterval === "month"}
                                className={personalBillingInterval === "month" ? "active" : ""}
                                onClick={() => setPersonalBillingInterval("month")}
                              >
                                Monthly
                              </button>
                              <button
                                type="button"
                                role="radio"
                                aria-checked={personalBillingInterval === "year"}
                                className={personalBillingInterval === "year" ? "active" : ""}
                                onClick={() => setPersonalBillingInterval("year")}
                              >
                                Yearly
                                <span>2 months free</span>
                              </button>
                            </div>
                            {personalBillingInterval === "year" ? (
                              <span className="billing-savings-summary">
                                <CheckCircle size={15} weight="fill" /> Save 17% every year
                              </span>
                            ) : (
                              <span className="billing-savings-summary muted">
                                Flexible month to month
                              </span>
                            )}
                          </div>

                          <div className="billing-price-grid">
                            {personalPlanOptions.map(({ planKey, monthly, yearly, selected }) => {
                              const marketing = PERSONAL_PLAN_MARKETING[planKey];
                              const yearlySavings = monthly.unitAmount * 12 - yearly.unitAmount;
                              const monthlyEquivalent = yearly.unitAmount / 12;
                              return (
                                <article
                                  className={`billing-price-card plan-${planKey} ${planKey === "pro" ? "is-featured" : ""}`}
                                  key={selected.id}
                                >
                                  <div className="billing-plan-heading">
                                    <div>
                                      <span>{marketing.eyebrow}</span>
                                      <h4>{marketing.name}</h4>
                                    </div>
                                    {planKey === "pro" ? (
                                      <span className="billing-popular-badge">Best value</span>
                                    ) : null}
                                  </div>
                                  <p className="billing-plan-description">{marketing.description}</p>

                                  {personalBillingInterval === "year" ? (
                                    <>
                                      <div className="billing-price-line">
                                        <s>{formatBillingPrice(monthly)}/mo</s>
                                        <strong>
                                          {formatBillingAmount(
                                            monthlyEquivalent,
                                            yearly.currency,
                                            2,
                                          )}
                                        </strong>
                                        <small>/mo</small>
                                      </div>
                                      <div className="billing-annual-detail">
                                        <strong>
                                          Save {formatBillingAmount(yearlySavings, yearly.currency)}
                                        </strong>
                                        <span>
                                          {formatBillingPrice(yearly)} billed yearly after trial
                                        </span>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="billing-price-line">
                                        <strong>{formatBillingPrice(monthly)}</strong>
                                        <small>/month</small>
                                      </div>
                                      <div className="billing-annual-detail neutral">
                                        <span>Simple monthly billing after trial</span>
                                      </div>
                                    </>
                                  )}

                                  {billing.trialAvailable ? (
                                    <div className="billing-card-trial">
                                      <span>First 30 days</span>
                                      <div>
                                        <s>{formatBillingPrice(monthly)}</s>
                                        <strong>FREE</strong>
                                      </div>
                                    </div>
                                  ) : null}

                                  <ul className="billing-plan-features">
                                    {marketing.features.map((feature) => (
                                      <li key={feature}>
                                        <CheckCircle size={15} weight="fill" />
                                        <span>{feature}</span>
                                      </li>
                                    ))}
                                  </ul>

                                  <button
                                    type="button"
                                    className="btn primary"
                                    disabled={Boolean(billingAction)}
                                    onClick={() =>
                                      void beginCheckout(planKey, selected.interval)
                                    }
                                  >
                                    {billingAction === `${planKey}-${selected.interval}`
                                      ? "Opening Stripe…"
                                      : billing.trialAvailable
                                        ? "Start free · Pay $0 today"
                                        : `Choose ${marketing.name}`}
                                  </button>
                                  <small className="billing-card-fine-print">
                                    {billing.trialAvailable
                                      ? `Then ${formatBillingPrice(selected)} per ${selected.interval}. `
                                      : ""}
                                    Cancel anytime.
                                  </small>
                                </article>
                              );
                            })}
                          </div>

                          <div className="billing-trust-strip">
                            <span><Check size={14} /> $0 charged today</span>
                            <span><Check size={14} /> Reminder before renewal</span>
                            <span><Check size={14} /> Cancel anytime</span>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="billing-native-note">
                      <CreditCard size={20} />
                      <div>
                        <strong>Billing setup is in progress</strong>
                        <p>Stripe test prices and webhook credentials have not been connected yet.</p>
                      </div>
                    </div>
                  )}
                  {!isNativePlatform ? (
                    <div className="church-billing-card plan-church">
                      <div>
                        <span className="eyebrow"><Church size={14} /> Organization plan</span>
                        <h3>Bible Alone for Churches</h3>
                        <p>
                          $99/month or $990/year includes 5 staff or leader seats and
                          2,500 pooled monthly credits. Added seats are $15/month or
                          $150/year and add 500 credits each. Contact{" "}
                          <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>{" "}
                          for more than 100 seats.
                        </p>
                      </div>
                      {billing.organizations.some((organization) =>
                        ["owner", "admin"].includes(organization.role),
                      ) ? (
                        <div className="church-checkout-controls">
                          <label>
                            Workspace
                            <select
                              value={churchOrganizationId}
                              onChange={(event) => setChurchOrganizationId(event.target.value)}
                            >
                              {billing.organizations
                                .filter((organization) =>
                                  ["owner", "admin"].includes(organization.role),
                                )
                                .map((organization) => (
                                  <option value={organization.organizationId} key={organization.organizationId}>
                                    {organization.name}
                                  </option>
                                ))}
                            </select>
                          </label>
                          <label>
                            Seats
                            <input
                              type="number"
                              min={5}
                              max={100}
                              value={churchSeats}
                              onChange={(event) =>
                                setChurchSeats(
                                  Math.max(5, Math.min(100, Number(event.target.value) || 5)),
                                )
                              }
                            />
                          </label>
                          <label>
                            Billing
                            <select
                              value={churchInterval}
                              onChange={(event) =>
                                setChurchInterval(event.target.value as "month" | "year")
                              }
                            >
                              <option value="month">Monthly</option>
                              <option value="year">Annual · 2 months free</option>
                            </select>
                          </label>
                          <button
                            type="button"
                            className="btn primary"
                            disabled={Boolean(billingAction)}
                            onClick={() => {
                              if (churchOrganizationId) {
                                void beginChurchCheckout(churchOrganizationId);
                              }
                            }}
                          >
                            {billing.trialAvailable ? "Start church trial" : "Continue to Stripe"}
                          </button>
                        </div>
                      ) : (
                        <p className="tiny muted">
                          Create a church workspace as an owner or administrator to start checkout.
                        </p>
                      )}
                    </div>
                  ) : null}
                </>
              ) : null}
              {billingError ? (
                <div className="chat-error">
                  <WarningCircle size={17} /> {billingError}
                </div>
              ) : null}
              <p className="billing-disclosure">
                Card required. {billing?.trialAvailable
                  ? "The selected plan is free for 30 days, then renews automatically at the checkout price until canceled. "
                  : "Your lifetime trial has already been redeemed; Stripe will show the amount due before confirmation. "}
                One lifetime trial per verified account.
                Included credits expire at each monthly refresh and do not roll over. Access changes
                only after a signed Stripe webhook confirms billing state.
              </p>
            </section>
          ) : tab === "support" ? (
            <SupportFeedback me={me} />
          ) : tab === "bible" ? (
            <section className="settings-section settings-surface">
              <h2 className="panel-title">Bible reader</h2>
              <p className="panel-sub">Only properly licensed translations belong in Bible Alone.</p>
              <div className="setting-row"><div className="setting-copy"><h3>Translation</h3><p>The complete public-domain KJV is installed for offline reading.</p></div><span className="pill green">KJV · Offline</span></div>
              <div className="setting-row"><div className="setting-copy"><h3>Other translations</h3><p>No copyrighted translation is distributed without a license.</p></div><span className="pill">Not installed</span></div>
              <Link className="btn" to="/scripture/John/3"><BookOpenText size={15} /> Open reader</Link>
            </section>
          ) : (
            <section className="settings-section settings-surface">
              <h2 className="panel-title">Privacy and data</h2>
              <p className="panel-sub">Private notes stay private unless you deliberately share a specific item.</p>
              <div className="setting-row"><div className="setting-copy"><h3>Prayer journal encryption</h3><p>Prayer titles and bodies are encrypted at rest.</p></div><span className="pill green"><LockKey size={13} /> Encrypted</span></div>
              <div className="setting-row"><div className="setting-copy"><h3>Export account data</h3><p>Download profile, conversation metadata, and Bible annotations.</p></div><button className="btn" onClick={() => void downloadExport()}><DownloadSimple size={15} /> Export JSON</button></div>
              <div className="setting-row"><div className="setting-copy"><h3>Sign out</h3><p>End this browser session without deleting synced data.</p></div><button className="btn" onClick={() => void authClient.signOut()}><SignOut size={15} /> Sign out</button></div>
              <div className="setting-row"><div className="setting-copy"><h3>Delete account</h3><p>Permanently remove your account and all associated synced data. An active Stripe subscription must be canceled first.</p></div><button className="btn danger" onClick={() => { setDeleteError(""); setDeletingAccount(true); }}><Trash size={15} /> Delete account</button></div>
            </section>
          )}
          {message ? <div className="settings-message">{message}</div> : null}
          <section className="settings-section doctrinal-boundary">
            <ShieldCheck size={22} />
            <div><strong>Sola is not a spiritual authority.</strong><p>{brand.description} It does not receive revelation, speak for God, or replace the ordinary means God has given the church.</p></div>
          </section>
        </div>
      </div>
      {editingProfile && me ? (
        <Onboarding
          name={me.user.name}
          email={me.user.email}
          initialProfile={me.profile}
          onCancel={() => setEditingProfile(false)}
          onComplete={async () => {
            await onRefresh();
            setEditingProfile(false);
            setMessage("Your profile and Sola preferences were updated.");
          }}
        />
      ) : null}
      {deletingAccount ? (
        <Modal title="Permanently delete account" onClose={() => setDeletingAccount(false)}>
          <form
            className="stack"
            onSubmit={async (event) => {
              event.preventDefault();
              setDeleteError("");
              try {
                await api("/api/account", {
                  method: "DELETE",
                  body: JSON.stringify({ confirmation: deleteConfirmation }),
                });
                await authClient.signOut();
                window.location.assign("/");
              } catch (caught) {
                setDeleteError(
                  caught instanceof Error ? caught.message : "The account could not be deleted.",
                );
              }
            }}
          >
            <p>This cannot be undone. Conversations, notes, sermons and their revisions, preaching sessions, prayers, library sources, groups you own, and profile data will be removed.</p>
            {deleteError ? <div className="chat-error"><WarningCircle size={17} /> {deleteError}</div> : null}
            <div className="field"><label htmlFor="delete-confirmation">Type DELETE MY ACCOUNT</label><input id="delete-confirmation" className="input" value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value)} autoComplete="off" required /></div>
            <div className="modal-actions"><button type="button" className="btn" onClick={() => setDeletingAccount(false)}>Cancel</button><button className="btn danger" disabled={deleteConfirmation !== "DELETE MY ACCOUNT"}>Delete permanently</button></div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}

function SupportFeedback({ me }: { me: MeResponse }) {
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [eligible, setEligible] = useState(me.billing.admin);
  const [requestType, setRequestType] = useState<SupportRequest["requestType"]>("bug");
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const response = await api<{
        eligible: boolean;
        requests: SupportRequest[];
      }>("/api/support");
      setEligible(response.eligible);
      setRequests(response.requests);
      setError("");
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Support requests could not be loaded.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [me.user.id]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      await api("/api/support", {
        method: "POST",
        body: JSON.stringify({
          requestType,
          title,
          details,
          pageUrl: window.location.href,
        }),
      });
      setTitle("");
      setDetails("");
      setMessage(
        requestType === "bug"
          ? "Your error report was sent."
          : "Your feature request was sent.",
      );
      await load();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Your feedback could not be sent.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="settings-section settings-surface support-feedback">
      <h2 className="panel-title">Support and feedback</h2>
      <p className="panel-sub">
        Paid members can report a problem or suggest what Bible Alone should build next.
        Never include passwords, card numbers, prayer-journal content, or other sensitive data.
      </p>
      {eligible ? (
        <form className="stack support-form" onSubmit={submit}>
          <div className="support-type-toggle" role="radiogroup" aria-label="Feedback type">
            <button
              type="button"
              className={requestType === "bug" ? "active" : ""}
              role="radio"
              aria-checked={requestType === "bug"}
              onClick={() => setRequestType("bug")}
            >
              <Bug size={17} /> Report an error
            </button>
            <button
              type="button"
              className={requestType === "feature" ? "active" : ""}
              role="radio"
              aria-checked={requestType === "feature"}
              onClick={() => setRequestType("feature")}
            >
              <Lightbulb size={17} /> Request a feature
            </button>
          </div>
          <div className="field">
            <label htmlFor="support-title">Short title</label>
            <input
              id="support-title"
              className="input"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              minLength={3}
              maxLength={120}
              placeholder={
                requestType === "bug"
                  ? "What went wrong?"
                  : "What would make Bible Alone better?"
              }
              required
            />
          </div>
          <div className="field">
            <label htmlFor="support-details">Details</label>
            <textarea
              id="support-details"
              className="input support-details"
              value={details}
              onChange={(event) => setDetails(event.target.value)}
              minLength={10}
              maxLength={5000}
              placeholder={
                requestType === "bug"
                  ? "What did you expect, what happened, and how can we reproduce it?"
                  : "Describe the need and how you would use this feature."
              }
              required
            />
          </div>
          <button className="btn primary" disabled={submitting}>
            {submitting ? "Sending…" : "Send to Bible Alone"}
          </button>
        </form>
      ) : (
        <div className="billing-native-note">
          <LockKey size={20} />
          <div>
            <strong>Paid-member support</strong>
            <p>
              Start Core, Pro, Max, or a church workspace plan to submit errors and
              feature requests here.
            </p>
          </div>
        </div>
      )}
      {error ? <div className="chat-error"><WarningCircle size={17} /> {error}</div> : null}
      {message ? <div className="settings-message">{message}</div> : null}
      <div className="support-history">
        <h3>Your requests</h3>
        {loading ? (
          <p className="muted">Loading requests…</p>
        ) : requests.length ? (
          requests.map((request) => (
            <article className="support-request-card" key={request.id}>
              <div>
                <span className={`pill ${request.requestType === "feature" ? "purple" : ""}`}>
                  {request.requestType === "bug" ? "Error" : "Feature"}
                </span>
                <span className="pill">{request.status.replaceAll("_", " ")}</span>
              </div>
              <strong>{request.title}</strong>
              <p>{request.details}</p>
              {request.adminNotes ? (
                <div className="support-admin-reply">
                  <ShieldStar size={16} />
                  <span><strong>Bible Alone:</strong> {request.adminNotes}</span>
                </div>
              ) : null}
              <small>{new Date(request.createdAt * 1000).toLocaleString()}</small>
            </article>
          ))
        ) : (
          <p className="muted">You have not submitted any requests yet.</p>
        )}
      </div>
    </section>
  );
}

interface AdminUserRecord {
  id: string;
  name: string;
  email: string;
  emailVerified: number;
  image: string | null;
  role: string;
  banned: number;
  banReason: string | null;
  banExpires: number | string | null;
  createdAt: number | string;
  username: string | null;
  planKey: PlanKey;
  subscriptionStatus: string | null;
  availableMillis: number;
  sessionCount: number;
}

interface AdminSupportRecord extends SupportRequest {
  userId: string;
  userName: string;
  userEmail: string;
}

interface AdminDashboardResponse {
  summary: {
    totalUsers: number;
    bannedUsers: number;
    paidSubscriptions: number;
    openSupportRequests: number;
    openApplicationErrors: number;
    openCommunityReports: number;
  };
  users: AdminUserRecord[];
  supportRequests: AdminSupportRecord[];
  applicationErrors: Array<{
    id: string;
    requestId: string;
    path: string;
    method: string;
    errorName: string;
    errorMessage: string;
    status: "open" | "resolved" | "ignored";
    createdAt: number;
    userId: string | null;
    userName: string | null;
    userEmail: string | null;
  }>;
  reports: Array<{
    id: string;
    targetType: string;
    targetId: string;
    reason: string;
    details: string;
    status: "open" | "reviewing" | "resolved" | "dismissed";
    createdAt: number;
    reporterName: string;
    reporterEmail: string;
  }>;
  audit: Array<{
    id: string;
    action: string;
    entityType: string;
    entityId: string;
    metadataJson: string;
    createdAt: number;
    actorName: string | null;
    actorEmail: string | null;
  }>;
}

type AdminUserAction = "credits" | "password" | "ban" | "delete";

function AdminDashboard({
  me,
  onAuth,
}: {
  me: MeResponse | null;
  onAuth: () => void;
}) {
  const [data, setData] = useState<AdminDashboardResponse | null>(null);
  const [tab, setTab] = useState<"users" | "feedback" | "errors" | "reports" | "audit">("users");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [action, setAction] = useState<{
    kind: AdminUserAction;
    user: AdminUserRecord;
  } | null>(null);

  const load = async (search = query) => {
    if (!me || !isAdministrator(me.user)) return;
    setLoading(true);
    try {
      const response = await api<AdminDashboardResponse>(
        `/api/admin/dashboard${search.trim() ? `?query=${encodeURIComponent(search.trim())}` : ""}`,
      );
      setData(response);
      setError("");
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "The admin dashboard could not be loaded.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load("");
  }, [me?.user.id, me?.user.role]);

  if (!me) {
    return (
      <div className="view">
        <SignInCard
          title="Sign in to the admin dashboard"
          copy="Administrator access is verified on the server for every request."
          onAuth={onAuth}
        />
      </div>
    );
  }
  if (!isAdministrator(me.user)) {
    return (
      <div className="view">
        <section className="card empty-state">
          <span className="empty-icon"><LockKey size={27} /></span>
          <h3>Administrator access required</h3>
          <p>This page is restricted to authorized Bible Alone administrators.</p>
          <Link className="btn" to="/">Return home</Link>
        </section>
      </div>
    );
  }

  const update = async (
    path: string,
    body: Record<string, unknown>,
    successMessage: string,
  ) => {
    setError("");
    setMessage("");
    try {
      await api(path, { method: "PATCH", body: JSON.stringify(body) });
      setMessage(successMessage);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The update could not be saved.");
    }
  };

  const summaryItems = data
    ? [
        ["Accounts", data.summary.totalUsers],
        ["Paid", data.summary.paidSubscriptions],
        ["Suspended", data.summary.bannedUsers],
        ["Feedback", data.summary.openSupportRequests],
        ["Errors", data.summary.openApplicationErrors],
        ["Reports", data.summary.openCommunityReports],
      ]
    : [];

  return (
    <div className="view admin-view">
      <header className="page-header">
        <div>
          <span className="eyebrow"><ShieldStar size={15} weight="fill" /> Administrator</span>
          <h1 className="page-title">Bible Alone control room</h1>
          <p className="page-subtitle">
            Manage members, credits, feedback, application errors, and community reports.
          </p>
        </div>
        <button className="btn" onClick={() => void load()} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      <div className="admin-summary-grid">
        {summaryItems.map(([label, value]) => (
          <article className="admin-summary-card" key={String(label)}>
            <span>{label}</span>
            <strong>{Number(value).toLocaleString()}</strong>
          </article>
        ))}
      </div>

      <nav className="admin-tabs" aria-label="Admin dashboard sections">
        {([
          ["users", "Accounts"],
          ["feedback", "Feedback"],
          ["errors", "Errors"],
          ["reports", "Reports"],
          ["audit", "Audit log"],
        ] as const).map(([value, label]) => (
          <button
            type="button"
            className={tab === value ? "active" : ""}
            onClick={() => setTab(value)}
            key={value}
          >
            {label}
          </button>
        ))}
      </nav>

      {error ? <div className="chat-error"><WarningCircle size={17} /> {error}</div> : null}
      {message ? <div className="settings-message">{message}</div> : null}

      {tab === "users" ? (
        <section className="admin-panel">
          <form
            className="admin-search"
            onSubmit={(event) => {
              event.preventDefault();
              void load(query);
            }}
          >
            <MagnifyingGlass size={18} />
            <input
              className="input"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name, email, or username"
            />
            <button className="btn">Search</button>
          </form>
          <div className="admin-list">
            {data?.users.map((user) => (
              <article className="admin-user-card" key={user.id}>
                <div className="admin-user-primary">
                  <span className="avatar">
                    {user.image ? <img src={user.image} alt="" /> : initials(user.name)}
                  </span>
                  <div>
                    <strong>{user.name}</strong>
                    <span>{user.email}</span>
                    <small>
                      @{user.username ?? "no-profile"} · {user.sessionCount} active session
                      {user.sessionCount === 1 ? "" : "s"}
                    </small>
                  </div>
                </div>
                <div className="admin-user-state">
                  <span className={`pill ${hasAdminRoleClient(user.role) ? "purple" : ""}`}>
                    {hasAdminRoleClient(user.role) ? "Admin" : user.planKey}
                  </span>
                  {user.banned ? <span className="pill danger-pill">Suspended</span> : null}
                  <small>
                    {user.subscriptionStatus
                      ? billingStatusLabel(user.subscriptionStatus)
                      : "No active subscription"}
                  </small>
                </div>
                <div className="admin-user-actions">
                  <button className="btn" onClick={() => setAction({ kind: "credits", user })}>
                    <Sparkle size={15} /> Credits
                  </button>
                  <button
                    className="btn"
                    disabled={user.id === me.user.id}
                    onClick={() => setAction({ kind: "password", user })}
                  >
                    <LockKey size={15} /> Reset password
                  </button>
                  {user.banned ? (
                    <button
                      className="btn"
                      onClick={async () => {
                        await api(`/api/admin/users/${user.id}/unban`, { method: "POST" });
                        setMessage(`${user.email} can sign in again.`);
                        await load();
                      }}
                    >
                      Restore account
                    </button>
                  ) : (
                    <button
                      className="btn"
                      disabled={user.id === me.user.id}
                      onClick={() => setAction({ kind: "ban", user })}
                    >
                      Suspend
                    </button>
                  )}
                  <button
                    className="btn danger"
                    disabled={user.id === me.user.id || hasAdminRoleClient(user.role)}
                    onClick={() => setAction({ kind: "delete", user })}
                  >
                    <Trash size={15} /> Delete
                  </button>
                </div>
                {user.banReason ? <p className="admin-ban-reason">Reason: {user.banReason}</p> : null}
              </article>
            ))}
          </div>
        </section>
      ) : tab === "feedback" ? (
        <section className="admin-panel admin-list">
          {data?.supportRequests.length ? data.supportRequests.map((request) => (
            <article className="admin-record-card" key={request.id}>
              <div className="admin-record-head">
                <div>
                  <span className={`pill ${request.requestType === "feature" ? "purple" : ""}`}>
                    {request.requestType}
                  </span>
                  <span className="pill">{request.status}</span>
                </div>
                <small>{new Date(request.createdAt * 1000).toLocaleString()}</small>
              </div>
              <h3>{request.title}</h3>
              <p>{request.details}</p>
              <small>{request.userName} · {request.userEmail} · {request.pageUrl ?? "No page attached"}</small>
              <div className="admin-record-actions">
                {(["reviewing", "planned", "resolved", "closed"] as const).map((status) => (
                  <button
                    className="btn"
                    key={status}
                    disabled={request.status === status}
                    onClick={() => void update(
                      `/api/admin/support/${request.id}`,
                      { status, adminNotes: request.adminNotes },
                      `Feedback marked ${status}.`,
                    )}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </article>
          )) : <p className="muted">No paid-member feedback yet.</p>}
        </section>
      ) : tab === "errors" ? (
        <section className="admin-panel admin-list">
          {data?.applicationErrors.length ? data.applicationErrors.map((item) => (
            <article className="admin-record-card" key={item.id}>
              <div className="admin-record-head">
                <span className={`pill ${item.status === "open" ? "danger-pill" : ""}`}>{item.status}</span>
                <small>{new Date(item.createdAt * 1000).toLocaleString()}</small>
              </div>
              <h3>{item.errorName}</h3>
              <p>{item.errorMessage}</p>
              <code>{item.method} {item.path}</code>
              <small>Request {item.requestId} · {item.userEmail ?? "Anonymous"}</small>
              <div className="admin-record-actions">
                <button className="btn" onClick={() => void update(
                  `/api/admin/errors/${item.id}`,
                  { status: "resolved" },
                  "Error marked resolved.",
                )}>Resolve</button>
                <button className="btn" onClick={() => void update(
                  `/api/admin/errors/${item.id}`,
                  { status: "ignored" },
                  "Error ignored.",
                )}>Ignore</button>
              </div>
            </article>
          )) : <p className="muted">No application errors have been recorded.</p>}
        </section>
      ) : tab === "reports" ? (
        <section className="admin-panel admin-list">
          {data?.reports.length ? data.reports.map((report) => (
            <article className="admin-record-card" key={report.id}>
              <div className="admin-record-head">
                <span className="pill">{report.status}</span>
                <small>{new Date(report.createdAt * 1000).toLocaleString()}</small>
              </div>
              <h3>{report.reason.replaceAll("_", " ")}</h3>
              <p>{report.details || "No additional details supplied."}</p>
              <small>
                {report.reporterName} · {report.reporterEmail} · {report.targetType} {report.targetId}
              </small>
              <div className="admin-record-actions">
                {(["reviewing", "resolved", "dismissed"] as const).map((status) => (
                  <button className="btn" key={status} onClick={() => void update(
                    `/api/admin/reports/${report.id}`,
                    { status, rationale: "" },
                    `Report marked ${status}.`,
                  )}>{status}</button>
                ))}
              </div>
            </article>
          )) : <p className="muted">No community reports yet.</p>}
        </section>
      ) : (
        <section className="admin-panel admin-list">
          {data?.audit.length ? data.audit.map((item) => (
            <article className="admin-audit-row" key={item.id}>
              <ShieldCheck size={17} />
              <div>
                <strong>{item.action}</strong>
                <span>{item.entityType} · {item.entityId}</span>
                <small>
                  {item.actorEmail ?? "System"} · {new Date(item.createdAt * 1000).toLocaleString()}
                </small>
              </div>
            </article>
          )) : <p className="muted">No administrator actions recorded yet.</p>}
        </section>
      )}

      {action ? (
        <AdminUserActionModal
          action={action}
          onClose={() => setAction(null)}
          onComplete={async (successMessage) => {
            setAction(null);
            setMessage(successMessage);
            await load();
          }}
        />
      ) : null}
    </div>
  );
}

function hasAdminRoleClient(role: string) {
  return role
    .split(",")
    .some((value) => value.trim().toLowerCase() === "admin");
}

function AdminUserActionModal({
  action,
  onClose,
  onComplete,
}: {
  action: { kind: AdminUserAction; user: AdminUserRecord };
  onClose: () => void;
  onComplete: (message: string) => Promise<void>;
}) {
  const [credits, setCredits] = useState(100);
  const [planKey, setPlanKey] = useState<"core" | "pro" | "max">("pro");
  const [reason, setReason] = useState("");
  const [days, setDays] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmationEmail, setConfirmationEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const labels: Record<AdminUserAction, string> = {
    credits: "Grant Sola credits",
    password: "Set temporary password",
    ban: "Suspend account",
    delete: "Permanently delete account",
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (action.kind === "credits") {
        await api(`/api/admin/users/${action.user.id}/credits`, {
          method: "POST",
          body: JSON.stringify({ credits, planKey, reason }),
        });
        await onComplete(`${credits.toLocaleString()} credits granted to ${action.user.email}.`);
      } else if (action.kind === "password") {
        await api(`/api/admin/users/${action.user.id}/password`, {
          method: "POST",
          body: JSON.stringify({ newPassword }),
        });
        await onComplete(`Password reset and sessions revoked for ${action.user.email}.`);
      } else if (action.kind === "ban") {
        await api(`/api/admin/users/${action.user.id}/ban`, {
          method: "POST",
          body: JSON.stringify({
            reason,
            days: days ? Number(days) : null,
          }),
        });
        await onComplete(`${action.user.email} was suspended.`);
      } else {
        await api(`/api/admin/users/${action.user.id}`, {
          method: "DELETE",
          body: JSON.stringify({ confirmationEmail }),
        });
        await onComplete(`${action.user.email} was permanently deleted.`);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The administrator action failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={labels[action.kind]} onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <p>
          <strong>{action.user.name}</strong><br />
          <span className="muted">{action.user.email}</span>
        </p>
        {action.kind === "credits" ? (
          <>
            <div className="field">
              <label htmlFor="admin-credit-amount">Credits</label>
              <input
                id="admin-credit-amount"
                className="input"
                type="number"
                min={1}
                max={100000}
                value={credits}
                onChange={(event) => setCredits(Number(event.target.value))}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="admin-credit-plan">Access tier</label>
              <select
                id="admin-credit-plan"
                className="select"
                value={planKey}
                onChange={(event) => setPlanKey(event.target.value as typeof planKey)}
              >
                <option value="core">Core</option>
                <option value="pro">Pro</option>
                <option value="max">Max</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="admin-credit-reason">Audit reason</label>
              <input id="admin-credit-reason" className="input" value={reason} onChange={(event) => setReason(event.target.value)} minLength={3} maxLength={500} required />
            </div>
          </>
        ) : action.kind === "password" ? (
          <div className="field">
            <label htmlFor="admin-temp-password">Temporary password</label>
            <input
              id="admin-temp-password"
              className="input"
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              minLength={12}
              maxLength={128}
              autoComplete="new-password"
              required
            />
            <small>The member will be signed out everywhere after this change.</small>
          </div>
        ) : action.kind === "ban" ? (
          <>
            <div className="field">
              <label htmlFor="admin-ban-reason">Reason shown to member</label>
              <textarea id="admin-ban-reason" className="input" value={reason} onChange={(event) => setReason(event.target.value)} minLength={3} maxLength={500} required />
            </div>
            <div className="field">
              <label htmlFor="admin-ban-days">Days (leave blank for indefinite)</label>
              <input id="admin-ban-days" className="input" type="number" min={1} max={3650} value={days} onChange={(event) => setDays(event.target.value)} />
            </div>
          </>
        ) : (
          <>
            <div className="chat-error">
              <WarningCircle size={17} />
              This immediately cancels and removes any Stripe customer, then permanently deletes all account data.
            </div>
            <div className="field">
              <label htmlFor="admin-delete-email">Type {action.user.email}</label>
              <input id="admin-delete-email" className="input" value={confirmationEmail} onChange={(event) => setConfirmationEmail(event.target.value)} autoComplete="off" required />
            </div>
          </>
        )}
        {error ? <div className="chat-error"><WarningCircle size={17} /> {error}</div> : null}
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button
            className={`btn ${action.kind === "delete" || action.kind === "ban" ? "danger" : "primary"}`}
            disabled={
              busy ||
              (action.kind === "delete" &&
                confirmationEmail.toLowerCase() !== action.user.email.toLowerCase())
            }
          >
            {busy ? "Saving…" : labels[action.kind]}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Notifications({
  user,
  onAuth,
}: {
  user: MeResponse["user"] | null;
  onAuth: () => void;
}) {
  const [items, setItems] = useState<AppNotification[]>([]);
  const [error, setError] = useState("");
  const load = async () => {
    if (!user) return setItems([]);
    try {
      const response = await api<{ notifications: AppNotification[] }>("/api/notifications");
      setItems(response.notifications);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Notifications could not be loaded.");
    }
  };
  useEffect(() => {
    void load();
  }, [user?.id]);
  return (
    <div className="view">
      <header className="page-header">
        <div><h1 className="page-title">Notifications</h1><p className="page-subtitle">Connection requests, private group invitations, and replies that need your attention.</p></div>
        {user && items.some((item) => !item.readAt) ? <button className="btn" onClick={async () => { await api("/api/notifications/read-all", { method: "POST" }); await load(); }}><Check size={15} /> Mark all read</button> : null}
      </header>
      {!user ? <SignInCard title="Sign in to see notifications" copy="Notifications are tied to your private Bible Alone account." onAuth={onAuth} /> : error ? <div className="chat-error"><WarningCircle size={17} /> {error}</div> : items.length ? (
        <section className="card panel notification-list">
          {items.map((item) => (
            <Link
              className={`notification-row ${item.readAt ? "" : "unread"}`}
              to={item.targetUrl ?? "/community"}
              key={item.id}
              onClick={() => {
                if (!item.readAt) void api(`/api/notifications/${item.id}/read`, { method: "PATCH" });
              }}
            >
              <span className="action-icon"><Bell size={17} /></span>
              <span><strong>{item.title}</strong><small>{item.body}</small></span>
              <time>{new Date(item.createdAt * 1000).toLocaleString()}</time>
            </Link>
          ))}
        </section>
      ) : <div className="card empty-state"><Bell size={28} /><h3>You are caught up</h3><p>New invitations, connection requests, and group replies will appear here.</p></div>}
    </div>
  );
}

function LegalPage({ kind }: { kind: "privacy" | "terms" }) {
  const privacy = kind === "privacy";
  return (
    <div className="view legal-page">
      <header className="page-header">
        <div>
          <span className="eyebrow">{brand.legalOrganizationName}</span>
          <h1 className="page-title">{privacy ? "Privacy Policy" : "Terms of Use"}</h1>
          <p className="page-subtitle">Effective July 27, 2026 · Contact {brand.supportEmail}</p>
        </div>
      </header>
      <article className="card panel legal-copy">
        {privacy ? (
          <>
            <h2>What Bible Alone stores</h2>
            <p>We store the account, profile, conversations, study notes and their revision history, private sermon documents and revisions, linked-source metadata, preaching-session timing, Bible annotations, notebooks, community activity, and other content you deliberately save so those features can work across devices. Private prayer-journal text is encrypted before it is stored. Passwords are hashed by the authentication system and are never stored as readable text.</p>
            <h2>Service providers</h2>
            <p>Cloudflare hosts the application, database, background workflows, and saved Deep Study reports. Google processes Google sign-in when you choose it. Replicate processes prompts, permitted source excerpts, and recent conversation context using the selected OpenAI model family for Sola; Bible Alone chooses the model on the server. Tavily processes Deep Study search queries and returns public web results. ElevenLabs processes audio only when you deliberately use speech-to-text or text-to-speech. Stripe processes payment details, checkout, trials, invoices, and subscription management when you purchase a web subscription; Bible Alone stores Stripe customer, price, subscription status, credit, and billing-period identifiers but not your card number. An email delivery provider may process an address and recovery message when email recovery is enabled.</p>
            <h2>Scripture and AI data boundaries</h2>
            <p>The built-in public-domain KJV can be read offline after the app is installed. Sola is a fallible AI assistant. Private study-note excerpts are sent to the language-model provider only when you attach a note, type a matching hashtag, turn on Search my notes, or deliberately link a note into a sermon preparation. Sermon metadata, bounded sermon text, selected blocks, linked note excerpts, audience, duration, doctrine profile, and verified primary Scripture are sent to the language-model provider only when you use Sola inside the sermon builder or request a sermon draft. Sermon blocks and private note contents are never sent to Tavily. Deep Research for a sermon sends only a sanitized public-web query made from the canonical passage and the optional research focus; its saved report and draft remain linked for provenance. The exact saved note revision used by a request is recorded for provenance. Do not submit confidential pastoral, medical, legal, financial, or safeguarding information that you are not authorized to share with the applicable service providers.</p>
            <h2>Community privacy and safety</h2>
            <p>Groups begin private and membership requires a deliberate invitation and acceptance. Private notes and prayer entries are never attached to a post automatically. Reports are visible to authorized moderators. Blocking removes the connection and prevents ordinary discovery between the two accounts.</p>
            <h2>Your choices</h2>
            <p>You can export your account data or permanently delete the account from Settings. You can also sign out without deleting synced data. For access or deletion help, email <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>.</p>
            <h2>Security and retention</h2>
            <p>We use encrypted transport, secure session cookies, access controls, rate limits, and audit records for sensitive community operations. No Internet service can promise absolute security. We retain saved data until you delete it or the account, subject to limited backups, security records, and legal obligations.</p>
            <h2>Children</h2>
            <p>Bible Alone is not directed to children under 13. Teen use should occur with a parent or guardian and the oversight appropriate to the family and local church. Do not use community features to solicit private contact with minors.</p>
          </>
        ) : (
          <>
            <h2>Use of the service</h2>
            <p>You may use Bible Alone for lawful personal, family, church, educational, and ministry purposes. You are responsible for your account, the content you submit, and verifying generated material before relying on or sharing it.</p>
            <h2>Scripture and spiritual authority</h2>
            <p>Bible Alone is built on the conviction that Scripture is the final infallible authority for Christian faith and practice. Sola is software: it does not speak for God, receive revelation, possess pastoral office, or produce inspired or infallible interpretation. The service does not replace prayer, personal Bible study, pastors, teachers, qualified professionals, or the local church.</p>
            <h2>AI limitations</h2>
            <p>Generated answers may be incomplete, inaccurate, biased, or fabricated. Direct Scripture quotations, historical context, theological interpretation, denominational perspective, practical application, and AI-generated suggestions are different categories. Verify quotations and interpretations in context.</p>
            <h2>Community conduct</h2>
            <p>Do not harass, threaten, impersonate, exploit, expose private information, distribute illegal content, evade safety controls, or use the service to groom or endanger a minor. We may hold or remove content, limit accounts, and preserve evidence needed to investigate credible safety or legal concerns.</p>
            <h2>Your content</h2>
            <p>You keep ownership of content you submit. You grant Bible Alone only the limited rights needed to host, process, protect, and display that content according to your chosen features and visibility. Only upload material you are authorized to store and process.</p>
            <h2>Subscriptions and billing</h2>
            <p>A new eligible subscriber supplies a payment method and receives the selected plan for 30 days at $0. The first charge occurs automatically on the exact date and for the amount shown at Stripe Checkout unless canceled first. Plans renew at the displayed monthly or annual interval until canceled. Manage, change, or cancel an existing subscription through the Stripe billing portal in Settings. Deleting a Bible Alone account is blocked while a subscription remains active so recurring billing is not orphaned.</p>
            <h2>Sola credits</h2>
            <p>Included Sola credits refresh monthly, expire at refresh, and do not roll over, including on annual subscriptions. Purchased credits are used after included credits, require an active paid subscription, and expire after 12 months. Sola pauses when the available balance cannot cover a reservation; non-AI Bible reading, notes, highlights, and community features remain available. Unused reserved credits are returned after normal completion or cancellation.</p>
            <h2>Availability and liability</h2>
            <p>The service is provided as available and may change or experience interruptions. To the maximum extent allowed by law, Bible Alone is not liable for decisions made solely from generated content or for indirect or consequential losses. Some jurisdictions do not allow every limitation, so those limits apply only where lawful.</p>
            <h2>Changes and contact</h2>
            <p>Material changes will be posted with a new effective date. Continued use after a change means you accept the updated terms. Questions can be sent to <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>.</p>
          </>
        )}
        <div className="legal-links"><Link to={privacy ? "/terms" : "/privacy"}>{privacy ? "Read the Terms of Use" : "Read the Privacy Policy"}</Link><Link to="/">Return to Bible Alone</Link></div>
      </article>
    </div>
  );
}

function AuthDialog({ onClose }: { onClose: () => void }) {
  const [mode, setMode] = useState<"signin" | "register">("signin");
  const [forgot, setForgot] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const google = async () => {
    setError("");
    setBusy(true);
    const result = await authClient.signIn.social({
      provider: "google",
      callbackURL: window.location.href,
    });
    if (result.error) {
      setError(result.error.message ?? "Google sign-in could not start.");
      setBusy(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setBusy(true);
    const result =
      mode === "signin"
        ? await authClient.signIn.email({ email, password, callbackURL: window.location.href })
        : await authClient.signUp.email({ name, email, password, callbackURL: window.location.href });
    if (result.error) {
      setError(result.error.message ?? "Authentication failed.");
      setBusy(false);
      return;
    }
    onClose();
  };

  const requestReset = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const response = await fetch("/api/auth/request-password-reset", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email,
          redirectTo: `${window.location.origin}/reset-password`,
        }),
      });
      const result = (await response.json().catch(() => ({}))) as {
        message?: string;
        error?: string;
      };
      if (!response.ok) {
        throw new Error(
          result.error ??
            "Email recovery is not configured. You can still continue with Google.",
        );
      }
      setError(result.message ?? "If that account exists, a secure reset link was sent.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Password recovery could not start.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-layer" role="presentation">
      <section className="auth-dialog" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        <button className="modal-close" onClick={onClose} aria-label="Close sign in"><X size={19} /></button>
        <div className="auth-brand"><BrandLockup compact /></div>
        <h2 id="auth-title">{forgot ? "Reset your password" : mode === "signin" ? "Continue with Sola" : "Create your Bible Alone account"}</h2>
        <p>{forgot ? "Enter your account email. For privacy, the result does not reveal whether an account exists." : mode === "signin" ? "Sync chats, Bible notes, and private journal data." : "Start with a secure account, then choose only the preferences you need."}</p>
        {!forgot ? <button className="google-button" onClick={() => void google()} disabled={busy}>
          <GoogleLogo size={20} weight="bold" />
          Continue with Google
        </button> : null}
        {!forgot ? <div className="auth-divider"><span>or use email</span></div> : null}
        <form onSubmit={forgot ? requestReset : submit} className="stack">
          {mode === "register" && !forgot ? (
            <div className="field"><label htmlFor="auth-name">Name</label><input id="auth-name" className="input" value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" required /></div>
          ) : null}
          <div className="field"><label htmlFor="auth-email">Email</label><input id="auth-email" className="input" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required /></div>
          {!forgot ? <div className="field"><label htmlFor="auth-password">Password</label><input id="auth-password" className="input" type="password" minLength={10} maxLength={128} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={mode === "signin" ? "current-password" : "new-password"} required /></div> : null}
          {error ? <div className="form-error"><WarningCircle size={16} /> {error}</div> : null}
          <button className="btn primary auth-submit" disabled={busy}>{busy ? "Please wait…" : forgot ? "Send reset link" : mode === "signin" ? "Sign in" : "Create account"}</button>
        </form>
        {mode === "signin" && !forgot ? <button className="auth-switch" onClick={() => { setForgot(true); setError(""); }}>Forgot your password?</button> : null}
        <button className="auth-switch" onClick={() => {
          setError("");
          if (forgot) setForgot(false);
          else setMode(mode === "signin" ? "register" : "signin");
        }}>
          {forgot ? "Back to sign in" : mode === "signin" ? "New to Bible Alone? Create an account" : "Already have an account? Sign in"}
        </button>
        <p className="auth-boundary">Sola is a fallible AI tool. Scripture—not generated text—is the final authority.</p>
        <p className="auth-legal">By continuing, you agree to the <Link to="/terms" onClick={onClose}>Terms</Link> and acknowledge the <Link to="/privacy" onClick={onClose}>Privacy Policy</Link>.</p>
      </section>
    </div>
  );
}

function ResetPassword() {
  const token = new URLSearchParams(useLocation().search).get("token");
  const invalid = new URLSearchParams(useLocation().search).get("error");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [message, setMessage] = useState(
    invalid ? "This password-reset link is invalid or has expired." : "",
  );
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirmation) return setMessage("The passwords do not match.");
    if (!token) return setMessage("This password-reset link is invalid or has expired.");
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ newPassword: password, token }),
      });
      const result = (await response.json().catch(() => ({}))) as { message?: string };
      if (!response.ok) throw new Error(result.message ?? "The password could not be reset.");
      setDone(true);
      setMessage("Your password was reset. You can now sign in.");
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "The password could not be reset.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="view">
      <section className="card settings-section reset-password-card">
        <div className="auth-brand"><BrandLockup compact /></div>
        <h1 className="page-title">Choose a new password</h1>
        <p className="panel-sub">Use at least 10 characters. Resetting a password signs out other email/password sessions.</p>
        {!done ? (
          <form className="stack" onSubmit={reset}>
            <div className="field"><label htmlFor="new-password">New password</label><input id="new-password" className="input" type="password" minLength={10} maxLength={128} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" required /></div>
            <div className="field"><label htmlFor="confirm-password">Confirm password</label><input id="confirm-password" className="input" type="password" minLength={10} maxLength={128} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="new-password" required /></div>
            <button className="btn primary" disabled={busy || !token}>{busy ? "Resetting…" : "Reset password"}</button>
          </form>
        ) : <Link className="btn primary" to="/">Return to sign in</Link>}
        {message ? <div className={done ? "settings-message" : "form-error"}>{message}</div> : null}
      </section>
    </div>
  );
}

function Onboarding({
  name,
  email,
  onComplete,
  initialProfile,
  onCancel,
}: {
  name: string;
  email: string;
  onComplete: () => Promise<void>;
  initialProfile?: Profile | null;
  onCancel?: () => void;
}) {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState(
    initialProfile?.username ??
      email.split("@")[0]?.replace(/[^a-zA-Z0-9_]/g, "").slice(0, 30) ??
      "",
  );
  const [displayName, setDisplayName] = useState(initialProfile?.display_name ?? name);
  const [doctrine, setDoctrine] = useState(
    initialProfile?.doctrine_document_id
      ? "custom"
      : initialProfile?.doctrine_profile ?? "reformed_baptist",
  );
  const [doctrineDocumentId, setDoctrineDocumentId] = useState(
    initialProfile?.doctrine_document_id ?? "",
  );
  const [doctrineDocuments, setDoctrineDocuments] = useState<DoctrineDocument[]>([]);
  const [uploadingDoctrine, setUploadingDoctrine] = useState(false);
  const doctrineFileRef = useRef<HTMLInputElement>(null);
  const [depth, setDepth] = useState<"concise" | "balanced" | "deep">(
    (initialProfile?.response_depth as "concise" | "balanced" | "deep") ?? "balanced",
  );
  const [multiple, setMultiple] = useState(
    initialProfile ? Boolean(initialProfile.show_multiple_interpretations) : true,
  );
  const [scriptureMode, setScriptureMode] = useState(Boolean(initialProfile?.scripture_mode));
  const [interests, setInterests] = useState<string[]>(() => {
    if (!initialProfile?.interests_json) return ["everyday", "bible-study"];
    try {
      const parsed = JSON.parse(initialProfile.interests_json);
      return Array.isArray(parsed) ? parsed : ["everyday", "bible-study"];
    } catch {
      return ["everyday", "bible-study"];
    }
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    void api<{ documents: DoctrineDocument[] }>("/api/doctrines")
      .then((response) => {
        if (active) setDoctrineDocuments(response.documents);
      })
      .catch((caught) => {
        if (active) {
          setError(
            caught instanceof Error
              ? caught.message
              : "Uploaded doctrines could not be loaded.",
          );
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const uploadDoctrine = async (file: File | undefined) => {
    if (!file) return;
    setUploadingDoctrine(true);
    setError("");
    try {
      const extension = file.name.split(".").pop()?.toLowerCase();
      if (!["txt", "md", "markdown"].includes(extension ?? "")) {
        throw new Error("Choose a plain-text or Markdown doctrine file.");
      }
      if (file.size > 100_000) {
        throw new Error("Doctrine files must be 100 KB or smaller.");
      }
      const response = await api<{ document: DoctrineDocument }>(
        "/api/doctrines",
        {
          method: "POST",
          headers: {
            "content-type":
              extension === "txt" ? "text/plain" : "text/markdown",
            "x-doctrine-filename": encodeURIComponent(file.name),
          },
          body: file,
        },
      );
      setDoctrineDocuments((current) => [
        response.document,
        ...current.filter((document) => document.id !== response.document.id),
      ]);
      setDoctrine("custom");
      setDoctrineDocumentId(response.document.id);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The doctrine file could not be uploaded.",
      );
    } finally {
      setUploadingDoctrine(false);
      if (doctrineFileRef.current) doctrineFileRef.current.value = "";
    }
  };

  const toggleInterest = (interest: string) =>
    setInterests((current) =>
      current.includes(interest)
        ? current.filter((item) => item !== interest)
        : [...current, interest],
    );

  const finish = async () => {
    setSaving(true);
    setError("");
    try {
      await api("/api/onboarding", {
        method: "PUT",
        body: JSON.stringify({
          username,
          displayName,
          doctrineProfile: doctrine,
          doctrineDocumentId:
            doctrine === "custom" ? doctrineDocumentId : null,
          bibleTranslation: "kjv",
          responseDepth: depth,
          scriptureMode,
          showMultipleInterpretations: multiple,
          ageBand: "adult",
          interests,
        }),
      });
      await onComplete();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Onboarding could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-layer onboarding-layer">
      <section className="onboarding-card" role="dialog" aria-modal="true" aria-labelledby="onboarding-title">
        {onCancel ? <button className="modal-close" onClick={onCancel} aria-label="Close profile editor"><X size={19} /></button> : null}
        <div className="onboarding-progress"><span style={{ width: `${(step / 3) * 100}%` }} /></div>
        <div className="auth-brand"><BrandLockup compact /></div>
        <span className="eyebrow">Step {step} of 3</span>
        {step === 1 ? (
          <>
            <h2 id="onboarding-title">Make Bible Alone yours</h2>
            <p>We ask only what is needed now. Every preference can be revised later.</p>
            <div className="onboarding-fields">
              <div className="field"><label htmlFor="onboarding-name">Name</label><input id="onboarding-name" className="input" value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></div>
              <div className="field"><label htmlFor="onboarding-username">Username</label><div className="username-field"><span>@</span><input id="onboarding-username" className="input" value={username} onChange={(event) => setUsername(event.target.value.replace(/[^a-zA-Z0-9_]/g, ""))} maxLength={30} /></div></div>
            </div>
          </>
        ) : step === 2 ? (
          <>
            <h2 id="onboarding-title">Choose a starting perspective</h2>
            <p>The selected profile guides default framing. It never changes the biblical text.</p>
            <div className="doctrine-options">
              {Object.entries(doctrineProfiles).map(([value, label]) => (
                <button
                  className={doctrine === value ? "active" : ""}
                  onClick={() => {
                    setDoctrine(value);
                    setDoctrineDocumentId("");
                  }}
                  key={value}
                >
                  <span>{label}</span>{doctrine === value ? <CheckCircle size={18} weight="fill" /> : null}
                </button>
              ))}
            </div>
            <div className="doctrine-upload-card">
              <div>
                <strong>Use your own doctrine</strong>
                <span>Upload private UTF-8 text or Markdown, up to 100 KB.</span>
              </div>
              <input
                ref={doctrineFileRef}
                className="sr-only"
                type="file"
                accept=".txt,.md,.markdown,text/plain,text/markdown"
                aria-label="Upload your doctrine"
                onChange={(event) =>
                  void uploadDoctrine(event.target.files?.[0])
                }
              />
              <button
                className="btn"
                type="button"
                disabled={uploadingDoctrine}
                onClick={() => doctrineFileRef.current?.click()}
              >
                <UploadSimple size={16} />
                {uploadingDoctrine ? "Uploading…" : "Upload doctrine"}
              </button>
            </div>
            {doctrineDocuments.length ? (
              <div className="custom-doctrine-options" aria-label="Your uploaded doctrines">
                {doctrineDocuments.map((document) => {
                  const selected =
                    doctrine === "custom" &&
                    doctrineDocumentId === document.id;
                  return (
                    <button
                      className={selected ? "active" : ""}
                      type="button"
                      onClick={() => {
                        setDoctrine("custom");
                        setDoctrineDocumentId(document.id);
                      }}
                      key={document.id}
                    >
                      <span>
                        <strong>{document.title}</strong>
                        <small>
                          Your private upload · {Math.max(1, Math.ceil(document.sizeBytes / 1024))} KB
                        </small>
                      </span>
                      {selected ? <CheckCircle size={18} weight="fill" /> : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
            {doctrine === "custom" && !doctrineDocumentId ? (
              <div className="form-error">
                <WarningCircle size={16} /> Upload or choose a doctrine file.
              </div>
            ) : null}
            <label className="check-row"><input type="checkbox" checked={multiple} onChange={(event) => setMultiple(event.target.checked)} /> Show responsible alternative interpretations when relevant</label>
          </>
        ) : (
          <>
            <h2 id="onboarding-title">How will you use Sola?</h2>
            <p>This keeps ordinary answers natural and study tools close at hand.</p>
            <div className="interest-grid">
              {[
                ["everyday", "Everyday Christian assistance", Sparkle],
                ["bible-study", "Deeper Bible study", BookOpenText],
                ["ministry", "Ministry and teaching", ChalkboardTeacher],
                ["family", "Family discipleship", UsersThree],
              ].map(([value, label, InterestIcon]) => {
                const ItemIcon = InterestIcon as Icon;
                return (
                  <button className={interests.includes(String(value)) ? "active" : ""} onClick={() => toggleInterest(String(value))} key={String(value)}>
                    <ItemIcon size={21} /><span>{String(label)}</span>{interests.includes(String(value)) ? <Check size={16} /> : null}
                  </button>
                );
              })}
            </div>
            <div className="field">
              <label htmlFor="depth">Default response depth</label>
              <select id="depth" className="select" value={depth} onChange={(event) => setDepth(event.target.value as typeof depth)}>
                <option value="concise">Concise</option><option value="balanced">Balanced</option><option value="deep">Deep study</option>
              </select>
            </div>
            <label className="check-row"><input type="checkbox" checked={scriptureMode} onChange={(event) => setScriptureMode(event.target.checked)} /> Start Bible questions in Scripture Mode</label>
          </>
        )}
        {error ? <div className="form-error"><WarningCircle size={16} /> {error}</div> : null}
        <div className="onboarding-actions">
          {step > 1 ? <button className="btn" onClick={() => setStep(step - 1)}><ArrowLeft size={15} /> Back</button> : <span />}
          {step < 3 ? (
            <button
              className="btn primary"
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 1 && (username.length < 3 || !displayName.trim())) ||
                (step === 2 && doctrine === "custom" && !doctrineDocumentId)
              }
            >
              Continue <ArrowRight size={15} />
            </button>
          ) : (
            <button className="btn primary" onClick={() => void finish()} disabled={saving || !interests.length}>{saving ? "Saving…" : initialProfile ? "Save preferences" : "Enter Bible Alone"} <ArrowRight size={15} /></button>
          )}
        </div>
      </section>
    </div>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="modal-layer">
      <section className="modal-card" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div className="modal-head"><h2 id="modal-title">{title}</h2><button className="modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button></div>
        <div className="modal-body">{children}</div>
      </section>
    </div>
  );
}

function SignInCard({
  title,
  copy,
  onAuth,
}: {
  title: string;
  copy: string;
  onAuth: () => void;
}) {
  return (
    <section className="card empty-state sign-in-card">
      <span className="empty-icon"><LockKey size={27} /></span>
      <h3>{title}</h3>
      <p>{copy}</p>
      <button className="btn primary" onClick={onAuth}><SignIn size={16} /> Sign in securely</button>
    </section>
  );
}

function NotFound() {
  return (
    <div className="card empty-state">
      <span className="empty-icon"><BookOpenText size={27} /></span>
      <h3>That page was not found</h3>
      <p>Return home or continue reading Scripture.</p>
      <div className="row" style={{ justifyContent: "center" }}>
        <Link className="btn primary" to="/">Home</Link>
        <Link className="btn" to="/scripture/John/3">Open Scripture</Link>
      </div>
    </div>
  );
}

function MobileBottomNav() {
  const location = useLocation();
  const items: Array<{ to: string; label: string; icon: Icon }> = [
    { to: "/ask-sola", label: "Ask", icon: ChatCircleDots },
    { to: "/scripture/John/3", label: "Bible", icon: BookOpenText },
    { to: "/study", label: "Study", icon: NotePencil },
    { to: "/community", label: "Groups", icon: UsersThree },
    { to: "/settings", label: "Settings", icon: GearSix },
  ];
  return (
    <nav className="mobile-bottom-nav" aria-label="Mobile navigation">
      {items.map((item) => {
        const ItemIcon = item.icon;
        const isActive = location.pathname.startsWith(item.to.split("/").slice(0, 2).join("/"));
        return <Link className={isActive ? "active" : ""} to={item.to} key={item.to}><ItemIcon size={20} /><span>{item.label}</span></Link>;
      })}
    </nav>
  );
}
